// ============================================================================
// WORK ITEMS KANBAN BOARD
// Advanced drag-and-drop Kanban view with dynamic columns and zoom
// Jira-style multi-column board with configurable workflow states
// Date: November 7, 2025
// ============================================================================

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/service-operations/lifecycle.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/service-operations/lifecycle#board-columns-vs-statuses)
 */

import { useState, useEffect, useMemo, useRef, useCallback, memo } from 'react';
import { useResponsive } from '@/hooks/useResponsive';
import BrandedLoadingSpinner, { CardLoadingRow } from '@/components/shared/BrandedLoadingSpinner';
import UserDocsLink from '@/components/shared/UserDocsLink';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
  DragOverlay,
  useDroppable,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Archive,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Calendar,
  User,
  MapPin,
  Tag,
  Plus,
  Eye,
  CheckSquare,
  Square,
  ClipboardList,
  Activity,
  PauseCircle,
  XCircle,
  Target,
  Navigation,
  Zap,
  ZoomIn,
  ZoomOut,
  Settings,
  Settings2,
  EyeOff,
  ChevronLeft,
  ChevronRight,
  Users,
  UserCheck,
  ListTodo,
  GripVertical,
  CalendarClock,
  Route,
  Lock,
  Link2
} from 'lucide-react';
import { format, isSameDay, addDays, subDays } from 'date-fns';
import type { WorkItem, WorkItemType } from './UniversalWorkItems';
import type { WorkItemTemplate } from './WorkItemModal';
import QuickAddTaskButton from './shared/QuickAddTaskButton';
import SLATimer from './shared/SLATimer';
// NEW: UI/UX Enhancements (November 2025)
import { useKanbanZoom } from '@/hooks/useKanbanZoom';
import { EnhancedDragGhost, DropZoneHighlight } from '@/components/kanban/EnhancedDragGhost';
import { confirm, notify } from '@/lib/notifications';
import AddColumnModal from '@/components/kanban/AddColumnModal';
import { authService } from '@/lib/authService';
import {
  canSeeAppointmentRescheduleState,
  getAppointmentDisplayStatus,
  getAppointmentOperationalStatus,
} from '@/lib/work-items/appointmentRescheduleState';
import { useTheme } from '@/contexts/ThemeContext';
import { logUiAudit } from '@/lib/audit/uiAudit';
import AppointmentDropSetupModal, { type AppointmentDropSetupSelection } from '@/components/AppointmentDropSetupModal';
import AppointmentAssigneesModal from '@/components/work-items/AppointmentAssigneesModal';
import AppointmentCard, { type AppointmentCardAssignee, type AppointmentCardModel, type AppointmentCardTone } from '@/components/work-items/AppointmentCard';
import { DEFAULT_APPOINTMENT_TYPES, useJobConfig } from '@/hooks/useJobConfig';
import type { WorkflowStatus } from '@/hooks/useWorkflowStatuses';
import { normalizeAppointmentTypeKey, PURCHASE_APPOINTMENT_TYPE_KEY } from '@/lib/work-items/appointmentTypeConfig';
// NEW: Kanban style presets (January 2026)
import { getKanbanStylePreset, getColumnHeaderColors, type KanbanStylePreset } from '@/components/kanban/KanbanStylePresets';
import {
  alignRollingScheduleVisibleStartToNow,
  formatTimeSAST,
  formatLocalISOSAST,
  getCurrentHourSAST,
  getTodayStrSAST,
  getDateStrSAST,
  isDueDateOverdueSAST,
} from '@/lib/sastTime';
// NEW: Resource Schedule Timeline - Horizontal schedule view (February 2026)
import ResourceScheduleTimeline, { type Resource as TimelineResource, type ResourceScheduleTimelineRef, SERVICE_OPERATIONS_CONFIG } from './ResourceScheduleTimeline';
// NEW: Extracted shared types/constants and TimeAllocationView (February 2026)
import {
  type WorkflowState,
  type AssignedUser,
  type ZoomLevel,
  type UserLeave,
  ZOOM_SETTINGS,
  COLOR_CLASSES,
  getKBAvatarColor,
  getKBInitials,
  WORK_ITEMS_VIEW_BOARD_PREFS_BUTTON_CLASS,
  WORK_ITEMS_VIEW_TITLE_PILL_CLASS,
  WORK_ITEMS_VIEW_TOOLBAR_CARD_CLASS,
  WORK_ITEMS_VIEW_TOOLBAR_GROUP_CLASS,
  WORK_ITEMS_VIEW_TOOLBAR_ICON_BUTTON_CLASS,
  WORK_ITEMS_VIEW_TOOLBAR_INNER_ROW_CLASS,
} from '@/components/kanban/kanban-shared';
import TimeAllocationView from '@/components/kanban/TimeAllocationView';
import { useOperationsAssignmentOptions, type OperationsAssignmentTeam } from '@/hooks/useOperationsAssignmentOptions';
import WorkItemHoverCard from '@/components/work-items/WorkItemHoverCard';
import {
  getAppointmentDisplayNumber,
  getAppointmentGroupLeaderDisplayName,
  isAppointmentGroupLeader,
} from '@/lib/appointmentDisplay';
import { buildAppointmentCardPresentation } from '@/lib/appointmentCardPresentation';
import { getStandardWorkItemStatusBadgeClass, formatAppointmentDisplayStatusLabel, getToneForAppointmentStatusKey } from '@/lib/appointmentStatusVisual';
import { buildWorkItemTooltipLines } from '@/lib/workItemTooltip';
import {
  filterScheduleVisibleItems,
  getGenericUnscheduledScheduleItems,
  getUnscheduledJobsForSelectedDate,
} from '@/components/kanban/scheduleFilters';
import { type ScheduleClipboardResource } from '@/lib/scheduleClipboard';
import { getWorkItemTypeAccent } from '@/lib/work-items/typeIdentity';
import { emitOperationsGuidedTourTrainingEvent } from '@/lib/operations/guidedTourEvents';
import { canManuallyTargetWorkflowStatusKey, isSystemManagedWorkflowStatusKey } from '@/lib/work-items/workflowStatusRules';
import { appointmentsAllowIntentionalOverlap } from '@/lib/work-items/appointmentSideMissions';
import { getSuggestedProgressFromStatus, jobStatusBlocksAppointmentCreation, normalizeWorkflowStatusKey } from '@/lib/work-items/operationalStatusConfig';
import { isWorkflowActionPermitted, useWorkflowActionPermissions } from '@/lib/operations/workflowActionPermissions';
import { WorkItemsTypeAccentBadge } from '@/components/work-items/WorkItemsViewAccent';
import { WorkItemAgentChip } from '@/components/WorkItemAgentBadge';
import ScheduleExportButton from '@/components/schedule/ScheduleExportButton';
import {
  getHiddenColumnKeysFromVisibleColumns,
  sanitizeHiddenColumnKeys,
} from '@/lib/work-items/columnVisibilityPreferences';
import {
  areKanbanViewPreferencesHydrated,
  getKanbanHiddenResultsNotice,
  getVisibleColumnKeysForKanbanView,
  getVisibleColumnSetForKanbanView,
} from '@/lib/work-items/kanbanViewPreferences';
import { WORK_ITEM_LIST_CHANGED_EVENT } from '@/lib/websocket/lifecycle';

export interface BoardToolbarColumnOption {
  key: string;
  label: string;
  count: number;
  visible: boolean;
  disabled: boolean;
  colorClassName: string;
  onToggle: () => void;
}

export interface BoardToolbarModel {
  showZoomControls: boolean;
  zoomLabel: string;
  canZoomIn: boolean;
  canZoomOut: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomReset: () => void;
  showDateControls: boolean;
  selectedDateLabel?: string;
  selectedDateCompactLabel?: string;
  isToday?: boolean;
  onPreviousDate?: () => void;
  onToday?: () => void;
  onNextDate?: () => void;
  showTimeWindowControls: boolean;
  canRollEarlier?: boolean;
  canRollLater?: boolean;
  canResetTimeWindow?: boolean;
  onRollEarlier?: () => void;
  onRollLater?: () => void;
  onResetTimeWindow?: () => void;
  showSidebarToggle: boolean;
  sidebarVisible?: boolean;
  sidebarCount?: number;
  sidebarLabel?: string;
  onToggleSidebar?: () => void;
  showColumnSettings: boolean;
  columnSettingsOpen: boolean;
  visibleColumnCount: number;
  totalColumnCount: number;
  onToggleColumnSettings?: () => void;
  onCloseColumnSettings?: () => void;
  onToggleAllColumns?: () => void;
  columnOptions: BoardToolbarColumnOption[];
}

interface KanbanBoardProps {
  items: WorkItem[];
  onUpdateStatus: (itemId: string, newStatus: string) => Promise<void>;
  onUpdateAssignment?: (itemId: string, assignedTo: number | null) => Promise<void>;
  onViewItem: (itemId: string) => void;
  onRefresh?: () => Promise<void>; // Callback to refresh items after drag-drop
  itemType: WorkItemType;
  focusedItemType?: string; // Selected item type when a mixed board should respect one workflow/status set
  templates?: WorkItemTemplate[];
  workflowStates?: WorkflowState[];
  /** Full workflow statuses with allowed_from transitions (from useWorkflowStatuses / useJobConfig). If provided, drag-and-drop enforces configured transition rules. */
  workflowStatuses?: WorkflowStatus[];
  moduleKey?: string; // For fetching users with module access
  /** Workflow scope ('visitor-management', …) for status-config writes —
   *  column saves go to that scope's row, not the client-wide 'default'. */
  workflowContext?: string | null;
  assignedUsersOverride?: AssignedUser[]; // Optional scoped assignee list; when provided, skip broad module-user fetches
  currentUserId?: number; // For personal view - only show this user's assignments
  showCreateButton?: boolean; // Show "New Item" button
  onCreateItem?: () => void; // Callback when create button is clicked
  boardKey?: string; // Unique key for persisting column settings
  preferenceScopeKey?: string; // Stable user-preference scope shared with other work item views
  onColumnsChange?: (columns: WorkflowState[]) => void; // Callback when columns change
  onColumnsExposed?: (columns: WorkflowState[]) => void; // Callback to expose current columns to parent
  preferenceReloadKey?: number; // Increment to force preference reload after modal saves
  workflowField?: string; // Which field to use for workflow tracking (supports dotted paths like custom_fields.collection_workflow_stage)
  hideColumnSettings?: boolean; // Hide the column visibility settings button (for workspaces with their own settings)
  useStrictWorkflowStates?: boolean; // Don't merge with DEFAULT_COLUMNS - use only provided workflowStates (for workspaces)
  enableTimeAllocation?: boolean; // Show Time Allocation tab (default: false)
  // NEW: Style customization (January 2026)
  stylePreset?: string; // Preset ID from KanbanStylePresets (e.g., 'classic', 'vibrant', 'minimal')
  columnColorOverrides?: Record<string, { // Override column colors for specific statuses
    headerBackground?: string;
    headerTextColor?: string;
    border?: string;
    countBadgeBackground?: string;
  }>;
  // NEW: Schedule update callback for Resource Schedule Timeline (February 2026)
  onUpdateSchedule?: (itemId: string, scheduledStart: string, scheduledEnd: string, resourceId: number) => void;
  // NEW: Default board view mode — lets parent control which internal view to start with (February 2026)
  defaultBoardViewMode?: 'status' | 'assignment' | 'time_allocation' | 'resource_schedule';
  // NEW: Hide the internal view mode toggle when parent provides its own (February 2026)
  hideBoardViewToggle?: boolean;
  // NEW: Callback fired when an appointment is created or schedule-updated via drag-drop (February 2026)
  // Enables schedule ↔ route event bus sync
  onAppointmentChange?: (event: { action: 'created' | 'updated' | 'moved'; appointmentId?: string; date: string; assignedTo?: number }) => void;
  // NEW: Navigate to route planning view — shows "Generate Routes" button in schedule views (February 2026)
  onNavigateToRoutes?: (date: string) => void;
  // Active view label — displayed as a heading in the toolbar (e.g. "Jobs — Status Board")
  activeViewLabel?: string;
  // Opens the shared board design preferences modal from the board-owned title rows
  onOpenBoardPreferences?: () => void;
  // NEW: Open team member profile modal when clicking an avatar
  onOpenTeamMember?: (userId: number) => void;
  // Hide the internal board toolbar when a parent header renders those controls
  hideToolbar?: boolean;
  // Publish a board toolbar model upward for parent-managed header controls
  onToolbarModelChange?: (model: BoardToolbarModel | null) => void;
  /** True when the WorkItemsFilterBar has any active filter. The Resource Schedule Timeline
   *  hides resource rows that have no matching items while this is true. */
  filtersActive?: boolean;
  /** Non-null user IDs from the active assignee filter. When present, the Resource Schedule Timeline
   *  shows exactly those user rows (not co-assignees of their items) plus team rows with matching items. */
  activeAssigneeUserIds?: number[];
  /** Called when the schedule's selected date changes, so the parent can re-fetch
   *  with a date-windowed API query instead of loading all records. */
  onScheduleDateChange?: (date: Date) => void;
}

// View mode type - 4 board views: Status, Assignment, Vertical Schedule, Horizontal Schedule
type ViewMode = 'status' | 'assignment' | 'time_allocation' | 'resource_schedule';

type SortableDragListeners = ReturnType<typeof useSortable>['listeners'];

interface ExtendedWorkItem extends WorkItem {
  current_workflow_stage?: string | null;
  module_context?: string | null;
  payment_status?: string | null;
  grand_total?: string | number | null;
  balance_due?: string | number | null;
  currency?: string | null;
  custom_fields?: Record<string, unknown>;
}

const FINANCE_DOCUMENT_ITEM_TYPES = new Set([
  'invoice',
  'quote',
  'supplier_invoice',
  'purchase_order',
  'credit_note',
  'sales_order',
  'proforma',
  'delivery_note',
  'supplier_quote',
  'supplier_credit',
  'grn',
]);

interface PersistedBoardColumn {
  column_key: string;
  column_label: string;
  color?: string | null;
  description?: string | null;
  is_hidden?: boolean;
}

interface BoardColumnsResponse {
  success?: boolean;
  data?: PersistedBoardColumn[];
}

const TIME_ALLOCATION_CARD_DRAG_ID_PREFIX = 'ta-card::';

function getWorkItemIdFromDragId(rawId: string): string {
  if (!rawId.startsWith(TIME_ALLOCATION_CARD_DRAG_ID_PREFIX)) {
    return rawId;
  }

  const separatorIndex = rawId.lastIndexOf('::');
  return separatorIndex === -1 ? rawId : rawId.slice(separatorIndex + 2);
}

interface KanbanBoardPreferenceState {
  version: number;
  selectedViewMode?: ViewMode;
  selectedDateISO?: string;
  timeAllocationVisibleStartHour?: number;
  timeAllocationDefaultStartHour?: number;
  timeAllocationVisibleHoursCount?: number;
  resourceScheduleSidebarVisible?: boolean;
  zoomScale?: number;
  resourceScheduleTimelineZoom?: number;
  resourceScheduleVisibleStartHour?: number;
  statusHiddenColumnKeys?: string[];
  assignmentHiddenColumnKeys?: string[];
  timeAllocationHiddenColumnKeys?: string[];
  resourceScheduleHiddenColumnKeys?: string[];
  statusColumnOrder?: string[];
  resourceScheduleStartHour?: number;
  resourceScheduleEndHour?: number;
  resourceScheduleIntervalMinutes?: 15 | 30 | 60;
  resourceScheduleTimelineMode?: 'dynamic' | 'static';
}

interface AssigneeLink {
  user_id: number;
}

interface AssigneesResponse {
  success?: boolean;
  data?: AssigneeLink[];
}

const KANBAN_BOARD_PREFERENCE_VERSION = 3;
const KANBAN_VISIBLE_START_HOUR_MIN = -12;
const KANBAN_VISIBLE_START_HOUR_MAX = 23;
const KANBAN_TIME_ALLOCATION_DEFAULT_START_HOUR = 6;
const KANBAN_TIME_ALLOCATION_DEFAULT_VISIBLE_HOURS = 13;
const KANBAN_TIME_ALLOCATION_VISIBLE_HOURS_MIN = 4;
const KANBAN_TIME_ALLOCATION_VISIBLE_HOURS_MAX = 24;
const KANBAN_RESOURCE_TIMELINE_ZOOM_MIN = 0.6;
const KANBAN_RESOURCE_TIMELINE_ZOOM_MAX = 2.5;

function clampScheduleStartHour(value: number): number {
  return Math.max(0, Math.min(23, Math.floor(value)));
}

function clampScheduleEndHour(value: number, startHour: number): number {
  return Math.max(startHour + 1, Math.min(23, Math.floor(value)));
}

function normalizeScheduleIntervalMinutes(value: unknown): 15 | 30 | 60 | undefined {
  return value === 15 || value === 30 || value === 60 ? value : undefined;
}

function normalizeHiddenColumnKeys(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const normalizedValues = value
    .map((entry) => String(entry || '').trim())
    .filter(Boolean);

  return normalizedValues.length > 0 ? Array.from(new Set(normalizedValues)) : [];
}

function isKanbanBoardViewMode(value: unknown): value is ViewMode {
  return value === 'status' || value === 'assignment' || value === 'time_allocation' || value === 'resource_schedule';
}

function clampKanbanVisibleStartHour(value: number): number {
  return Math.max(KANBAN_VISIBLE_START_HOUR_MIN, Math.min(KANBAN_VISIBLE_START_HOUR_MAX, value));
}

function clampTimeAllocationVisibleHoursCount(value: number): number {
  return Math.max(
    KANBAN_TIME_ALLOCATION_VISIBLE_HOURS_MIN,
    Math.min(KANBAN_TIME_ALLOCATION_VISIBLE_HOURS_MAX, Math.floor(value)),
  );
}

function clampKanbanResourceTimelineZoom(value: number): number {
  return Math.max(KANBAN_RESOURCE_TIMELINE_ZOOM_MIN, Math.min(KANBAN_RESOURCE_TIMELINE_ZOOM_MAX, value));
}

function normalizeBoardPreferenceDate(value: unknown): string | undefined {
  if (typeof value !== 'string' || value.trim().length === 0) {
    return undefined;
  }

  const normalizedValue = value.trim();
  const parsedDate = new Date(`${normalizedValue}T00:00:00`);
  return Number.isNaN(parsedDate.getTime()) ? undefined : normalizedValue;
}

function normalizeKanbanBoardPreferenceState(value: unknown): KanbanBoardPreferenceState {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {
      version: KANBAN_BOARD_PREFERENCE_VERSION,
    };
  }

  const record = value as Record<string, unknown>;
  const visibleStartHour = typeof record.timeAllocationVisibleStartHour === 'number' && Number.isFinite(record.timeAllocationVisibleStartHour)
    ? clampKanbanVisibleStartHour(record.timeAllocationVisibleStartHour)
    : undefined;
  const timeAllocationDefaultStartHour = typeof record.timeAllocationDefaultStartHour === 'number' && Number.isFinite(record.timeAllocationDefaultStartHour)
    ? clampScheduleStartHour(record.timeAllocationDefaultStartHour)
    : undefined;
  const timeAllocationVisibleHoursCount = typeof record.timeAllocationVisibleHoursCount === 'number' && Number.isFinite(record.timeAllocationVisibleHoursCount)
    ? clampTimeAllocationVisibleHoursCount(record.timeAllocationVisibleHoursCount)
    : undefined;
  const zoomScale = typeof record.zoomScale === 'number' && Number.isFinite(record.zoomScale)
    ? Math.max(0.5, Math.min(1.5, record.zoomScale))
    : undefined;
  const resourceScheduleTimelineZoom = typeof record.resourceScheduleTimelineZoom === 'number' && Number.isFinite(record.resourceScheduleTimelineZoom)
    ? clampKanbanResourceTimelineZoom(record.resourceScheduleTimelineZoom)
    : undefined;
  const resourceScheduleVisibleStartHour = typeof record.resourceScheduleVisibleStartHour === 'number' && Number.isFinite(record.resourceScheduleVisibleStartHour)
    ? clampKanbanVisibleStartHour(record.resourceScheduleVisibleStartHour)
    : undefined;
  const resourceScheduleStartHour = typeof record.resourceScheduleStartHour === 'number' && Number.isFinite(record.resourceScheduleStartHour)
    ? clampScheduleStartHour(record.resourceScheduleStartHour)
    : undefined;
  const resourceScheduleEndHour = typeof record.resourceScheduleEndHour === 'number' && Number.isFinite(record.resourceScheduleEndHour)
    ? clampScheduleEndHour(
      record.resourceScheduleEndHour,
      resourceScheduleStartHour ?? clampScheduleStartHour(SERVICE_OPERATIONS_CONFIG.startHour ?? 7),
    )
    : undefined;
  const resourceScheduleIntervalMinutes = normalizeScheduleIntervalMinutes(record.resourceScheduleIntervalMinutes);
  const resourceScheduleTimelineMode = (record.resourceScheduleTimelineMode === 'dynamic' || record.resourceScheduleTimelineMode === 'static')
    ? record.resourceScheduleTimelineMode
    : undefined;
  const statusHiddenColumnKeys = Array.isArray(record.statusHiddenColumnKeys)
    ? sanitizeHiddenColumnKeys(
      record.statusHiddenColumnKeys,
      record.statusHiddenColumnKeys.filter((value): value is string => typeof value === 'string'),
    )
    : undefined;
  const assignmentHiddenColumnKeys = normalizeHiddenColumnKeys(record.assignmentHiddenColumnKeys);
  const timeAllocationHiddenColumnKeys = normalizeHiddenColumnKeys(record.timeAllocationHiddenColumnKeys);
  const resourceScheduleHiddenColumnKeys = normalizeHiddenColumnKeys(record.resourceScheduleHiddenColumnKeys);
  const statusColumnOrder = Array.isArray(record.statusColumnOrder)
    ? record.statusColumnOrder.map((value) => String(value || '').trim()).filter(Boolean)
    : undefined;

  return {
    version: KANBAN_BOARD_PREFERENCE_VERSION,
    selectedViewMode: isKanbanBoardViewMode(record.selectedViewMode) ? record.selectedViewMode : undefined,
    selectedDateISO: normalizeBoardPreferenceDate(record.selectedDateISO),
    timeAllocationVisibleStartHour: visibleStartHour,
    timeAllocationDefaultStartHour,
    timeAllocationVisibleHoursCount,
    resourceScheduleSidebarVisible: typeof record.resourceScheduleSidebarVisible === 'boolean'
      ? record.resourceScheduleSidebarVisible
      : undefined,
    zoomScale,
    resourceScheduleTimelineZoom,
    resourceScheduleVisibleStartHour,
    statusHiddenColumnKeys,
    assignmentHiddenColumnKeys,
    timeAllocationHiddenColumnKeys,
    resourceScheduleHiddenColumnKeys,
    statusColumnOrder,
    resourceScheduleStartHour,
    resourceScheduleEndHour,
    resourceScheduleIntervalMinutes,
    resourceScheduleTimelineMode,
  };
}

function areStringSetsEqual(left: Set<string>, right: Set<string>): boolean {
  if (left.size !== right.size) {
    return false;
  }

  for (const value of left) {
    if (!right.has(value)) {
      return false;
    }
  }

  return true;
}

function areStringArraysEqual(left: string[] | undefined, right: string[] | undefined): boolean {
  if (left === right) return true;
  if (!left || !right) return false;
  if (left.length !== right.length) return false;

  const sortedLeft = [...left].sort();
  const sortedRight = [...right].sort();

  for (let i = 0; i < sortedLeft.length; i++) {
    if (sortedLeft[i] !== sortedRight[i]) {
      return false;
    }
  }

  return true;
}

function areAssignedUsersListsEqual(left: AssignedUser[], right: AssignedUser[]): boolean {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((user, index) => {
    const candidate = right[index];
    return (
      user.id === candidate.id
      && user.email === candidate.email
      && user.first_name === candidate.first_name
      && user.last_name === candidate.last_name
      && user.role === candidate.role
      && user.isInactive === candidate.isInactive
    );
  });
}

function getHiddenColumnKeysForViewMode(
  preferenceState: KanbanBoardPreferenceState,
  viewMode: ViewMode,
): string[] | undefined {
  switch (viewMode) {
    case 'status':
      return preferenceState.statusHiddenColumnKeys;
    case 'assignment':
      return preferenceState.assignmentHiddenColumnKeys;
    case 'time_allocation':
      return preferenceState.timeAllocationHiddenColumnKeys;
    case 'resource_schedule':
      return preferenceState.resourceScheduleHiddenColumnKeys;
    default:
      return undefined;
  }
}

type ExtendedWorkflowState = WorkflowState & {
  isStart?: boolean;
  isEnd?: boolean;
};

function getItemCustomFields(item: WorkItem | null | undefined): Record<string, unknown> {
  const extendedItem = item as ExtendedWorkItem | null | undefined;
  return extendedItem?.custom_fields ?? {};
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function getNestedFieldValue(source: Record<string, unknown> | null | undefined, path: string): unknown {
  if (!source || !path) {
    return undefined;
  }

  return path.split('.').reduce<unknown>((value, segment) => {
    if (value == null || typeof value !== 'object') {
      return undefined;
    }

    return (value as Record<string, unknown>)[segment];
  }, source);
}

function getWorkflowValue(item: WorkItem, workflowField: string): string {
  const extendedItem = item as ExtendedWorkItem;

  if (workflowField === 'status') {
    return item.item_type === 'appointment'
      ? (getAppointmentOperationalStatus(item) || 'new')
      : (item.status || 'new');
  }

  const rawValue = workflowField === 'current_workflow_stage'
    ? extendedItem.current_workflow_stage
    : getNestedFieldValue(extendedItem as unknown as Record<string, unknown>, workflowField);

  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return 'new';
  }

  return String(rawValue);
}

function setNestedFieldValue(target: Record<string, unknown>, path: string[], value: unknown): Record<string, unknown> {
  if (path.length === 0) {
    return target;
  }

  const [head, ...rest] = path;

  if (rest.length === 0) {
    return {
      ...target,
      [head]: value,
    };
  }

  const nextValue = target[head];
  const nextTarget = nextValue && typeof nextValue === 'object'
    ? (nextValue as Record<string, unknown>)
    : {};
  return {
    ...target,
    [head]: setNestedFieldValue(nextTarget, rest, value),
  };
}

function withWorkflowValue(item: WorkItem, workflowField: string, value: string): WorkItem {
  if (workflowField === 'status') {
    return { ...item, status: value };
  }

  if (workflowField === 'current_workflow_stage') {
    return { ...item, current_workflow_stage: value } as WorkItem;
  }

  const path = workflowField.split('.');
  const updatedItem = setNestedFieldValue(item as unknown as Record<string, unknown>, path, value) as WorkItem;

  if (path[0] === 'custom_fields' && path.length === 2) {
    return {
      ...updatedItem,
      [path[1]]: value,
    } as WorkItem;
  }

  return updatedItem;
}

interface PendingAppointmentSetup {
  activeItem: WorkItem;
  scheduledStartISO: string;
  scheduledEndISO: string;
  newAssignedTo: number | string | null;
  assignedTeamId?: string | null;
  assignedUserIds?: Array<number | string | null | undefined>;
  duration: number;
  sourceView: 'time_allocation' | 'resource_schedule';
}

function normalizeAssignedUserId(value: number | string | null | undefined): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return null;
    }

    const parsedValue = Number.parseInt(trimmedValue, 10);
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
  }

  return null;
}

type AssignmentAwareWorkItem = WorkItem & {
  assigned_users?: unknown;
  assigned_users_details?: unknown;
  custom_fields?: Record<string, unknown> | null;
};

function parseAssignmentSourceArray(value: unknown): unknown[] {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed.startsWith('[')) {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
}

function getAssignmentUserIdFromValue(value: unknown): number | null {
  const directId = normalizeAssignedUserId(value as number | string | null | undefined);
  if (directId != null) {
    return directId;
  }

  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const record = value as Record<string, unknown>;
  const candidateKeys = ['user_id', 'userId', 'id', 'value', 'member_id', 'employee_id'];

  for (const key of candidateKeys) {
    const candidateId = normalizeAssignedUserId(record[key] as number | string | null | undefined);
    if (candidateId != null) {
      return candidateId;
    }
  }

  return null;
}

function getParsedAssignedUserIds(item: WorkItem): number[] {
  const assignmentAwareItem = item as AssignmentAwareWorkItem;
  const customFields = assignmentAwareItem.custom_fields && typeof assignmentAwareItem.custom_fields === 'object'
    ? assignmentAwareItem.custom_fields
    : {};
  const assignedUserIds = new Set<number>();

  [
    assignmentAwareItem.assigned_users,
    assignmentAwareItem.assigned_users_details,
    item.assignees,
    customFields.assigned_users,
    customFields.assigned_users_details,
    customFields.assigned_user_ids,
    customFields.participant_user_ids,
  ].forEach((source) => {
    parseAssignmentSourceArray(source).forEach((entry) => {
      const entryUserId = getAssignmentUserIdFromValue(entry);
      if (entryUserId != null) {
        assignedUserIds.add(entryUserId);
      }
    });
  });

  const assignedToUserId = normalizeAssignedUserId(item.assigned_to);
  if (assignedToUserId != null) {
    assignedUserIds.add(assignedToUserId);
  }

  return Array.from(assignedUserIds);
}

function getPrimaryAssignedUserId(item: WorkItem, assignedUserIds: number[]): number | null {
  const assignedToUserId = normalizeAssignedUserId(item.assigned_to);
  if (assignedToUserId != null && assignedUserIds.includes(assignedToUserId)) {
    return assignedToUserId;
  }

  return assignedUserIds[0] ?? null;
}

function getNormalizedAssignedTeamId(item: WorkItem): string | null {
  return typeof item.assigned_team === 'string' && item.assigned_team.trim().length > 0
    ? item.assigned_team
    : null;
}

function isItemAssignedToUserLane(item: WorkItem, userId: number): boolean {
  if (getNormalizedAssignedTeamId(item)) {
    return false;
  }

  return getParsedAssignedUserIds(item).includes(userId);
}

function formatAssignedUserDisplayName(user?: Pick<AssignedUser, 'first_name' | 'last_name' | 'email'> | null): string | null {
  if (!user) {
    return null;
  }

  const fullName = [user.first_name, user.last_name]
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .join(' ')
    .trim();

  return fullName || user.email || null;
}

function getFallbackAssigneeName(item: WorkItem): string | null {
  const candidates = [item.assignee_name, item.assigned_to_name];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate.trim();
    }
  }

  return null;
}

function buildAppointmentCardAssignees(
  item: WorkItem,
  assignedUsers: AssignedUser[],
): AppointmentCardAssignee[] {
  const explicitAssignees = Array.isArray(item.assignees)
    ? item.assignees
      .map((assignee): AppointmentCardAssignee | null => {
        const name = [assignee.first_name, assignee.last_name]
          .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
          .join(' ')
          .trim() || assignee.email || null;

        if (!name) {
          return null;
        }

        return {
          id: assignee.user_id,
          name,
          isPrimary: assignee.user_id === normalizeAssignedUserId(item.assigned_to),
        };
      })
      .filter((assignee): assignee is AppointmentCardAssignee => Boolean(assignee))
    : [];

  if (explicitAssignees.length > 0) {
    return explicitAssignees;
  }

  return getParsedAssignedUserIds(item)
    .map((userId): AppointmentCardAssignee | null => {
      const user = assignedUsers.find((candidate) => candidate.id === userId);
      const name = formatAssignedUserDisplayName(user);
      if (!name) {
        return null;
      }

      return {
        id: userId,
        name,
        isPrimary: userId === normalizeAssignedUserId(item.assigned_to),
      };
    })
    .filter((assignee): assignee is AppointmentCardAssignee => Boolean(assignee));
}

const APPOINTMENT_STYLE_CARD_ITEM_TYPES = new Set(['appointment', 'job', 'project', 'task']);

const _MS_PER_DAY = 1000 * 60 * 60 * 24;
function getRelativeDateLabel(dateString: string): string {
  const date = new Date(dateString);
  const now = Date.now();
  const diffDays = Math.ceil((date.getTime() - now) / _MS_PER_DAY);

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays > 0) return `In ${diffDays} days`;
  return `${Math.abs(diffDays)} days ago`;
}

function formatKanbanStatusLabel(status?: string | null): string | null {
  const normalized = String(status || '').trim().toLowerCase();
  if (!normalized || normalized === 'open' || normalized === 'new') {
    return null;
  }
  return formatAppointmentDisplayStatusLabel(normalized);
}

function getKanbanStatusTone(status?: string | null): AppointmentCardTone {
  return getToneForAppointmentStatusKey(status || '');
}

function getKanbanPriorityPresentation(priority?: WorkItem['priority'] | null): { label: string | null; tone: AppointmentCardTone } {
  switch (priority) {
    case 'critical':
      return { label: '⬤ Critical', tone: 'red' };
    case 'urgent':
      return { label: '🔴 Urgent', tone: 'red' };
    case 'high':
      return { label: '▲ High', tone: 'orange' };
    case 'low':
      return { label: '○ Low', tone: 'gray' };
    default:
      return { label: null, tone: 'gray' };
  }
}

function getGenericWorkItemTimingPresentation(item: WorkItem, isOverdue: boolean): { label: string | null; tone: AppointmentCardTone } {
  if (item.due_date) {
    if (isOverdue) {
      return { label: 'Overdue', tone: 'red' };
    }

    const dueLabel = getRelativeDateLabel(item.due_date);
    if (dueLabel === 'Today') {
      return { label: 'Due Today', tone: 'amber' };
    }

    if (dueLabel === 'Tomorrow') {
      return { label: 'Due Tomorrow', tone: 'blue' };
    }
  }

  if (item.scheduled_start) {
    return { label: 'Scheduled', tone: 'violet' };
  }

  if (item.start_date) {
    return { label: 'Planned', tone: 'gray' };
  }

  return { label: null, tone: 'gray' };
}

function getGenericWorkItemScheduleLabel(item: WorkItem): string | null {
  if (item.due_date) {
    return `Due ${getRelativeDateLabel(item.due_date)}`;
  }

  if (item.scheduled_start) {
    return `Scheduled ${getRelativeDateLabel(item.scheduled_start)}`;
  }

  if (item.start_date) {
    return `Starts ${getRelativeDateLabel(item.start_date)}`;
  }

  return null;
}

// Types and constants (WorkflowState, AssignedUser, UserLeave, ZoomLevel, ZOOM_SETTINGS,
// COLOR_CLASSES, avatar helpers) imported from @/components/kanban/kanban-shared

// Icon mapping for common states
const STATE_ICONS: Record<string, typeof Archive> = {
  backlog: Archive,
  scheduled: Calendar,
  dispatched: Navigation,
  todo: ClipboardList,
  open: Target,
  en_route: Navigation,
  arrived: MapPin,
  in_progress: Activity,
  'in-progress': Activity,
  on_hold: PauseCircle,
  pending_approval: Clock,
  pending_closure: UserCheck,
  review: Eye,
  testing: Zap,
  done: CheckCircle2,
  resolved: UserCheck,
  completed: CheckCircle2,
  closed: CheckCircle2,
  blocked: AlertTriangle,
  cancelled: XCircle
};

// Module-level currency formatter cache — avoids constructing Intl.NumberFormat on every render
const _currencyFormatterCache = new Map<string, Intl.NumberFormat>();
function getCurrencyFormatter(currency: string): Intl.NumberFormat {
  let fmt = _currencyFormatterCache.get(currency);
  if (!fmt) {
    fmt = new Intl.NumberFormat('en-ZA', { style: 'currency', currency, maximumFractionDigits: 0 });
    _currencyFormatterCache.set(currency, fmt);
  }
  return fmt;
}

// Default fallback columns - Jira-style workflow
// NOTE: Consolidated columns to reduce confusion:
// - pending_approval: All items awaiting any type of approval (replaces pending_closure, review)
// - resolved: Staff have finished work — awaiting admin review before closing
// - closed: Admin-approved final state (only admins can move items here)
const DEFAULT_COLUMNS: WorkflowState[] = [
  { key: 'open', label: 'Unassigned', color: 'gray', description: 'New items not yet assigned or scheduled' },
  { key: 'backlog', label: 'Backlog', color: 'slate', description: 'Items queued for future work' },
  { key: 'scheduled', label: 'Scheduled', color: 'cyan', description: 'Scheduled for specific date/time' },
  { key: 'dispatched', label: 'Dispatched', color: 'violet', description: 'Dispatched to technician via route' },
  { key: 'en_route', label: 'En Route', color: 'blue', description: 'Technician travelling to location' },
  { key: 'arrived', label: 'Arrived', color: 'indigo', description: 'Technician arrived at location' },
  { key: 'in_progress', label: 'In Progress', color: 'yellow', description: 'Currently being worked on' },
  { key: 'on_hold', label: 'On Hold', color: 'orange', description: 'Work paused or blocked' },
  { key: 'pending_approval', label: 'Pending Approval', color: 'amber', description: 'Submitted, awaiting approval before completion' },
  { key: 'resolved', label: 'Resolved', color: 'teal', description: 'Staff completed — awaiting admin review to close' },
  { key: 'completed', label: 'Completed', color: 'emerald', description: 'Completed successfully' },
  { key: 'closed', label: 'Closed', color: 'slate', description: 'Admin-approved and closed (admin only)' },
  { key: 'cancelled', label: 'Cancelled', color: 'red', description: 'Work cancelled' }
];

// Status aliases - maps legacy/alternate status values to consolidated columns
// This ensures items with old statuses still appear in the correct column
const STATUS_ALIASES: Record<string, string> = {
  // Unassigned aliases - new items that haven't been assigned yet
  'new': 'unassigned',
  'open': 'unassigned',
  'todo': 'unassigned',
  'to_do': 'unassigned',
  'draft': 'unassigned',
  // Backlog aliases
  'planning': 'backlog',
  'ready': 'backlog',
  'queued': 'backlog',
  // In Progress aliases
  'active': 'in_progress',
  'working': 'in_progress',
  'started': 'in_progress',
  // Pending approval aliases
  'pending_closure': 'pending_approval',
  'review': 'pending_approval',
  'admin_approval': 'pending_approval',
  'awaiting_approval': 'pending_approval',
  'under_review': 'pending_approval',
  // Completed aliases
  'done': 'completed',
  'finished': 'completed',
  'paid': 'completed',
  'delivered': 'completed'
  // NOTE: 'resolved' and 'closed' are now their own columns — NOT aliased to 'completed'
  // resolved = staff finished work, awaiting admin review
  // closed = admin-approved closure
};

// ============================================================================
// KANBAN CARD COMPONENT
// ============================================================================

function KanbanCardBase({
  item,
  isDragging,
  isSelected,
  onSelect,
  onView,
  onUpdateStatus,
  template,
  zoomLevel = 'normal',
  assignedUsers = [],
  operationsTeamsById,
  presetConfig, // Kanban style preset configuration (January 2026)
  onOpenTeamMember
}: {
  item: WorkItem;
  isDragging?: boolean;
  isSelected?: boolean;
  onSelect?: (itemId: string) => void;
  onView?: (itemId: string) => void;
  onUpdateStatus?: (itemId: string, newStatus: string) => Promise<void>;
  template?: WorkItemTemplate | null;
  zoomLevel?: ZoomLevel;
  assignedUsers?: AssignedUser[];
  operationsTeamsById?: Map<string, OperationsAssignmentTeam>;
  presetConfig?: KanbanStylePreset;
  onOpenTeamMember?: (userId: number) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: sortableIsDragging,
  } = useSortable({ id: item.id });

  // Track click position for click vs drag detection
  const clickStartRef = useRef<{ x: number; y: number; time: number } | null>(null);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const zoomSettings = ZOOM_SETTINGS[zoomLevel];
  const { currentTheme } = useTheme();
  const isDark = currentTheme?.mode === 'dark' || presetConfig?.id === 'darkPro' || presetConfig?.id === 'neon';
  const currentUser = useMemo(() => authService.getUser(), []);
  const canSeeRescheduleStatus = useMemo(
    () => canSeeAppointmentRescheduleState(currentUser),
    [currentUser]
  );
  const operationalStatus = item.item_type === 'appointment'
    ? getAppointmentOperationalStatus(item)
    : item.status;
  const displayStatus = item.item_type === 'appointment'
    ? getAppointmentDisplayStatus(item, { privilegedViewer: canSeeRescheduleStatus })
    : item.status;
  const extendedItem = item as ExtendedWorkItem;

  const isOverdue = isDueDateOverdueSAST(item.due_date, {
    currentStatus: operationalStatus,
    terminalStatuses: ['done', 'closed'],
  });
  const paymentStatus = String(extendedItem.payment_status || '').trim().toLowerCase();
  const financeTotal = Number(extendedItem.grand_total || 0);
  const hasExplicitFinanceBalance = extendedItem.balance_due !== null && extendedItem.balance_due !== undefined;
  const financeBalance = hasExplicitFinanceBalance
    ? Number(extendedItem.balance_due || 0)
    : financeTotal;
  const financeCurrency = String(extendedItem.currency || 'ZAR');
  const moduleContext = String(extendedItem.module_context || '').trim().toLowerCase();
  const isFinanceDocumentCard = FINANCE_DOCUMENT_ITEM_TYPES.has(String(item.item_type || '').trim().toLowerCase())
    || moduleContext === 'financial-management';
  const showFinanceMeta = isFinanceDocumentCard && (
    paymentStatus.length > 0
    || financeTotal > 0
    || hasExplicitFinanceBalance
  );

  const formatFinanceCurrency = (amount: number) => getCurrencyFormatter(financeCurrency).format(Number.isFinite(amount) ? amount : 0);

  const financeStatusBadgeClass = paymentStatus
    ? ({
      paid: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      partially_paid: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
      partial: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      overdue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      unpaid: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      sent: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      outstanding: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    } as Record<string, string>)[paymentStatus] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';

  // Priority-based left border color
  const priorityBorderColor = {
    urgent: 'border-l-red-600',
    critical: 'border-l-red-700',
    high: 'border-l-orange-500',
    medium: 'border-l-yellow-500',
    low: 'border-l-green-500',
  }[item.priority || 'medium'] || 'border-l-gray-400';
  const primaryAssignee = item.assignees?.[0] || null;
  const cardAssignees = item.assignees ?? [];
  const progressPercentage = typeof item.progress_percentage === 'number'
    ? item.progress_percentage
    : Number(item.progress_percentage || 0);
  const usesAppointmentStyleCard = APPOINTMENT_STYLE_CARD_ITEM_TYPES.has((item.item_type || '').toLowerCase());
  const isAppointmentCard = item.item_type === 'appointment';
  const appointmentVisuals = useMemo(
    () => {
      if (!usesAppointmentStyleCard) {
        return null;
      }

      if (isAppointmentCard) {
        return buildAppointmentCardPresentation(item, isDark, {
          includePriorityLabel: zoomLevel !== 'compact',
          includeTimingLabel: true,
          appointmentPrivilegedViewer: canSeeRescheduleStatus,
        });
      }

      const genericPriority = getKanbanPriorityPresentation(item.priority);
      const genericTiming = getGenericWorkItemTimingPresentation(item, isOverdue);

      return {
        cardClassName: priorityBorderColor,
        timingLabel: genericTiming.label,
        timingTone: genericTiming.tone,
        statusLabel: formatKanbanStatusLabel(item.status),
        statusTone: getKanbanStatusTone(item.status),
        itemType: item.item_type,
        priorityLabel: zoomLevel !== 'compact' ? genericPriority.label : null,
        priorityTone: genericPriority.tone,
      };
    },
    [usesAppointmentStyleCard, isAppointmentCard, item, isDark, zoomLevel, isOverdue, priorityBorderColor, canSeeRescheduleStatus]
  );
  const appointmentCardAssignees = useMemo(
    () => usesAppointmentStyleCard ? buildAppointmentCardAssignees(item, assignedUsers) : [],
    [assignedUsers, usesAppointmentStyleCard, item]
  );
  const appointmentAssigneeName = useMemo(() => {
    if (!usesAppointmentStyleCard) {
      return null;
    }

    const assignedTeamId = getNormalizedAssignedTeamId(item);
    if (assignedTeamId) {
      return operationsTeamsById?.get(assignedTeamId)?.team_name || 'Assigned team';
    }

    return appointmentCardAssignees[0]?.name || getFallbackAssigneeName(item);
  }, [appointmentCardAssignees, usesAppointmentStyleCard, item, operationsTeamsById]);
  const appointmentProgressPercentage = useMemo(() => {
    if (!usesAppointmentStyleCard) {
      return null;
    }

    const normalizedProgress = Number.isFinite(progressPercentage) ? progressPercentage : 0;
    return getSuggestedProgressFromStatus(item.status, normalizedProgress) ?? normalizedProgress;
  }, [usesAppointmentStyleCard, item.status, progressPercentage]);
  const appointmentCardModel = useMemo<AppointmentCardModel | null>(() => {
    if (!usesAppointmentStyleCard || !appointmentVisuals) {
      return null;
    }

    const scheduleLabel = isAppointmentCard ? null : getGenericWorkItemScheduleLabel(item);

    return {
      id: item.id,
      title: item.title || item.item_number || `Untitled ${item.item_type || 'item'}`,
      itemNumber: isAppointmentCard ? (getAppointmentDisplayNumber(item) || item.item_number || item.id) : (item.item_number || item.id),
      groupNumber: null,
      isGroupLeader: isAppointmentCard ? isAppointmentGroupLeader(item) : false,
      groupLeaderName: isAppointmentCard ? getAppointmentGroupLeaderDisplayName(item, appointmentAssigneeName) : null,
      parentJobNumber: item.parent_job_number || null,
      timingLabel: appointmentVisuals.timingLabel,
      timingTone: appointmentVisuals.timingTone,
      statusLabel: appointmentVisuals.statusLabel,
      statusTone: appointmentVisuals.statusTone,
      priorityLabel: appointmentVisuals.priorityLabel,
      priorityTone: appointmentVisuals.priorityTone,
      scheduledStart: isAppointmentCard ? item.scheduled_start : null,
      scheduledEnd: isAppointmentCard ? item.scheduled_end : null,
      scheduleLabel,
      assigneeName: appointmentAssigneeName,
      assignees: getNormalizedAssignedTeamId(item) ? [] : appointmentCardAssignees,
      contactName: item.contact_name || null,
      locationLabel: item.location_name || item.location_address || null,
      description: item.description || null,
      compactDescription: item.location_name || item.contact_name || item.description || undefined,
      progressPercentage: appointmentProgressPercentage,
      progressLabel: 'Progress',
      isGrouped: false,
      tooltipLines: buildWorkItemTooltipLines(item, { appointmentPrivilegedViewer: canSeeRescheduleStatus }),
    };
  }, [
    appointmentAssigneeName,
    appointmentCardAssignees,
    appointmentProgressPercentage,
    appointmentVisuals,
    canSeeRescheduleStatus,
    isAppointmentCard,
    usesAppointmentStyleCard,
    item,
  ]);

  // Handle click - only open if it wasn't a drag
  const handleClick = (e: React.MouseEvent) => {
    // Don't open if we're dragging or just finished dragging
    if (sortableIsDragging) return;

    // Check if this was a real click (not end of drag)
    if (clickStartRef.current) {
      const dx = Math.abs(e.clientX - clickStartRef.current.x);
      const dy = Math.abs(e.clientY - clickStartRef.current.y);
      const elapsed = Date.now() - clickStartRef.current.time;

      // Only trigger click if mouse didn't move much and was quick (< 200ms)
      if (dx < 5 && dy < 5 && elapsed < 200) {
        e.preventDefault();
        e.stopPropagation();
        onView?.(item.id);
      }
    }
    clickStartRef.current = null;
  };

  // Track mousedown position for click detection
  const handleMouseDown = (e: React.MouseEvent) => {
    clickStartRef.current = { x: e.clientX, y: e.clientY, time: Date.now() };
  };

  // Build card styles from preset or fallback to defaults
  const cardStyles = presetConfig?.card;
  const cardClasses = cardStyles
    ? `${cardStyles.background} ${cardStyles.border} ${cardStyles.borderRadius} ${cardStyles.shadow} ${cardStyles.priorityBorderWidth}`
    : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded border-l-[3px]';
  const cardHoverClasses = cardStyles
    ? `${cardStyles.backgroundHover} ${cardStyles.shadowHover}`
    : 'hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-750';

  if (appointmentCardModel && appointmentVisuals) {
    const appointmentContentClassName = zoomLevel === 'expanded'
      ? 'p-3'
      : zoomLevel === 'compact'
        ? 'p-2.5'
        : 'p-3';

    const appointmentFooter = zoomLevel === 'expanded'
      ? (
        <div className="flex items-center justify-end gap-2">
          <QuickAddTaskButton
            variant="icon"
            size="sm"
            referenceType="work_item"
            referenceId={item.id}
            referenceUrl={`/client-zone/modules/total-operations?item=${item.id}`}
            title={`Follow up: ${item.title}`}
            description={`Review work item ${item.item_number}`}
            priority={item.priority as 'low' | 'medium' | 'high' | 'urgent'}
            dueDate={item.due_date || undefined}
            contextData={{
              item_type: item.item_type,
              item_number: item.item_number,
              current_status: item.status,
            }}
          />
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onView?.(item.id);
            }}
            className="rounded p-1 text-gray-400 transition-colors hover:text-blue-600 dark:hover:text-blue-400"
            title="Open item"
          >
            <Eye className="h-3.5 w-3.5" />
          </button>
        </div>
      )
      : undefined;

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        className={`
          kanban-card group relative
          select-none
          transition-all duration-100 ease-out
          ${sortableIsDragging ? 'opacity-0 dragging' : ''}
          ${isDragging ? 'rounded-xl ring-1 ring-blue-400' : ''}
        `}
      >
        {onSelect && (
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onSelect(item.id);
            }}
            className="absolute left-2 top-2 z-20 rounded-md bg-white/90 p-1 text-gray-500 shadow-sm transition hover:bg-white hover:text-purple-600 dark:bg-gray-900/80 dark:text-gray-300 dark:hover:bg-gray-900 dark:hover:text-purple-400"
            title={isSelected ? 'Deselect item' : 'Select item'}
          >
            {isSelected ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
          </button>
        )}

        <AppointmentCard
          appointment={appointmentCardModel}
          isDark={isDark}
          compact={zoomLevel === 'compact'}
          interactive
          omitDefaultBorder={isAppointmentCard}
          className={`${isAppointmentCard ? '' : 'border-l-[3px]'} ${appointmentVisuals.cardClassName} ${cardStyles?.borderRadius || 'rounded'} ${cardStyles?.shadow || 'shadow-sm'} ${isSelected ? 'ring-2 ring-purple-500' : ''} ${isOverdue ? 'ring-1 ring-red-400/70' : ''}`}
          contentClassName={appointmentContentClassName}
          footer={appointmentFooter}
        />
      </div>
    );
  }

  return (
    <WorkItemHoverCard
      as="div"
      workItem={item}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      className={`
        kanban-card group relative ${cardClasses} ${priorityBorderColor}
        select-none
        transition-all duration-100 ease-out
        ${sortableIsDragging ? 'opacity-0 dragging' : cardHoverClasses}
        ${isDragging ? 'ring-1 ring-blue-400' : ''}
        ${isSelected ? 'ring-2 ring-purple-500 bg-purple-50 dark:bg-purple-900/30' : ''}
        ${isOverdue ? 'bg-red-50/50 dark:bg-red-900/20' : ''}
      `}
    >
      {onSelect && (
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onSelect(item.id);
          }}
          className="absolute right-2 top-2 z-10 rounded-md bg-white/90 p-1 text-gray-500 shadow-sm transition hover:bg-white hover:text-purple-600 dark:bg-gray-900/80 dark:text-gray-300 dark:hover:bg-gray-900 dark:hover:text-purple-400"
          title={isSelected ? 'Deselect item' : 'Select item'}
        >
          {isSelected ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
        </button>
      )}

      <div className={cardStyles?.padding || zoomSettings.cardPadding}>
        {/* Compact: Just title + item number */}
        {zoomLevel === 'compact' ? (
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <span className={`text-[10px] font-mono ${cardStyles?.itemNumberColor || 'text-gray-400 dark:text-gray-500'} block mb-0.5`}>
                {item.item_number}
              </span>
              <h4 className={`font-medium ${cardStyles?.titleColor || 'text-gray-900 dark:text-white'} text-xs line-clamp-2 leading-tight group-hover:text-blue-600 dark:group-hover:text-blue-400`}>
                {item.title}
              </h4>
              {/* Compact Badges */}
              <div className="flex flex-wrap items-center gap-1 mt-1">
                {/* En Route Badge - Animated Travel Indicator */}
                {displayStatus === 'en_route' && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 flex items-center gap-0.5 animate-pulse">
                    🚗 En Route
                  </span>
                )}
                {/* Arrived Badge */}
                {displayStatus === 'arrived' && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 flex items-center gap-0.5">
                    📍 Arrived
                  </span>
                )}
                {/* Status Badge - show if not 'open' (unassigned) */}
                {displayStatus && displayStatus !== 'open' && displayStatus !== 'en_route' && displayStatus !== 'arrived' && (
                  <span className={`text-[9px] px-1 py-0.5 rounded font-medium ${getStandardWorkItemStatusBadgeClass(displayStatus, item.item_type)}`}>
                    {formatAppointmentDisplayStatusLabel(displayStatus)}
                  </span>
                )}
                {/* Scheduled Badge */}
                {(item.scheduled_start || item.scheduled_end) && (
                  <span className="text-[9px] px-1 py-0.5 rounded font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
                    📅
                  </span>
                )}
              </div>
              {/* Minimal footer for compact */}
              <div className={`flex items-center gap-2 mt-1 text-[10px] ${cardStyles?.metaColor || 'text-gray-500'}`}>
                {showFinanceMeta && (
                  <>
                    {paymentStatus && (
                      <span className={`px-1 py-0.5 rounded font-medium ${financeStatusBadgeClass}`}>
                        {paymentStatus.replace(/_/g, ' ')}
                      </span>
                    )}
                    <span className={`font-semibold ${financeBalance > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}`}>
                      {financeBalance > 0 ? formatFinanceCurrency(financeBalance) : 'Paid'}
                    </span>
                  </>
                )}
                {primaryAssignee ? (
                  <button
                    type="button"
                    className="flex items-center gap-1 hover:opacity-80 transition-opacity cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); if (onOpenTeamMember && primaryAssignee.user_id) onOpenTeamMember(primaryAssignee.user_id); }}
                    title={`View ${primaryAssignee.first_name || 'team member'} profile`}
                  >
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center text-white text-[6px] font-semibold ${getKBAvatarColor(primaryAssignee.email, primaryAssignee.first_name)}`}>
                      {getKBInitials(primaryAssignee.first_name, primaryAssignee.last_name, primaryAssignee.email)}
                    </div>
                    <span className={`${cardStyles?.metaColor ? 'text-blue-400' : 'text-blue-600'} truncate max-w-[70px]`}>
                      {primaryAssignee.first_name || 'Assigned'}
                    </span>
                  </button>
                ) : item.assigned_to ? (
                  <div className="flex items-center gap-1">
                    {(() => {
                      const userId = typeof item.assigned_to === 'string' ? parseInt(item.assigned_to) : item.assigned_to;
                      const user = assignedUsers.find(u => u.id === userId);
                      const name = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Assigned' : 'Assigned';
                      return (
                        <button
                          type="button"
                          className="flex items-center gap-1 hover:opacity-80 transition-opacity cursor-pointer"
                          onClick={(e) => { e.stopPropagation(); if (onOpenTeamMember && userId) onOpenTeamMember(userId); }}
                          title={`View ${name} profile`}
                        >
                          <div className={`w-4 h-4 rounded-full flex items-center justify-center text-white text-[6px] font-semibold ${getKBAvatarColor(user?.email, user?.first_name)}`}>
                            {getKBInitials(user?.first_name, user?.last_name, user?.email)}
                          </div>
                          <span className={`${cardStyles?.metaColor ? 'text-blue-400' : 'text-blue-600'} truncate max-w-[70px]`}>{name}</span>
                        </button>
                      );
                    })()}
                  </div>
                ) : null}
                {item.due_date && (
                  <span className={isOverdue ? 'text-red-600 font-medium' : ''}>
                    {getRelativeDateLabel(item.due_date)}
                  </span>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* Normal/Expanded view */
          <>
            {/* Item Title & Number */}
            <div className="mb-2">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-xs font-mono ${cardStyles?.itemNumberColor || 'text-gray-500 dark:text-gray-300'} ${cardStyles?.itemNumberBg || 'bg-gray-100 dark:bg-gray-700'} px-1.5 py-0.5 rounded`}>
                  {item.item_number}
                </span>
                {template && (
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded text-white font-medium"
                    style={{ backgroundColor: template.color || '#6B7280' }}
                  >
                    {template.template_name}
                  </span>
                )}
              </div>

              {/* Status/Assignment/Scheduled Badges */}
              <div className="flex flex-wrap items-center gap-1 mb-1.5">
                {/* En Route Badge - Animated Travel Indicator */}
                {displayStatus === 'en_route' && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 flex items-center gap-1 animate-pulse">
                    <span className="inline-block animate-bounce">🚗</span>
                    En Route
                  </span>
                )}
                {/* Arrived Badge */}
                {displayStatus === 'arrived' && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 flex items-center gap-1">
                    📍 Arrived
                  </span>
                )}
                {/* Status Badge - show if not 'open' (unassigned) */}
                {displayStatus && displayStatus !== 'open' && displayStatus !== 'en_route' && displayStatus !== 'arrived' && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${getStandardWorkItemStatusBadgeClass(displayStatus, item.item_type)}`}>
                    {formatAppointmentDisplayStatusLabel(displayStatus)}
                  </span>
                )}

                {/* Scheduled Badge - show if has scheduled times */}
                {(item.scheduled_start || item.scheduled_end) && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 flex items-center gap-0.5">
                    <Calendar className="w-2.5 h-2.5" />
                    Scheduled
                  </span>
                )}

                {/* Agent Badge - GavShell issue-work state on the item */}
                <WorkItemAgentChip workItem={item} />
              </div>
              <h4 className={`font-medium ${cardStyles?.titleColor || 'text-gray-900 dark:text-white'} ${zoomSettings.fontSize} line-clamp-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors leading-snug`}>
                {template && template.icon && (
                  <span className="mr-1">{template.icon}</span>
                )}
                {item.title}
              </h4>
            </div>

            {/* Description - Only if enabled for zoom level */}
            {zoomSettings.showDescription && item.description && (
              <p className={`text-xs ${cardStyles?.metaColor || 'text-gray-600 dark:text-gray-300'} mb-2 line-clamp-2`}>
                {item.description}
              </p>
            )}

            {/* SLA Timer for Support Tickets */}
            {item.item_subtype === 'support-desk' && (
              <div className="mb-2">
                <SLATimer
                  deadline={item.sla_resolution_deadline || null}
                  breached={item.sla_resolution_breached || false}
                  breachedAt={item.sla_resolution_breached_at}
                  label="Resolution"
                  variant="compact"
                  isPaused={item.status === 'waiting_on_customer'}
                  showIcon={true}
                />
              </div>
            )}

            {/* Progress Bar - Only if enabled for zoom level */}
            {zoomSettings.showProgress && progressPercentage > 0 && (
              <div className="mb-2">
                <div className={`flex items-center justify-between text-xs ${cardStyles?.metaColor || 'text-gray-600 dark:text-gray-300'} mb-1`}>
                  <span>Progress</span>
                  <span>{progressPercentage}%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1.5 overflow-hidden">
                  <div
                    className={`h-1.5 rounded-full transition-all duration-300 ${progressPercentage === 100 ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
                      progressPercentage >= 75 ? 'bg-gradient-to-r from-blue-500 to-cyan-500' :
                        progressPercentage >= 50 ? 'bg-gradient-to-r from-amber-500 to-yellow-500' :
                          progressPercentage >= 25 ? 'bg-gradient-to-r from-orange-500 to-amber-500' :
                            'bg-gradient-to-r from-red-500 to-orange-500'
                      }`}
                    style={{ width: `${progressPercentage}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Item Details - Assignee & Due Date */}
            <div className={`flex items-center gap-3 text-xs ${cardStyles?.metaColor || 'text-gray-500 dark:text-gray-300'} mt-2`}>
              {showFinanceMeta && (
                <div className="flex flex-wrap items-center gap-1.5 mr-1">
                  {paymentStatus && (
                    <span className={`px-1.5 py-0.5 rounded font-medium ${financeStatusBadgeClass}`}>
                      {paymentStatus.replace(/_/g, ' ')}
                    </span>
                  )}
                  <span className={`font-semibold ${financeBalance > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}`}>
                    {financeBalance > 0 ? `Bal ${formatFinanceCurrency(financeBalance)}` : 'Paid in full'}
                  </span>
                </div>
              )}

              {/* Assignee */}
              {primaryAssignee ? (
                <button
                  type="button"
                  className="flex items-center gap-1.5 hover:opacity-80 transition-opacity cursor-pointer"
                  onClick={(e) => { e.stopPropagation(); if (onOpenTeamMember && primaryAssignee.user_id) onOpenTeamMember(primaryAssignee.user_id); }}
                  title={`View ${primaryAssignee.first_name || 'team member'} profile`}
                >
                  <div className="flex -space-x-1">
                    {cardAssignees.slice(0, 2).map((a, idx) => (
                      <div key={a.user_id || idx} className={`w-5 h-5 rounded-full flex items-center justify-center text-white text-[7px] font-semibold ring-1 ring-white dark:ring-gray-800 ${getKBAvatarColor(a.email, a.first_name)}`}>
                        {getKBInitials(a.first_name, a.last_name, a.email)}
                      </div>
                    ))}
                  </div>
                  <span className={`${cardStyles?.metaColor ? 'text-blue-400' : 'text-blue-600'} font-medium truncate max-w-[80px]`}>
                    {cardAssignees.length === 1
                      ? (primaryAssignee.first_name || 'Assigned')
                      : `${cardAssignees.length} people`
                    }
                  </span>
                </button>
              ) : item.assigned_to ? (
                <div className="flex items-center gap-1.5">
                  {(() => {
                    const userId = typeof item.assigned_to === 'string' ? parseInt(item.assigned_to) : item.assigned_to;
                    const user = assignedUsers.find(u => u.id === userId);
                    const name = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Assigned' : 'Assigned';
                    return (
                      <button
                        type="button"
                        className="flex items-center gap-1.5 hover:opacity-80 transition-opacity cursor-pointer"
                        onClick={(e) => { e.stopPropagation(); if (onOpenTeamMember && userId) onOpenTeamMember(userId); }}
                        title={`View ${name} profile`}
                      >
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center text-white text-[7px] font-semibold ${getKBAvatarColor(user?.email, user?.first_name)}`}>
                          {getKBInitials(user?.first_name, user?.last_name, user?.email)}
                        </div>
                        <span className={`${cardStyles?.metaColor ? 'text-blue-400' : 'text-blue-600'} font-medium truncate max-w-[80px]`}>{name}</span>
                      </button>
                    );
                  })()}
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <User className="w-3 h-3 text-gray-400" />
                  <span className="text-gray-400">Unassigned</span>
                </div>
              )}

              {/* Due date */}
              {item.due_date && (
                <div className={`flex items-center gap-1.5 ${isOverdue ? 'text-red-600 font-medium' : ''}`}>
                  <Calendar className={`w-3 h-3 ${isOverdue ? 'text-red-500' : ''}`} />
                  <span>{getRelativeDateLabel(item.due_date)}</span>
                </div>
              )}
            </div>

            {/* Tags - Only if enabled for zoom level */}
            {zoomSettings.showTags && item.tags && item.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {item.tags.slice(0, 2).map((tag, index) => (
                  <span
                    key={index}
                    className={`px-1.5 py-0.5 ${cardStyles?.itemNumberBg || 'bg-gray-100 dark:bg-gray-700'} ${cardStyles?.itemNumberColor || 'text-gray-500 dark:text-gray-300'} text-[10px] rounded flex items-center gap-1`}
                  >
                    <Tag className="w-2 h-2" />
                    {tag}
                  </span>
                ))}
                {item.tags.length > 2 && (
                  <span className={`px-1.5 py-0.5 ${cardStyles?.itemNumberBg || 'bg-gray-100 dark:bg-gray-700'} ${cardStyles?.itemNumberColor || 'text-gray-500 dark:text-gray-300'} text-[10px] rounded`}>
                    +{item.tags.length - 2}
                  </span>
                )}
              </div>
            )}

            {/* Footer with actions */}
            {zoomLevel === 'expanded' && (
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                {/* Approve Closure Button - Only show for pending_closure status */}
                {item.status === 'pending_closure' && onUpdateStatus && (
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      const confirmed = await confirm({
                        title: 'Approve Closure',
                        message: 'Approve closure and mark this job as completed?',
                        variant: 'success',
                        confirmText: 'Approve',
                        cancelText: 'Cancel',
                        onConfirm: () => { },
                      });
                      if (confirmed) {
                        try {
                          await onUpdateStatus(item.id, 'completed');
                        } catch (error) {
                          console.error('Failed to approve closure:', error);
                          notify.error('Failed to approve closure. Please try again.');
                        }
                      }
                    }}
                    className="flex items-center gap-1 px-2 py-1 bg-green-500 text-white text-[10px] font-medium rounded hover:bg-green-600 transition-colors"
                  >
                    <CheckCircle2 className="w-3 h-3" />
                    Approve
                  </button>
                )}

                <div className="flex items-center gap-2">
                  <QuickAddTaskButton
                    variant="icon"
                    size="sm"
                    referenceType="work_item"
                    referenceId={item.id}
                    referenceUrl={`/client-zone/modules/total-operations?item=${item.id}`}
                    title={`Follow up: ${item.title}`}
                    description={`Review work item ${item.item_number}`}
                    priority={item.priority as 'low' | 'medium' | 'high' | 'urgent'}
                    dueDate={item.due_date || undefined}
                    contextData={{
                      item_type: item.item_type,
                      item_number: item.item_number,
                      current_status: item.status
                    }}
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onView?.(item.id);
                    }}
                    className="p-1 text-gray-400 hover:text-blue-600 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Eye className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </WorkItemHoverCard>
  );
}

// ============================================================================
// KANBAN CARD MEMO WRAPPER
// Prevents re-renders when parent columns re-render but card data is unchanged.
// Compares item identity via id + updated_at + status + priority (the fields
// most likely to mutate between refreshes), plus all other props by reference.
// ============================================================================

type KanbanCardProps = Parameters<typeof KanbanCardBase>[0];

function areKanbanCardPropsEqual(
  prev: KanbanCardProps,
  next: KanbanCardProps
): boolean {
  // Scalar equality — cheap first
  if (
    prev.isSelected !== next.isSelected ||
    prev.zoomLevel !== next.zoomLevel ||
    prev.isDragging !== next.isDragging
  ) return false;

  // Reference equality for stable objects
  if (
    prev.assignedUsers !== next.assignedUsers ||
    prev.operationsTeamsById !== next.operationsTeamsById ||
    prev.presetConfig !== next.presetConfig ||
    prev.template !== next.template ||
    prev.onView !== next.onView ||
    prev.onSelect !== next.onSelect ||
    prev.onUpdateStatus !== next.onUpdateStatus ||
    prev.onOpenTeamMember !== next.onOpenTeamMember
  ) return false;

  // Item identity — use id + updated_at as the primary change signal,
  // then fall back to the render-critical scalar fields for safety.
  const pi = prev.item;
  const ni = next.item;
  if (pi === ni) return true;
  if (
    pi.id !== ni.id ||
    pi.updated_at !== ni.updated_at ||
    pi.status !== ni.status ||
    pi.priority !== ni.priority ||
    pi.title !== ni.title ||
    pi.assigned_to !== ni.assigned_to ||
    pi.assigned_team !== ni.assigned_team ||
    pi.progress_percentage !== ni.progress_percentage ||
    pi.scheduled_start !== ni.scheduled_start ||
    pi.scheduled_end !== ni.scheduled_end ||
    pi.due_date !== ni.due_date ||
    pi.payment_status !== ni.payment_status ||
    pi.grand_total !== ni.grand_total ||
    pi.balance_due !== ni.balance_due
  ) return false;

  return true;
}

const KanbanCard = memo(KanbanCardBase, areKanbanCardPropsEqual);

// ============================================================================
// KANBAN COLUMN COMPONENT
// ============================================================================

function KanbanColumn({
  column,
  items,
  selectedItems,
  onItemSelect,
  onViewItem,
  onUpdateStatus,
  getTemplate,
  zoomLevel = 'normal',
  viewMode = 'status',
  assignedUsers = [],
  operationsTeamsById,
  isOver = false,
  activeId = null,
  dragHandleProps = null,
  isDraggingColumn = false,
  canAcceptDrops = true,
  presetConfig, // Kanban style preset configuration (January 2026)
  onOpenTeamMember
}: {
  column: WorkflowState;
  items: WorkItem[];
  selectedItems: string[];
  onItemSelect: (itemId: string) => void;
  onViewItem: (itemId: string) => void;
  onUpdateStatus: (itemId: string, newStatus: string) => Promise<void>;
  getTemplate: (item: WorkItem) => WorkItemTemplate | null;
  zoomLevel?: ZoomLevel;
  viewMode?: ViewMode;
  assignedUsers?: AssignedUser[];
  operationsTeamsById?: Map<string, OperationsAssignmentTeam>;
  isOver?: boolean;
  activeId?: string | null;
  dragHandleProps?: SortableDragListeners | null;
  isDraggingColumn?: boolean;
  canAcceptDrops?: boolean;
  presetConfig?: KanbanStylePreset;
  onOpenTeamMember?: (userId: number) => void;
}) {
  const { setNodeRef, isOver: dropIsOver } = useDroppable({
    id: column.key,
    disabled: !canAcceptDrops,
  });

  // Use either passed isOver or droppable's isOver
  const isDropTarget = isOver || dropIsOver;

  const zoomSettings = ZOOM_SETTINGS[zoomLevel];
  const colorClass = COLOR_CLASSES[column.color] || COLOR_CLASSES.gray;

  // Mobile-specific column width - use min-width instead of fixed for swipe scrolling
  const mobileColumnClass = zoomLevel === 'mobile' ? 'min-w-[85vw] max-w-[85vw]' : zoomSettings.columnWidth;

  // Determine icon based on view mode
  const StateIcon = viewMode === 'status'
    ? STATE_ICONS[column.key]
    : (column.key === 'unassigned' ? UserCheck : User);

  // For assignment view, find the user to get initials
  const isUserColumn = viewMode !== 'status' && column.key.startsWith('user-');
  const columnUser = isUserColumn ? assignedUsers.find(u => `user-${u.id}` === column.key) : null;

  // Build column styles from preset or fallback to defaults
  const columnStyles = presetConfig?.column;

  // NEW: Compute per-column header colors using getColumnHeaderColors for proper contrast
  // This returns intensity-aware text/background/border/accent colors for each column's base color
  const headerColors = presetConfig ? getColumnHeaderColors(presetConfig.id, column.color) : null;

  // Determine if preset headerBackground needs per-column color enrichment
  // Presets like vibrant/neon/glass/darkPro set headerBackground to just 'bg-gradient-to-r' (direction only, no color stops!)
  // Presets like pastel/corporate have complete headerBackground values with from-/to- color stops
  const isIncompleteHeaderBg = !columnStyles?.headerBackground ||
    (columnStyles.headerBackground.includes('gradient') && !columnStyles.headerBackground.includes('from-'));

  const effectiveHeaderBg = (headerColors && isIncompleteHeaderBg)
    ? `bg-gradient-to-r ${headerColors.background}`
    : (columnStyles?.headerBackground || '');

  // Always use per-column text color from getColumnHeaderColors when preset is active
  // This guarantees proper contrast against the actual header background
  const effectiveHeaderText = headerColors?.text || columnStyles?.headerTextColor || colorClass.headingText;

  // Add per-column border accent when preset is active
  const effectiveColumnBorder = (headerColors && columnStyles)
    ? `${columnStyles.border} ${headerColors.border}`
    : (columnStyles?.border || '');

  const columnClasses = columnStyles
    ? `${columnStyles.background} ${columnStyles.borderRadius} ${effectiveColumnBorder} ${columnStyles.shadow}`
    : `${colorClass.header} rounded-xl border-2`;

  return (
    <DropZoneHighlight isOver={isDropTarget && !!activeId} canDrop={canAcceptDrops}>
      <div
        ref={setNodeRef}
        className={`
          flex h-full min-h-0 flex-col ${mobileColumnClass} flex-shrink-0 
          ${columnClasses}
          ${isDropTarget && activeId ? 'ring-2 ring-blue-500 scale-[1.01] shadow-xl' : (columnStyles ? '' : colorClass.border)}
          ${isDraggingColumn ? 'opacity-50' : ''}
          transition-all duration-150 ease-out
          ${zoomLevel === 'mobile' ? 'snap-center' : ''}
        `}
      >
        {/* Column Header */}
        <div className={`flex-shrink-0 ${zoomSettings.headerPadding} border-b border-gray-200/50 dark:border-gray-600/50 ${effectiveHeaderBg} ${columnStyles?.borderRadius ? `${columnStyles.borderRadius} rounded-b-none` : ''}`}>
          <div className={`flex items-center ${zoomLevel === 'compact' ? 'flex-col gap-1' : 'justify-between mb-2'}`}>
            <div className="flex items-center gap-2">
              {/* Column Drag Handle */}
              {dragHandleProps && (
                <div
                  {...dragHandleProps}
                  className="cursor-grab active:cursor-grabbing p-1 -ml-1 rounded hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                  title="Drag to reorder column"
                >
                  <GripVertical className="w-4 h-4 text-gray-400" />
                </div>
              )}
              {columnUser ? (
                <button
                  type="button"
                  className="cursor-pointer hover:ring-2 hover:ring-orange-400 rounded-full transition-all"
                  onClick={(e) => { e.stopPropagation(); if (onOpenTeamMember && columnUser.id) onOpenTeamMember(columnUser.id); }}
                  title={`View ${columnUser.first_name || 'team member'} profile`}
                >
                  <div className={`${zoomLevel === 'compact' ? 'w-7 h-7' : 'w-9 h-9'} rounded-full flex items-center justify-center text-white ${zoomLevel === 'compact' ? 'text-[9px]' : 'text-xs'} font-semibold shadow-sm ${getKBAvatarColor(columnUser.email, columnUser.first_name)}`}>
                    {getKBInitials(columnUser.first_name, columnUser.last_name, columnUser.email)}
                  </div>
                </button>
              ) : StateIcon && (
                <div className={`p-${zoomLevel === 'compact' ? '1.5' : '2'} rounded-lg ${colorClass.iconBg} text-white shadow-sm`}>
                  <StateIcon className={`${zoomLevel === 'compact' ? 'w-3 h-3' : 'w-4 h-4'}`} />
                </div>
              )}
              <div>
                <div className="flex items-center gap-1.5">
                  <h3 className={`font-semibold ${effectiveHeaderText} ${zoomSettings.headerText}`}>{column.label}</h3>
                  {!canAcceptDrops && viewMode === 'status' && (
                    <Lock className={`w-3.5 h-3.5 ${effectiveHeaderText} opacity-70`} aria-label="System-managed status" />
                  )}
                </div>
                {column.description && zoomLevel !== 'compact' && (
                  <p className={`text-xs ${columnStyles?.subtitleTextColor || colorClass.subtitleText}`}>{column.description}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`font-bold ${columnStyles?.countBadge ? `${columnStyles.countBadge.background} ${columnStyles.countBadge.textColor} ${columnStyles.countBadge.borderRadius}` : `${effectiveHeaderText} bg-white/50 dark:bg-black/20 rounded-full`} ${zoomSettings.badgeSize} px-2 py-1`}>
                {items.length}
              </span>
            </div>
          </div>
        </div>

        {/* Column Content */}
        <div className={`flex-1 min-h-0 ${zoomSettings.columnPadding} space-y-${zoomLevel === 'compact' ? '2' : '3'} overflow-y-auto overscroll-contain pr-1 ${zoomLevel === 'mobile' ? 'custom-scrollbar' : 'kanban-scrollbar'}`}>
          <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
            {items.map((item) => (
              <KanbanCard
                key={item.id}
                item={item}
                isSelected={selectedItems.includes(item.id)}
                onSelect={onItemSelect}
                onView={onViewItem}
                onUpdateStatus={onUpdateStatus}
                template={getTemplate(item)}
                zoomLevel={zoomLevel}
                assignedUsers={assignedUsers}
                operationsTeamsById={operationsTeamsById}
                presetConfig={presetConfig}
                onOpenTeamMember={onOpenTeamMember}
              />
            ))}
          </SortableContext>

          {items.length === 0 && (
            <div className="text-center py-8 text-gray-400 dark:text-gray-500">
              {StateIcon && <StateIcon className={`${zoomLevel === 'compact' ? 'w-8 h-8' : 'w-12 h-12'} mx-auto mb-2 opacity-50`} />}
              <p className={zoomSettings.fontSize}>No items in {(column.label || 'this column').toLowerCase()}</p>
            </div>
          )}
        </div>
      </div>
    </DropZoneHighlight>
  );
}

// ============================================================================
// SORTABLE KANBAN COLUMN WRAPPER
// Wraps KanbanColumn for drag-to-reorder functionality
// ============================================================================

function SortableKanbanColumn({
  column,
  items,
  selectedItems,
  onItemSelect,
  onViewItem,
  onUpdateStatus,
  getTemplate,
  zoomLevel,
  viewMode,
  assignedUsers,
  operationsTeamsById,
  isOver,
  activeId,
  isDraggingColumn,
  canAcceptDrops,
  presetConfig, // Kanban style preset configuration (January 2026)
  onOpenTeamMember
}: {
  column: WorkflowState;
  items: WorkItem[];
  selectedItems: string[];
  onItemSelect: (itemId: string) => void;
  onViewItem: (itemId: string) => void;
  onUpdateStatus: (itemId: string, newStatus: string) => Promise<void>;
  getTemplate: (item: WorkItem) => WorkItemTemplate | null;
  zoomLevel: ZoomLevel;
  viewMode: ViewMode;
  assignedUsers: AssignedUser[];
  operationsTeamsById?: Map<string, OperationsAssignmentTeam>;
  isOver: boolean;
  activeId: string | null;
  isDraggingColumn: boolean;
  canAcceptDrops: boolean;
  presetConfig?: KanbanStylePreset;
  onOpenTeamMember?: (userId: number) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `column-${column.key}`,
    data: {
      type: 'column',
      column
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} className="h-full min-h-0">
      <KanbanColumn
        column={column}
        items={items}
        selectedItems={selectedItems}
        onItemSelect={onItemSelect}
        onViewItem={onViewItem}
        onUpdateStatus={onUpdateStatus}
        getTemplate={getTemplate}
        zoomLevel={zoomLevel}
        viewMode={viewMode}
        assignedUsers={assignedUsers}
        operationsTeamsById={operationsTeamsById}
        isOver={isOver}
        activeId={activeId}
        dragHandleProps={listeners}
        isDraggingColumn={isDragging || isDraggingColumn}
        canAcceptDrops={canAcceptDrops}
        presetConfig={presetConfig}
        onOpenTeamMember={onOpenTeamMember}
      />
    </div>
  );
}

// TimeAllocationView and sub-components extracted to @/components/kanban/TimeAllocationView.tsx

// ============================================================================
// MAIN KANBAN BOARD COMPONENT
// ============================================================================

export default function WorkItemsKanbanBoard({
  items,
  onUpdateStatus,
  onUpdateAssignment,
  onViewItem,
  onRefresh,
  itemType,
  focusedItemType,
  templates = [],
  workflowStates,
  workflowStatuses: boardWorkflowStatuses,
  moduleKey = 'total-operations',
  workflowContext,
  assignedUsersOverride,
  currentUserId,
  boardKey: propBoardKey,
  preferenceScopeKey,
  onColumnsChange,
  onColumnsExposed,
  preferenceReloadKey = 0,
  workflowField = 'status', // Default to status field
  hideColumnSettings = false, // Hide column settings for workspaces
  useStrictWorkflowStates = false, // Don't merge with defaults for workspaces
  enableTimeAllocation = false, // Default to false - only show for operations/scheduling modules
  stylePreset = 'classic', // Kanban style preset (January 2026)
  onUpdateSchedule, // Schedule update callback for Resource Schedule Timeline
  defaultBoardViewMode, // Default board view mode (parent-controlled)
  hideBoardViewToggle = false, // Hide internal toggle when parent controls view mode
  onAppointmentChange, // Callback for appointment create/update events (schedule↔route sync)
  activeViewLabel,
  onOpenBoardPreferences,
  onOpenTeamMember, // Open team member profile modal
  hideToolbar = false,
  onToolbarModelChange,
  filtersActive = false,
  activeAssigneeUserIds,
  onScheduleDateChange,
}: KanbanBoardProps) {
  // Mobile responsiveness
  const { isMobile, isSmallScreen } = useResponsive();

  // Theme
  const { currentTheme } = useTheme();

  useEffect(() => {
    void logUiAudit({
      component: 'WorkItemsKanbanBoard',
      action: 'mount',
      moduleKey,
      details: `Kanban board (${itemType})`,
      metadata: { itemType: itemType || null, boardKey: propBoardKey },
    });
  }, [moduleKey, itemType, propBoardKey]);

  // Ref to track when preferenceReloadKey last changed - skip persistence for 2 seconds after
  const preferenceReloadTimestampRef = useRef<number>(0);
  // Ref to track previous hidden keys to prevent infinite loops
  const prevHiddenKeysRef = useRef<string[]>([]);
  const isDark = currentTheme?.mode === 'dark' || stylePreset === 'darkPro' || stylePreset === 'neon';

  // Get the style preset configuration
  const presetConfig = useMemo(() => getKanbanStylePreset(stylePreset), [stylePreset]);
  const { jobTypes: configAppointmentTypes } = useJobConfig({ itemType: 'appointment' });
  const appointmentTypeOptions = useMemo(
    () => (configAppointmentTypes.length > 0 ? configAppointmentTypes : DEFAULT_APPOINTMENT_TYPES),
    [configAppointmentTypes]
  );

  // Generate board key from props if not provided
  const boardKey = propBoardKey || `${moduleKey || 'default'}-${itemType}`;
  const resolvedPreferenceScopeKey = preferenceScopeKey || boardKey;
  const boardPreferenceKey = useMemo(
    () => `work-items-view:kanban-board:${resolvedPreferenceScopeKey}`,
    [resolvedPreferenceScopeKey]
  );


  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [zoomLevel, setZoomLevel] = useState<ZoomLevel>('normal');
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set());
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [optimisticItems, setOptimisticItems] = useState<WorkItem[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>(defaultBoardViewMode || 'status');
  const [assignedUsers, setAssignedUsers] = useState<AssignedUser[]>([]);
  const [appointmentAssigneeUsers, setAppointmentAssigneeUsers] = useState<AssignedUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const { teams: allOperationsTeams, contractors: availableContractors } = useOperationsAssignmentOptions();
  const [resourceScheduleSidebarVisible, setResourceScheduleSidebarVisible] = useState(
    SERVICE_OPERATIONS_CONFIG.showUnscheduledSidebar ?? true
  );
  const [resourceScheduleTimelineZoom, setResourceScheduleTimelineZoom] = useState(1);

  const includeOperationsTeams = useMemo(() => {
    const normalizedItemType = (focusedItemType || itemType || '').toLowerCase();
    return enableTimeAllocation || ['job', 'appointment', 'task'].includes(normalizedItemType) || moduleKey === 'service-operations';
  }, [enableTimeAllocation, focusedItemType, itemType, moduleKey]);

  const operationsTeams = useMemo(
    () => includeOperationsTeams ? allOperationsTeams : [],
    [allOperationsTeams, includeOperationsTeams]
  );

  const operationsTeamsById = useMemo(() => {
    const lookup = new Map<string, OperationsAssignmentTeam>();
    operationsTeams.forEach((team) => lookup.set(team.id, team));
    return lookup;
  }, [operationsTeams]);

  const buildPlaceholderAssignees = useCallback((userIds: number[]) => {
    return userIds.map((userId) => ({ user_id: userId, first_name: '', last_name: '', email: '' }));
  }, []);

  const getItemAssignedUserIds = useCallback((item: WorkItem): number[] => {
    return getParsedAssignedUserIds(item);
  }, []);

  const resolveAssignmentTarget = useCallback((columnKey: string) => {
    if (columnKey === 'unassigned') {
      return {
        kind: 'unassigned' as const,
        assignedTo: null,
        assignedTeam: null,
        assignedUserIds: [] as number[],
        label: 'Unassigned',
      };
    }

    if (columnKey.startsWith('team-')) {
      const teamId = columnKey.replace('team-', '');
      const team = operationsTeamsById.get(teamId);
      const assignedUserIds = team?.memberUserIds || [];
      const assignedTo = team?.leader?.user_id || assignedUserIds[0] || null;

      return {
        kind: 'team' as const,
        assignedTo,
        assignedTeam: teamId,
        assignedUserIds,
        label: team?.team_name || 'Operations Team',
      };
    }

    if (columnKey.startsWith('user-')) {
      const assignedTo = Number.parseInt(columnKey.replace('user-', ''), 10);
      return {
        kind: 'user' as const,
        assignedTo: Number.isFinite(assignedTo) ? assignedTo : null,
        assignedTeam: null,
        assignedUserIds: Number.isFinite(assignedTo) ? [assignedTo] : [],
        label: 'User',
      };
    }

    return null;
  }, [operationsTeamsById]);

  const buildAppointmentAssignmentForTarget = useCallback((item: WorkItem, assignmentTarget: ReturnType<typeof resolveAssignmentTarget> | null) => {
    if (!assignmentTarget) {
      return {
        assignedTo: null,
        assignedTeam: null,
        assignedUserIds: [] as number[],
      };
    }

    if (assignmentTarget.kind === 'team') {
      return {
        assignedTo: assignmentTarget.assignedTo,
        assignedTeam: assignmentTarget.assignedTeam,
        assignedUserIds: assignmentTarget.assignedUserIds,
      };
    }

    if (item.item_type !== 'appointment') {
      return {
        assignedTo: assignmentTarget.assignedTo,
        assignedTeam: assignmentTarget.assignedTeam,
        assignedUserIds: assignmentTarget.assignedUserIds,
      };
    }

    const baseAssignedUserIds = getNormalizedAssignedTeamId(item)
      ? []
      : getItemAssignedUserIds(item);
    const mergedAssignedUserIds = Array.from(new Set([
      ...baseAssignedUserIds,
      ...assignmentTarget.assignedUserIds,
    ])).filter((userId) => Number.isFinite(userId) && userId > 0);

    return {
      assignedTo: getPrimaryAssignedUserId(item, mergedAssignedUserIds) ?? assignmentTarget.assignedTo,
      assignedTeam: null,
      assignedUserIds: mergedAssignedUserIds,
    };
  }, [getItemAssignedUserIds]);

  // Sync viewMode when parent changes defaultBoardViewMode
  useEffect(() => {
    if (defaultBoardViewMode) {
      setViewMode(defaultBoardViewMode);
    }
  }, [defaultBoardViewMode]);
  const [showAddColumn, setShowAddColumn] = useState(false);
  const [persistedColumns, setPersistedColumns] = useState<WorkflowState[] | null>(null);
  const [boardPreferenceState, setBoardPreferenceState] = useState<KanbanBoardPreferenceState>({
    version: KANBAN_BOARD_PREFERENCE_VERSION,
  });
  const [boardPreferencesLoaded, setBoardPreferencesLoaded] = useState(false);
  const [columnsInitialized, setColumnsInitialized] = useState(false);
  const [lastViewMode, setLastViewMode] = useState(viewMode);
  const [overColumnId, setOverColumnId] = useState<string | null>(null);
  const [columnOrder, setColumnOrder] = useState<string[]>([]); // Custom column order
  const [draggingColumnId, setDraggingColumnId] = useState<string | null>(null); // Track column being dragged
  const [selectedDate, setSelectedDate] = useState(new Date()); // For time allocation view
  const [dropHour, setDropHour] = useState<number | null>(null); // Track drop hour for time allocation
  const [hoveredHour, setHoveredHour] = useState<{ columnKey: string; hour: number } | null>(null); // Track hovered hour for visual feedback

  // Lift selectedDate to parent so the API query can be date-windowed (schedule views only)
  useEffect(() => {
    if (onScheduleDateChange) {
      onScheduleDateChange(selectedDate);
    }
  }, [selectedDate, onScheduleDateChange]);

  // Appointment setup state for drag-drop scheduling
  const [pendingAppointmentSetup, setPendingAppointmentSetup] = useState<PendingAppointmentSetup | null>(null);

  const getDefaultTimeAllocationStartHour = useCallback(() => {
    if (typeof boardPreferenceState.timeAllocationDefaultStartHour === 'number') {
      return clampScheduleStartHour(boardPreferenceState.timeAllocationDefaultStartHour);
    }

    return KANBAN_TIME_ALLOCATION_DEFAULT_START_HOUR;
  }, [boardPreferenceState.timeAllocationDefaultStartHour]);

  const timeAllocationVisibleHoursCount = useMemo(() => {
    if (typeof boardPreferenceState.timeAllocationVisibleHoursCount === 'number') {
      return clampTimeAllocationVisibleHoursCount(boardPreferenceState.timeAllocationVisibleHoursCount);
    }

    return KANBAN_TIME_ALLOCATION_DEFAULT_VISIBLE_HOURS;
  }, [boardPreferenceState.timeAllocationVisibleHoursCount]);

  const [timeAllocationVisibleStartHour, setTimeAllocationVisibleStartHour] = useState(
    KANBAN_TIME_ALLOCATION_DEFAULT_START_HOUR,
  );
  const [resourceScheduleVisibleStartHour, setResourceScheduleVisibleStartHour] = useState(
    clampScheduleStartHour(SERVICE_OPERATIONS_CONFIG.startHour ?? 7),
  );
  const skipNextTimeAllocationAutoAdjustRef = useRef(false);
  const skipNextResourceScheduleAutoAdjustRef = useRef(false);

  useEffect(() => {
    if (skipNextTimeAllocationAutoAdjustRef.current) {
      skipNextTimeAllocationAutoAdjustRef.current = false;
      return;
    }

    if (getDateStrSAST(selectedDate) === getTodayStrSAST()) {
      setTimeAllocationVisibleStartHour(
        alignRollingScheduleVisibleStartToNow(getCurrentHourSAST(), timeAllocationVisibleHoursCount),
      );
    } else {
      setTimeAllocationVisibleStartHour(getDefaultTimeAllocationStartHour());
    }
  }, [getDefaultTimeAllocationStartHour, selectedDate, timeAllocationVisibleHoursCount]);

  const handleTimeAllocationRollHours = useCallback((direction: 'up' | 'down') => {
    setTimeAllocationVisibleStartHour((previous) => {
      const step = 3;
      return direction === 'up'
        ? Math.max(previous - step, KANBAN_VISIBLE_START_HOUR_MIN)
        : Math.min(previous + step, KANBAN_VISIBLE_START_HOUR_MAX);
    });
  }, []);

  const handleResourceScheduleRollHours = useCallback((direction: 'up' | 'down') => {
    setResourceScheduleVisibleStartHour((previous) => {
      const step = 3;
      return direction === 'up'
        ? Math.max(previous - step, KANBAN_VISIBLE_START_HOUR_MIN)
        : Math.min(previous + step, KANBAN_VISIBLE_START_HOUR_MAX);
    });
  }, []);

  const [userLeaveData, setUserLeaveData] = useState<UserLeave[]>([]); // Approved leave for visible users
  const [, setLoadingLeave] = useState(false); // Loading state for leave data
  const skipBoardPreferencePersistRef = useRef(false);
  const skipNextBoardPreferenceStateSyncRef = useRef(false);
  const preserveLoadedHiddenColumnsRef = useRef(false);
  const appliedBoardPreferenceKeyRef = useRef<string | null>(null);
  const isSyncingFromExternalPreferencesRef = useRef(false);
  const skipPersistenceAfterExternalLoadRef = useRef(false);
  const previousStoredHiddenColumnKeysRef = useRef<string[] | undefined>(undefined);
  const previousAllowedColumnKeysRef = useRef<string[] | undefined>(undefined);
  const visibleColumnsChangedByUserRef = useRef(false);
  const visibleColumnsChangedBySyncRef = useRef(false);
  const visibleColumnsRef = useRef(visibleColumns);
  visibleColumnsRef.current = visibleColumns;

  // Auto-adjust zoom level on mobile
  useEffect(() => {
    if (isMobile) {
      setZoomLevel('mobile');
    } else if (isSmallScreen) {
      setZoomLevel('compact');
    }
  }, [isMobile, isSmallScreen]);

  // Function to check if a time slot is in the past (for greying out and blocking drops)
  // This is in the main component scope so it can be used in handleDragEnd
  // Supports dayOffset for rolling hour windows that cross midnight
  const isTimeSlotInPast = useCallback((hour: number, dayOffset: number = 0): boolean => {
    // Calculate the actual date for this time slot considering dayOffset
    const slotDate = new Date(selectedDate);
    slotDate.setDate(slotDate.getDate() + dayOffset);
    const slotDateStr = getDateStrSAST(slotDate);
    const todayStr = getTodayStrSAST();

    // If slot date is before today, ALL hours are in the past
    if (slotDateStr < todayStr) {
      return true;
    }

    // If slot date is after today, NO hours are in the past
    if (slotDateStr > todayStr) {
      return false;
    }

    // Slot date is today - check if hour is in the past
    const currentHour = getCurrentHourSAST();
    return hour <= currentHour;
  }, [selectedDate]);

  // Ref to track actual mouse position during drag (more accurate than dnd-kit's translated rect)
  const mousePositionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  // Track if we're currently dragging (to avoid state updates causing re-renders)
  const isDraggingRef = useRef(false);
  // Cache of DOM elements captured at drag-start so mousemove handlers never
  // call querySelectorAll (which walks the entire DOM) on every pointer event.
  const dragDomCacheRef = useRef<{
    unscheduledAreas: Element[];
    timelineAreas: Element[];
  } | null>(null);
  // Ref to store current view mode for use in event handlers without causing re-renders
  const viewModeRef = useRef(viewMode);
  viewModeRef.current = viewMode;
  // Ref to store the hovered hour/column for use in handleDragEnd (more reliable than state)
  const hoveredHourRef = useRef<{ columnKey: string; hour: number } | null>(null);
  // Ref to track last dispatched hour update (to avoid redundant state updates)
  const lastDispatchedHourRef = useRef<{ columnKey: string; hour: number } | null>(null);

  // Constants for hour calculation (defined here so they can be used in the effect)
  // Use rolling hour values - these will be used by the mouse position calculation
  const visibleStartHourRef = useRef(timeAllocationVisibleStartHour);
  visibleStartHourRef.current = timeAllocationVisibleStartHour;
  const visibleHoursCountRef = useRef(timeAllocationVisibleHoursCount);
  visibleHoursCountRef.current = timeAllocationVisibleHoursCount;

  // Track mouse position globally AND calculate hovered hour during drag
  // Note: This effect re-runs when zoomScale changes to use the correct scale
  useEffect(() => {
    const calculateHourFromPosition = (mouseY: number, mouseX: number) => {
      if (!isDraggingRef.current || viewModeRef.current !== 'time_allocation') {
        return;
      }

      // FIRST: Check if mouse is over an UNSCHEDULED area (takes priority)
      // If so, we should NOT set any hour - this prevents clash detection for unscheduled drops
      const unscheduledAreas = dragDomCacheRef.current?.unscheduledAreas ?? [];
      for (const area of unscheduledAreas) {
        const rect = area.getBoundingClientRect();
        if (mouseX >= rect.left && mouseX <= rect.right && mouseY >= rect.top && mouseY <= rect.bottom) {
          // Mouse is over unscheduled area - clear hour and return
          hoveredHourRef.current = null;
          // Only dispatch if the value actually changed
          if (lastDispatchedHourRef.current !== null) {
            lastDispatchedHourRef.current = null;
            window.dispatchEvent(new CustomEvent('kanban-hour-update', { detail: null }));
          }
          return;
        }
      }

      // Find ALL timeline areas and check which one the mouse is over
      const timelineAreas = dragDomCacheRef.current?.timelineAreas ?? [];
      let foundColumn: string | null = null;
      let foundHour: number | null = null;

      for (const area of timelineAreas) {
        const rect = area.getBoundingClientRect();
        const columnKey = area.getAttribute('data-timeline-area');

        // Check if mouse X is within this column's bounds
        if (mouseX >= rect.left && mouseX <= rect.right) {
          // Check if mouse Y is within the timeline area (not above it in unscheduled section)
          if (mouseY >= rect.top && mouseY <= rect.bottom) {
            foundColumn = columnKey;

            // CRITICAL: The timeline area's visual height is affected by CSS zoom
            // getBoundingClientRect() returns the VISUAL size (affected by zoom)
            // So we need to calculate based on the visual height per hour
            // Visual height of timeline = rect.height (already scaled by zoom)
            // Number of hours displayed uses the rolling hour window
            const startHourValue = visibleStartHourRef.current;
            const numHours = visibleHoursCountRef.current;
            const endHourValue = startHourValue + numHours - 1;
            const visualHourHeight = rect.height / numHours;

            // Calculate hour from Y position relative to this timeline area
            const relativeY = mouseY - rect.top;
            const hourOffset = Math.floor(relativeY / visualHourHeight);
            foundHour = Math.min(Math.max(startHourValue + hourOffset, startHourValue), endHourValue);
            break;
          }
        }
      }

      // Update ref immediately (for handleDragEnd to use)
      if (foundColumn && foundHour !== null) {
        hoveredHourRef.current = { columnKey: foundColumn, hour: foundHour };
      } else {
        hoveredHourRef.current = null;
      }

      // Update state for UI rendering via custom event - ONLY if the value changed
      // This prevents excessive re-renders during drag
      if (foundColumn && foundHour !== null) {
        const lastValue = lastDispatchedHourRef.current;
        if (!lastValue || lastValue.columnKey !== foundColumn || lastValue.hour !== foundHour) {
          lastDispatchedHourRef.current = { columnKey: foundColumn, hour: foundHour };
          window.dispatchEvent(new CustomEvent('kanban-hour-update', {
            detail: { columnKey: foundColumn, hour: foundHour }
          }));
        }
      } else {
        if (lastDispatchedHourRef.current !== null) {
          lastDispatchedHourRef.current = null;
          window.dispatchEvent(new CustomEvent('kanban-hour-update', {
            detail: null
          }));
        }
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      mousePositionRef.current = { x: e.clientX, y: e.clientY };
      // Calculate hour position on every mouse move during drag
      calculateHourFromPosition(e.clientY, e.clientX);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        const touch = e.touches[0];
        mousePositionRef.current = { x: touch.clientX, y: touch.clientY };
        calculateHourFromPosition(touch.clientY, touch.clientX);
      }
    };

    // ALWAYS track mouse position
    window.addEventListener('mousemove', handleMouseMove, { capture: true, passive: true });
    window.addEventListener('touchmove', handleTouchMove, { capture: true, passive: true });
    return () => {
      window.removeEventListener('mousemove', handleMouseMove, { capture: true });
      window.removeEventListener('touchmove', handleTouchMove, { capture: true });
    };
  }, []); // Empty deps - zoomScale is handled by using rect.height directly

  // Listen for hour update events and update state
  useEffect(() => {
    const handleHourUpdate = (e: CustomEvent) => {
      const detail = e.detail;
      if (detail) {
        setHoveredHour({ columnKey: detail.columnKey, hour: detail.hour });
        setDropHour(detail.hour);
      } else {
        setHoveredHour(null);
        setDropHour(null);
      }
    };

    window.addEventListener('kanban-hour-update', handleHourUpdate as EventListener);
    return () => {
      window.removeEventListener('kanban-hour-update', handleHourUpdate as EventListener);
    };
  }, []);

  // Refs for syncing scrollbars
  const topScrollRef = useRef<HTMLDivElement>(null);
  const bottomScrollRef = useRef<HTMLDivElement>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);

  // Ctrl+drag to scroll state
  const [isCtrlDragging, setIsCtrlDragging] = useState(false);
  const ctrlDragStart = useRef<{ x: number; scrollLeft: number } | null>(null);

  // Sync scrollbars
  const handleTopScroll = () => {
    if (topScrollRef.current && bottomScrollRef.current) {
      bottomScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
  };

  const handleBottomScroll = () => {
    if (topScrollRef.current && bottomScrollRef.current) {
      topScrollRef.current.scrollLeft = bottomScrollRef.current.scrollLeft;
    }
    // Mobile column index update is handled separately via effect
  };

  // Ctrl+click+drag to scroll
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.ctrlKey && bottomScrollRef.current) {
      e.preventDefault();
      setIsCtrlDragging(true);
      ctrlDragStart.current = {
        x: e.clientX,
        scrollLeft: bottomScrollRef.current.scrollLeft
      };
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isCtrlDragging && ctrlDragStart.current && bottomScrollRef.current) {
      e.preventDefault();
      const dx = e.clientX - ctrlDragStart.current.x;
      bottomScrollRef.current.scrollLeft = ctrlDragStart.current.scrollLeft - dx;
    }
  };

  const handleMouseUp = useCallback(() => {
    if (isCtrlDragging) {
      setIsCtrlDragging(false);
      ctrlDragStart.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
  }, [isCtrlDragging]);

  // Global mouse up listener for ctrl+drag
  useEffect(() => {
    const handleGlobalMouseUp = () => handleMouseUp();
    if (isCtrlDragging) {
      document.addEventListener('mouseup', handleGlobalMouseUp);
      return () => document.removeEventListener('mouseup', handleGlobalMouseUp);
    }
  }, [isCtrlDragging, handleMouseUp]);

  // Reset columns when viewMode changes (status/assignment have separate column configs)
  useEffect(() => {
    if (viewMode !== lastViewMode) {
      setColumnsInitialized(false);
      setPersistedColumns(null);
      setColumnOrder([]); // Reset column order on view mode change
      setLastViewMode(viewMode);
    }
  }, [viewMode, lastViewMode]);

  // Helper to get auth token
  const getAuthToken = useCallback(() => {
    try {
      const tokens = authService.getTokens();
      return tokens?.access_token || null;
    } catch {
      return null;
    }
  }, []);

  const getPreferenceRequestHeaders = useCallback(() => {
    const token = getAuthToken();
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }, [getAuthToken]);

  useEffect(() => {
    let isCancelled = false;

    const loadBoardPreferences = async () => {
      setBoardPreferencesLoaded(false);
      appliedBoardPreferenceKeyRef.current = null;

      try {
        const response = await fetch('/api/v1/preferences', {
          headers: getPreferenceRequestHeaders(),
        });

        if (!response.ok) {
          throw new Error(`Failed to load preferences (${response.status})`);
        }

        const data = await response.json();
        const preferences = (data?.preferences && typeof data.preferences === 'object')
          ? data.preferences as Record<string, unknown>
          : {};
        const legacyBoardPreferenceKey = `work-items-view:kanban-board:${boardKey}`;
        const storedBoardPreference = preferences[boardPreferenceKey] === undefined && legacyBoardPreferenceKey !== boardPreferenceKey
          ? preferences[legacyBoardPreferenceKey]
          : preferences[boardPreferenceKey];
        const normalizedPreferences = normalizeKanbanBoardPreferenceState(storedBoardPreference);
        if (!isCancelled) {
          // Mark that this preference load is from external source (server)
          // to prevent the sync effect from causing a render loop
          isSyncingFromExternalPreferencesRef.current = true;
          skipPersistenceAfterExternalLoadRef.current = true;
          skipBoardPreferencePersistRef.current = true;
          preserveLoadedHiddenColumnsRef.current = true;
          setBoardPreferenceState(normalizedPreferences);
          setBoardPreferencesLoaded(true);
        }
      } catch (error) {
        console.error('[KanbanBoard] Failed to load preferences:', error);
        if (!isCancelled) {
          skipBoardPreferencePersistRef.current = true;
          setBoardPreferenceState({
            version: KANBAN_BOARD_PREFERENCE_VERSION,
          });
          setBoardPreferencesLoaded(true);
        }
      }
    };

    void loadBoardPreferences();

    return () => {
      isCancelled = true;
    };
  }, [boardKey, boardPreferenceKey, getPreferenceRequestHeaders, preferenceReloadKey]);

  useEffect(() => {
    if (!boardPreferencesLoaded) {
      return;
    }

    if (skipBoardPreferencePersistRef.current) {
      skipBoardPreferencePersistRef.current = false;
      return;
    }

    // Skip persistence for 2 seconds after preferenceReloadKey changes
    // This prevents the board from overwriting preferences that were just saved by the modal
    const timeSinceReload = Date.now() - preferenceReloadTimestampRef.current;
    if (timeSinceReload < 2000) {
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        // Don't persist if we're in an incomplete state (e.g., during initial load)
        // This prevents overwriting valid preferences with empty state
        const statusHiddenKeys = boardPreferenceState.statusHiddenColumnKeys;
        const assignmentHiddenKeys = boardPreferenceState.assignmentHiddenColumnKeys;
        const timeAllocationHiddenKeys = boardPreferenceState.timeAllocationHiddenColumnKeys;
        const resourceScheduleHiddenKeys = boardPreferenceState.resourceScheduleHiddenColumnKeys;

        const isEmptyState = (statusHiddenKeys === undefined || (Array.isArray(statusHiddenKeys) && statusHiddenKeys.length === 0)) &&
          (assignmentHiddenKeys === undefined || (Array.isArray(assignmentHiddenKeys) && assignmentHiddenKeys.length === 0)) &&
          (timeAllocationHiddenKeys === undefined || (Array.isArray(timeAllocationHiddenKeys) && timeAllocationHiddenKeys.length === 0)) &&
          (resourceScheduleHiddenKeys === undefined || (Array.isArray(resourceScheduleHiddenKeys) && resourceScheduleHiddenKeys.length === 0));

        if (isEmptyState) {
          return;
        }

        await fetch('/api/v1/preferences', {
          method: 'PATCH',
          headers: getPreferenceRequestHeaders(),
          body: JSON.stringify({
            key: boardPreferenceKey,
            value: boardPreferenceState,
          }),
        }).then((response) => {
          if (!response.ok) {
            console.error('[WorkItemsKanbanBoard] Failed to persist preferences', response.status);
          }
        });
      } catch (err) {
        console.error('[WorkItemsKanbanBoard] Failed to persist preferences', err);
      }
    }, 500); // Debounce to avoid excessive API calls

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [boardPreferenceKey, boardPreferenceState, boardPreferencesLoaded, getPreferenceRequestHeaders]);

  const persistColumnOrder = useCallback(async (order: string[]) => {
    const nextOrderSignature = order.join('|');

    setColumnOrder((previous) => {
      if (previous.join('|') === nextOrderSignature) {
        return previous;
      }

      return order;
    });

    if (viewMode === 'status' || !boardKey || order.length === 0) {
      return;
    }

    try {
      const token = getAuthToken();
      if (!token) {
        return;
      }

      await fetch('/api/v1/work-items/boards/columns', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'reorder',
          board_key: boardKey,
          view_mode: viewMode,
          columns: order.map((column_key, position) => ({ column_key, position })),
        })
      });
    } catch (error) {
      console.error('[KanbanBoard] Failed to persist column order:', error);
    }
  }, [boardKey, getAuthToken, viewMode]);

  // Resolved IAM Action-matrix permissions (`operations_action_permissions`) for
  // gating appointment creation from job drag/drop — same source as the Actions
  // tab in PermissionsSlideOver.
  const { settings: actionPermissionSettings, role: actionPermissionRole } = useWorkflowActionPermissions();

  const canCreateAppointmentForItem = useCallback((item: WorkItem | null | undefined): boolean => {
    // `appointment.create` resolves against the job's own workflow slice
    // (module_context on the job, e.g. 'service-operations').
    const workflowKey = typeof item?.module_context === 'string' && item.module_context.trim().length > 0
      ? item.module_context.trim()
      : null;
    return isWorkflowActionPermitted({ requires_permission: 'appointment.create' }, actionPermissionSettings, actionPermissionRole, workflowKey);
  }, [actionPermissionRole, actionPermissionSettings]);

  const openAppointmentSetupModal = useCallback((nextSetup: PendingAppointmentSetup) => {
    // `appointment.create` for the job's workflow slice governs whether the
    // drop may open AppointmentDropSetupModal at all.
    if (!canCreateAppointmentForItem(nextSetup.activeItem)) {
      notify.error('You do not have permission to create appointments.');
    } else {
      setPendingAppointmentSetup(nextSetup);
    }
    setActiveId(null);
    setDropHour(null);
    setHoveredHour(null);
    hoveredHourRef.current = null;
    isDraggingRef.current = false;
    dragDomCacheRef.current = null;
  }, [canCreateAppointmentForItem]);

  // Helper function to check for appointment clashes (used in drag-drop and resize)
  const checkForClashes = useCallback((
    userId: number,
    newStart: Date,
    newEnd: Date,
    excludeItemId?: string,
  ): WorkItem[] => {
    // Use optimistic items if available, otherwise real items
    const currentItems = optimisticItems.length > 0 ? optimisticItems : items;
    const sourceItem = excludeItemId
      ? currentItems.find(item => item.id === excludeItemId) || items.find(item => item.id === excludeItemId) || null
      : null;

    // Find all appointments for this user on this day
    const userAppointments = currentItems.filter(item => {
      if (item.item_type !== 'appointment') return false;
      if (excludeItemId && item.id === excludeItemId) return false;
      if (!item.scheduled_start || !item.scheduled_end) return false;

      // Check if assigned to this user
      const itemUserIds = getItemAssignedUserIds(item);
      if (!itemUserIds.includes(userId)) return false;

      // Check if on the same day
      const itemStart = new Date(item.scheduled_start);
      if (!isSameDay(itemStart, selectedDate)) return false;

      return true;
    });

    // Check for overlaps
    const clashingAppointments: WorkItem[] = [];
    for (const apt of userAppointments) {
      const aptStart = new Date(apt.scheduled_start!);
      const aptEnd = new Date(apt.scheduled_end!);

      if (sourceItem && appointmentsAllowIntentionalOverlap(sourceItem, apt)) {
        continue;
      }

      // Check if times overlap (not just touch)
      // Overlap exists if: newStart < aptEnd AND newEnd > aptStart
      if (newStart < aptEnd && newEnd > aptStart) {
        clashingAppointments.push(apt);
      }
    }

    return clashingAppointments;
  }, [getItemAssignedUserIds, items, optimisticItems, selectedDate]);

  const [appointmentAssigneesItemId, setAppointmentAssigneesItemId] = useState<string | null>(null);

  const appointmentAssigneesSourceItems = useMemo(
    () => (optimisticItems.length > 0 ? optimisticItems : items),
    [items, optimisticItems],
  );

  const appointmentAssigneesItem = useMemo(() => {
    if (!appointmentAssigneesItemId) return null;
    return appointmentAssigneesSourceItems.find((i) => i.id === appointmentAssigneesItemId) ?? null;
  }, [appointmentAssigneesItemId, appointmentAssigneesSourceItems]);

  const appointmentAssigneesCurrentUserIds = useMemo(
    () => (appointmentAssigneesItem ? getItemAssignedUserIds(appointmentAssigneesItem) : []),
    [appointmentAssigneesItem, getItemAssignedUserIds],
  );

  const appointmentAssigneesCurrentContractorIds = useMemo(
    () => {
      if (!appointmentAssigneesItem?.assignees) return [];
      return appointmentAssigneesItem.assignees
        .filter((a) => a.assignee_type === 'contractor' && a.contact_id != null)
        .map((a) => a.contact_id!) as number[];
    },
    [appointmentAssigneesItem],
  );

  const appointmentAssigneesAvailableUsers = useMemo(
    () => {
      const usersById = new Map<number, { id: number; name: string; email?: string | null; assignee_type?: 'user' | 'contractor' }>();

      [...appointmentAssigneeUsers, ...assignedUsers]
        .filter((user) => typeof user.id === 'number' && Number.isFinite(user.id))
        .forEach((user) => {
          usersById.set(user.id, {
            id: user.id,
            name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || `User ${user.id}`,
            email: user.email,
          });
        });

      if (appointmentAssigneesItem?.assignees) {
        appointmentAssigneesItem.assignees.forEach((assignee) => {
          if (assignee.assignee_type === 'contractor' && assignee.contact_id != null) {
            const contractorId = -assignee.contact_id;
            if (usersById.has(contractorId)) return;
            usersById.set(contractorId, {
              id: contractorId,
              name: assignee.company_name || assignee.contact_person || `Contractor #${assignee.contact_id}`,
              email: assignee.email || assignee.contact_email || null,
              assignee_type: 'contractor',
            });
            return;
          }
          const userId = Number(assignee.user_id);
          if (!Number.isFinite(userId) || userId <= 0 || usersById.has(userId)) {
            return;
          }

          usersById.set(userId, {
            id: userId,
            name: `${assignee.first_name || ''} ${assignee.last_name || ''}`.trim() || assignee.email || `User ${userId}`,
            email: assignee.email,
          });
        });
      }

      // Add available contractors (not yet assigned)
      availableContractors.forEach((contractor) => {
        const contractorId = -contractor.id;
        if (usersById.has(contractorId)) return;
        usersById.set(contractorId, {
          id: contractorId,
          name: contractor.company_name || contractor.contact_person || `Contractor #${contractor.id}`,
          email: contractor.email || null,
          assignee_type: 'contractor',
        });
      });

      appointmentAssigneesCurrentUserIds.forEach((userId) => {
        if (usersById.has(userId)) {
          return;
        }

        usersById.set(userId, {
          id: userId,
          name: `User ${userId}`,
          email: null,
        });
      });

      return Array.from(usersById.values()).sort((left, right) => left.name.localeCompare(right.name));
    },
    [appointmentAssigneeUsers, appointmentAssigneesCurrentUserIds, appointmentAssigneesItem, assignedUsers, availableContractors],
  );

  const handleAssigneesModalBeforeSave = useCallback(
    async ({ newlyAddedUserIds }: { newlyAddedUserIds: number[]; nextAssigneeUserIds: number[] }) => {
      const appt = appointmentAssigneesItem;
      if (!appt?.scheduled_start || !appt?.scheduled_end) {
        return true;
      }
      const newStart = new Date(appt.scheduled_start);
      const newEnd = new Date(appt.scheduled_end);
      const allClashes: { userName: string; conflicts: WorkItem[] }[] = [];

      for (const targetUserId of newlyAddedUserIds) {
        const conflicts = checkForClashes(targetUserId, newStart, newEnd, appt.id);
        if (conflicts.length > 0) {
          const user = assignedUsers.find((u) => u.id === targetUserId);
          const userName = user
            ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email
            : `User ${targetUserId}`;
          allClashes.push({ userName, conflicts });
        }
      }

      if (allClashes.length === 0) {
        return true;
      }

      const clashDetails = allClashes
        .map((gc) => {
          const clashList = gc.conflicts
            .map(
              (clash) =>
                `  • ${clash.item_number || clash.title}: ${formatTimeSAST(clash.scheduled_start!)} - ${formatTimeSAST(clash.scheduled_end!)}`,
            )
            .join('\n');
          return `${gc.userName}:\n${clashList}`;
        })
        .join('\n\n');

      return confirm({
        title: 'Schedule overlap',
        message: `Adding these people may overlap with other work on their calendars:\n\n${clashDetails}\n\nContinue anyway?`,
        variant: 'warning',
        confirmText: 'Add anyway',
        cancelText: 'Cancel',
        onConfirm: () => { },
      });
    },
    [appointmentAssigneesItem, assignedUsers, checkForClashes],
  );

  // Get client branding for themed modals
  const { clientBranding } = useTheme();

  // NEW: Mouse wheel zoom controls (November 2025)
  const {
    zoomScale,
    zoomLabel,
    setZoom,
    zoomIn,
    zoomOut,
    resetZoom,
    canZoomIn,
    canZoomOut,
    containerRef
  } = useKanbanZoom({
    minZoom: 0.5,
    maxZoom: 1.5,
    zoomStep: 0.1
  });

  useEffect(() => {
    if (!boardPreferencesLoaded || defaultBoardViewMode) {
      return;
    }

    const preferredViewMode = currentUserId
      ? 'status'
      : boardPreferenceState.selectedViewMode;

    if (!preferredViewMode) {
      return;
    }

    const resolvedViewMode = !enableTimeAllocation && (preferredViewMode === 'time_allocation' || preferredViewMode === 'resource_schedule')
      ? 'status'
      : preferredViewMode;

    if (resolvedViewMode !== viewMode) {
      setViewMode(resolvedViewMode);
    }
  }, [boardPreferenceState.selectedViewMode, boardPreferencesLoaded, currentUserId, defaultBoardViewMode, enableTimeAllocation, viewMode]);

  useEffect(() => {
    if (!boardPreferencesLoaded || appliedBoardPreferenceKeyRef.current === boardPreferenceKey) {
      return;
    }

    appliedBoardPreferenceKeyRef.current = boardPreferenceKey;

    // Intentionally do not restore selectedDateISO or visible-start hours — schedule defaults to today + current window (February 2026).

    if (typeof boardPreferenceState.resourceScheduleSidebarVisible === 'boolean') {
      setResourceScheduleSidebarVisible((previous) => (
        previous === boardPreferenceState.resourceScheduleSidebarVisible
          ? previous
          : boardPreferenceState.resourceScheduleSidebarVisible!
      ));
    }

    if (typeof boardPreferenceState.resourceScheduleTimelineZoom === 'number') {
      setResourceScheduleTimelineZoom((previous) => (
        previous === boardPreferenceState.resourceScheduleTimelineZoom
          ? previous
          : boardPreferenceState.resourceScheduleTimelineZoom!
      ));
    }

    if (typeof boardPreferenceState.zoomScale === 'number') {
      setZoom((previous) => (
        previous === boardPreferenceState.zoomScale ? previous : boardPreferenceState.zoomScale!
      ));
    }
  }, [boardPreferenceKey, boardPreferenceState, boardPreferencesLoaded, setZoom]);

  // Ref for ResourceScheduleTimeline zoom control
  const timelineRef = useRef<ResourceScheduleTimelineRef>(null);
  // Force re-render when timeline zoom changes so toolbar label updates
  const [timelineZoomLabel, setTimelineZoomLabel] = useState('100%');
  const [timelineCanZoomIn, setTimelineCanZoomIn] = useState(true);
  const [timelineCanZoomOut, setTimelineCanZoomOut] = useState(true);

  // Poll timeline ref for zoom state (lightweight, runs on every render when in resource_schedule)
  useEffect(() => {
    if (viewMode !== 'resource_schedule') return;
    const interval = setInterval(() => {
      const tr = timelineRef.current;
      if (tr) {
        setTimelineZoomLabel((previous) => (previous === tr.zoomLabel ? previous : tr.zoomLabel));
        setTimelineCanZoomIn((previous) => (previous === tr.canZoomIn ? previous : tr.canZoomIn));
        setTimelineCanZoomOut((previous) => (previous === tr.canZoomOut ? previous : tr.canZoomOut));
      }
    }, 200);
    return () => clearInterval(interval);
  }, [viewMode]);

  // Enforce status view in personal mode
  useEffect(() => {
    // In personal view (when currentUserId is provided), always force status view
    if (currentUserId) {
      setViewMode('status');
    }
  }, [currentUserId]);

  // Prevent viewMode from changing to assignment, time_allocation, or resource_schedule when in personal view
  useEffect(() => {
    if (currentUserId && (viewMode === 'assignment' || viewMode === 'time_allocation' || viewMode === 'resource_schedule')) {
      setViewMode('status');
    }
  }, [viewMode, currentUserId]);

  // Load persisted column configuration
  useEffect(() => {
    const loadPersistedColumns = async () => {
      if (!boardKey || columnsInitialized) return;

      if (viewMode !== 'status') {
        setColumnsInitialized(true);
        return;
      }

      try {
        const token = getAuthToken();
        if (!token) return;

        // Include viewMode in the query to get the right column configuration
        const response = await fetch(`/api/v1/work-items/boards/columns?board_key=${encodeURIComponent(boardKey)}&item_type=${itemType}&view_mode=${viewMode}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json() as BoardColumnsResponse;
          if (data.success && data.data && data.data.length > 0) {
            // Convert persisted columns to WorkflowState format
            const loadedColumns: WorkflowState[] = data.data.map((col) => ({
              key: col.column_key,
              label: col.column_label,
              color: col.color || 'gray',
              description: col.description ?? undefined
            }));
            setPersistedColumns(loadedColumns);

            // Set visibility from persisted data
            const visibleSet = new Set<string>(
              data.data.filter((column) => !column.is_hidden).map((column) => column.column_key)
            );
            const hiddenSet = new Set<string>(
              data.data.filter((column) => column.is_hidden).map((column) => column.column_key)
            );
            setVisibleColumns(visibleSet.size > 0 ? visibleSet : new Set(data.data.map((column) => column.column_key)));
            setColumnOrder(data.data.map((column) => column.column_key));
          }
        }
      } catch (error) {
        console.error('[KanbanBoard] Error loading persisted columns:', error);
      } finally {
        setColumnsInitialized(true);
      }
    };

    loadPersistedColumns();
  }, [boardKey, columnsInitialized, getAuthToken, itemType, viewMode]);

  // Use persisted columns, then workflowStates from props, then DEFAULT_COLUMNS
  // IMPORTANT: If useStrictWorkflowStates is true, ONLY use workflowStates (for workspaces)
  // NEW: Dynamically add columns for any item statuses that don't have columns (prevents items from disappearing)
  const columns = useMemo(() => {
    const insertColumnByPreferredOrder = (target: WorkflowState[], column: WorkflowState) => {
      const defaultIndex = DEFAULT_COLUMNS.findIndex((candidate) => candidate.key === column.key);
      if (defaultIndex === -1) {
        target.push(column);
        return;
      }

      let insertIndex = -1;

      for (let index = defaultIndex - 1; index >= 0; index -= 1) {
        const previousKey = DEFAULT_COLUMNS[index].key;
        const existingIndex = target.findIndex((candidate) => candidate.key === previousKey);
        if (existingIndex !== -1) {
          insertIndex = existingIndex + 1;
          break;
        }
      }

      if (insertIndex === -1) {
        for (let index = defaultIndex + 1; index < DEFAULT_COLUMNS.length; index += 1) {
          const nextKey = DEFAULT_COLUMNS[index].key;
          const existingIndex = target.findIndex((candidate) => candidate.key === nextKey);
          if (existingIndex !== -1) {
            insertIndex = existingIndex;
            break;
          }
        }
      }

      if (insertIndex === -1) {
        insertIndex = 0;
      }

      target.splice(insertIndex, 0, column);
    };

    // Collect all unique statuses from items
    const itemStatuses = new Set<string>();
    items.forEach(item => {
      const status = getWorkflowValue(item, workflowField);
      if (status) {
        itemStatuses.add(status);
      }
    });

    // For workspaces with strict workflow states, ONLY use the provided workflowStates
    // but still add dynamic columns for any statuses that items have
    if (useStrictWorkflowStates && workflowStates && workflowStates.length > 0) {
      const result = [...workflowStates];
      const existingKeys = new Set(result.map(c => c.key));

      // Add dynamic columns for statuses that items have but don't have columns
      itemStatuses.forEach(status => {
        if (!existingKeys.has(status)) {
          // Try to find this status in DEFAULT_COLUMNS for proper styling
          const defaultCol = DEFAULT_COLUMNS.find(c => c.key === status);
          insertColumnByPreferredOrder(result, {
            key: status,
            label: defaultCol?.label || status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
            color: defaultCol?.color || 'gray',
            description: defaultCol?.description || `Items with status: ${status}`,
            isSystem: defaultCol?.isSystem ?? isSystemManagedWorkflowStatusKey(status)
          });
          existingKeys.add(status);
        }
      });

      return result;
    }

    let baseColumns: WorkflowState[];

    if (workflowStates && workflowStates.length > 0) {
      baseColumns = [...workflowStates];
    } else if (persistedColumns && persistedColumns.length > 0) {
      baseColumns = [...persistedColumns];
    } else {
      baseColumns = [...DEFAULT_COLUMNS];
    }

    // Merge missing columns from DEFAULT_COLUMNS (e.g., if scheduled was added later)
    const existingKeys = new Set(baseColumns.map(c => c.key));
    DEFAULT_COLUMNS.forEach((defaultColumn) => {
      if (!existingKeys.has(defaultColumn.key)) {
        insertColumnByPreferredOrder(baseColumns, defaultColumn);
        existingKeys.add(defaultColumn.key);
      }
    });

    // Add dynamic columns for any item statuses that don't have columns
    // This ensures items are never hidden due to missing columns (especially in personal view)
    itemStatuses.forEach(status => {
      if (!existingKeys.has(status)) {
        // Try to find this status in DEFAULT_COLUMNS for proper styling
        const defaultCol = DEFAULT_COLUMNS.find(c => c.key === status);
        insertColumnByPreferredOrder(baseColumns, {
          key: status,
          label: defaultCol?.label || status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
          color: defaultCol?.color || 'gray',
          description: defaultCol?.description || `Items with status: ${status}`,
          isSystem: defaultCol?.isSystem ?? isSystemManagedWorkflowStatusKey(status)
        });
        existingKeys.add(status);
      }
    });

    return baseColumns;
  }, [workflowStates, persistedColumns, useStrictWorkflowStates, items, workflowField]);

  // Fetch users with module access (needed for both assignment view and showing names on cards)
  useEffect(() => {
    if (assignedUsersOverride !== undefined) {
      setAssignedUsers((previous) => (
        areAssignedUsersListsEqual(previous, assignedUsersOverride)
          ? previous
          : assignedUsersOverride
      ));
      setLoadingUsers((previous) => (previous ? false : previous));
      return;
    }

    if (!moduleKey) {
      setAssignedUsers((previous) => (previous.length === 0 ? previous : []));
      setLoadingUsers((previous) => (previous ? false : previous));
      return;
    }

    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const getAuthToken = () => {
          try {
            const storedTokens = localStorage.getItem('auth_tokens');
            if (storedTokens) {
              const tokens = JSON.parse(storedTokens);
              return tokens.access_token;
            }
          } catch (error) {
            console.warn('[KanbanBoard] Failed to load auth token:', error);
          }
          return null;
        };

        const token = getAuthToken();
        // Fetch users with access to the specified module (falls back to total-operations if not set)
        const response = await fetch(`/api/v1/module-access/users?module_key=${moduleKey || 'total-operations'}`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setAssignedUsers((previous) => (
              areAssignedUsersListsEqual(previous, data.data)
                ? previous
                : data.data
            ));
          } else {
            setAssignedUsers((previous) => (previous.length === 0 ? previous : []));
          }
        } else {
          setAssignedUsers((previous) => (previous.length === 0 ? previous : []));
        }
      } catch (error) {
        console.error('[KanbanBoard] Error fetching users:', error);
        setAssignedUsers((previous) => (previous.length === 0 ? previous : []));
      } finally {
        setLoadingUsers((previous) => (previous ? false : previous));
      }
    };

    fetchUsers();
  }, [moduleKey, assignedUsersOverride]); // Removed viewMode dependency - always load users

  useEffect(() => {
    let isCancelled = false;

    const fetchAppointmentAssigneeUsers = async () => {
      try {
        const token = getAuthToken();
        const response = await fetch(`/api/v1/module-access/users?module_key=${moduleKey || 'total-operations'}`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          if (!isCancelled) {
            setAppointmentAssigneeUsers((previous) => (previous.length === 0 ? previous : []));
          }
          return;
        }

        const data = await response.json();
        const nextUsers = data.success && Array.isArray(data.data)
          ? data.data as AssignedUser[]
          : [];

        if (!isCancelled) {
          setAppointmentAssigneeUsers((previous) => (
            areAssignedUsersListsEqual(previous, nextUsers)
              ? previous
              : nextUsers
          ));
        }
      } catch (error) {
        console.error('[KanbanBoard] Error fetching appointment assignee users:', error);
        if (!isCancelled) {
          setAppointmentAssigneeUsers((previous) => (previous.length === 0 ? previous : []));
        }
      }
    };

    void fetchAppointmentAssigneeUsers();

    return () => {
      isCancelled = true;
    };
  }, [getAuthToken, moduleKey]);

  // Fetch approved leave data for users in time allocation view
  useEffect(() => {
    // Only fetch when in time allocation view with users loaded
    if (viewMode !== 'time_allocation' || assignedUsers.length === 0) {
      setUserLeaveData([]);
      return;
    }

    const fetchLeaveData = async () => {
      try {
        setLoadingLeave(true);
        const token = getAuthToken();
        if (!token) return;

        // Get user IDs from assignedUsers (excluding null/undefined)
        const userIds = assignedUsers
          .map(u => u.id)
          .filter((id): id is number => id != null);

        if (userIds.length === 0) {
          setUserLeaveData([]);
          return;
        }

        // Format date as YYYY-MM-DD
        const dateStr = format(selectedDate, 'yyyy-MM-dd');

        const response = await fetch('/api/v1/employee-management/actions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            action: 'getApprovedLeaveForSchedule',
            userIds,
            date: dateStr
          })
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setUserLeaveData(data.data);
          }
        }
      } catch (error) {
        console.error('[KanbanBoard] Error fetching leave data:', error);
      } finally {
        setLoadingLeave(false);
      }
    };

    fetchLeaveData();
  }, [assignedUsers, getAuthToken, selectedDate, viewMode]);

  // Create assignment columns based on users
  const assignmentColumns = useMemo<WorkflowState[]>(() => {
    // Time allocation and resource schedule use same columns as assignment
    if (viewMode !== 'assignment' && viewMode !== 'time_allocation' && viewMode !== 'resource_schedule') return [];

    // Filter users if currentUserId is provided (personal view)
    // Also filter out users with null/undefined ids
    const filteredUsers = (currentUserId
      ? assignedUsers.filter(user => user.id === currentUserId)
      : assignedUsers
    ).filter(user => user.id != null);

    const sortedOperationsTeams = [...operationsTeams].sort((left, right) => {
      const nameCompare = (left.team_name || '').localeCompare(right.team_name || '', undefined, { sensitivity: 'base' });
      if (nameCompare !== 0) {
        return nameCompare;
      }

      return String(left.id || '').localeCompare(String(right.id || ''), undefined, { sensitivity: 'base' });
    });

    const sortedUsers = [...filteredUsers].sort((left, right) => {
      const leftName = `${left.first_name || ''} ${left.last_name || ''}`.trim() || left.email || '';
      const rightName = `${right.first_name || ''} ${right.last_name || ''}`.trim() || right.email || '';
      const nameCompare = leftName.localeCompare(rightName, undefined, { sensitivity: 'base' });
      if (nameCompare !== 0) {
        return nameCompare;
      }

      return String(left.id || '').localeCompare(String(right.id || ''), undefined, { sensitivity: 'base' });
    });

    // Start with Unassigned column first for consistency with Status view
    const userColumns: WorkflowState[] = [{
      key: 'unassigned',
      label: 'Unassigned',
      color: 'gray',
      description: 'Items not assigned to anyone'
    }];

    if (includeOperationsTeams) {
      sortedOperationsTeams.forEach((team, index) => {
        const colors = ['teal', 'purple', 'blue', 'green', 'orange', 'red'];
        const colorIndex = index % colors.length;
        const memberCount = team.memberUserIds.length;
        const leaderName = team.leader?.name || 'No leader assigned';

        userColumns.push({
          key: `team-${team.id}`,
          label: team.team_name,
          color: colors[colorIndex],
          description: `${memberCount} member${memberCount === 1 ? '' : 's'} · Lead: ${leaderName}`,
          resourceType: 'team',
          resourceId: team.id,
          teamId: team.id,
          leaderUserId: team.leader?.user_id || null,
          memberUserIds: team.memberUserIds,
        });
      });
    }

    // Add user columns
    sortedUsers.forEach((user, index) => {
      const colors = ['blue', 'purple', 'green', 'orange', 'red', 'yellow'];
      const colorIndex = index % colors.length;
      const userName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Unknown User';
      const isInactiveAssignee = user.isInactive === true || user.role === 'inactive';

      userColumns.push({
        key: `user-${user.id}`,
        label: isInactiveAssignee ? `${userName} (Inactive)` : userName,
        color: colors[colorIndex],
        description: isInactiveAssignee ? 'Previously assigned work is retained here' : user.role || user.email || '',
        resourceType: 'user',
        resourceId: user.id,
        userId: user.id,
      });
    });

    // Add Pending Approval column - items awaiting approval are removed from user columns
    // This keeps the assignment/schedule views clean and focused on active work
    userColumns.push({
      key: 'status-pending_approval',
      label: 'Pending Approval',
      color: 'amber',
      description: 'Items awaiting approval'
    });

    // Add status-based columns for completed/cancelled items
    userColumns.push({
      key: 'status-completed',
      label: 'Completed',
      color: 'emerald',
      description: 'Completed items (all assignees)'
    });

    userColumns.push({
      key: 'status-cancelled',
      label: 'Cancelled',
      color: 'red',
      description: 'Cancelled items (all assignees)'
    });

    return userColumns;
  }, [assignedUsers, currentUserId, includeOperationsTeams, operationsTeams, viewMode]);

  const scheduleColumns = useMemo<WorkflowState[]>(() => {
    if (viewMode === 'resource_schedule') {
      return assignmentColumns.filter((column) => column.key.startsWith('user-') || column.key.startsWith('team-'));
    }

    return assignmentColumns.filter((column) => column.key === 'unassigned' || column.key.startsWith('user-') || column.key.startsWith('team-'));
  }, [assignmentColumns, viewMode]);

  const visibleScheduleColumns = useMemo<WorkflowState[]>(() => {
    return scheduleColumns.filter((column) => visibleColumns.has(column.key));
  }, [scheduleColumns, visibleColumns]);

  const timeAllocationExportResources = useMemo<ScheduleClipboardResource[]>(() => {
    return visibleScheduleColumns
      .filter((column) => column.key.startsWith('user-') || column.key.startsWith('team-'))
      .map((column) => {
        if (column.key.startsWith('user-')) {
          const userId = Number(column.key.replace('user-', ''));
          return {
            id: userId,
            name: column.label,
            userId,
            resourceType: 'user',
          } satisfies ScheduleClipboardResource;
        }

        const teamId = column.key.replace('team-', '');
        const matchingTeam = operationsTeams.find((team) => team.id === teamId);

        return {
          id: teamId,
          name: column.label,
          resourceType: 'team',
          teamId,
          memberUserIds: matchingTeam?.memberUserIds || [],
        } satisfies ScheduleClipboardResource;
      });
  }, [operationsTeams, visibleScheduleColumns]);

  const visibleResourceScheduleColumns = useMemo<WorkflowState[]>(() => {
    return scheduleColumns.filter((column) => (column.key.startsWith('user-') || column.key.startsWith('team-')) && visibleColumns.has(column.key));
  }, [scheduleColumns, visibleColumns]);

  // Get active columns based on view mode
  // Time allocation uses same columns as assignment
  const activeColumns = viewMode === 'status'
    ? columns
    : assignmentColumns;

  const activeColumnsSignature = useMemo(() => JSON.stringify(
    activeColumns.map((column) => ({
      key: column.key,
      label: column.label,
      color: column.color,
      description: column.description,
      isSystem: column.isSystem,
    }))
  ), [activeColumns]);

  const lastExposedColumnsSignatureRef = useRef<string | null>(null);

  // Expose current columns to parent for Board Design Preferences modal
  useEffect(() => {
    if (!onColumnsExposed) {
      lastExposedColumnsSignatureRef.current = null;
      return;
    }

    if (activeColumnsSignature === lastExposedColumnsSignatureRef.current) {
      return;
    }

    lastExposedColumnsSignatureRef.current = activeColumnsSignature;
    onColumnsExposed(activeColumns);
  }, [activeColumns, activeColumnsSignature, onColumnsExposed]);

  // Update timestamp when preferenceReloadKey changes to skip persistence for 2 seconds
  useEffect(() => {
    preferenceReloadTimestampRef.current = Date.now();
  }, [preferenceReloadKey]);

  const columnSettingsColumns = useMemo<WorkflowState[]>(() => {
    if (viewMode === 'time_allocation') {
      return scheduleColumns;
    }

    if (viewMode === 'resource_schedule') {
      return scheduleColumns.filter((column) => column.key.startsWith('user-') || column.key.startsWith('team-'));
    }

    return activeColumns;
  }, [activeColumns, scheduleColumns, viewMode]);
  const allowedColumnKeysForCurrentView = useMemo(
    () => (viewMode === 'status'
      ? columns.map((column) => column.key)
      : columnSettingsColumns.map((column) => column.key)),
    [columnSettingsColumns, columns, viewMode],
  );
  const storedHiddenColumnKeysForCurrentView = useMemo(
    () => {
      const keys = getHiddenColumnKeysForViewMode(boardPreferenceState, viewMode);
      return keys;
    },
    [boardPreferenceState, viewMode],
  );
  const expectedVisibleColumnKeysForCurrentView = useMemo(
    () => getVisibleColumnKeysForKanbanView(
      storedHiddenColumnKeysForCurrentView,
      allowedColumnKeysForCurrentView,
    ),
    [allowedColumnKeysForCurrentView, storedHiddenColumnKeysForCurrentView],
  );
  const hiddenColumnCountForCurrentView = useMemo(
    () => Math.max(0, allowedColumnKeysForCurrentView.length - expectedVisibleColumnKeysForCurrentView.length),
    [allowedColumnKeysForCurrentView.length, expectedVisibleColumnKeysForCurrentView.length],
  );
  const hiddenResultsNotice = useMemo(
    () => getKanbanHiddenResultsNotice(hiddenColumnCountForCurrentView),
    [hiddenColumnCountForCurrentView],
  );
  const isStatusBoardStructureReady = viewMode !== 'status'
    || useStrictWorkflowStates
    || Boolean(workflowStates && workflowStates.length > 0)
    || !boardKey
    || columnsInitialized;
  const areViewPreferencesHydrated = useMemo(() => {
    return areKanbanViewPreferencesHydrated({
      boardPreferencesLoaded,
      isStructureReady: isStatusBoardStructureReady,
      allowedColumnKeys: allowedColumnKeysForCurrentView,
      hiddenColumnKeys: storedHiddenColumnKeysForCurrentView,
      visibleColumns,
      // Skip column-based hydration for resource_schedule since it renders resources directly
      // via ResourceScheduleTimeline, not via Kanban columns
      skipColumnHydration: viewMode === 'resource_schedule',
    });
  }, [
    allowedColumnKeysForCurrentView,
    boardPreferencesLoaded,
    isStatusBoardStructureReady,
    storedHiddenColumnKeysForCurrentView,
    visibleColumns,
    viewMode,
  ]);

  // Sync visible columns from stored hidden column keys
  useEffect(() => {
    if (!boardPreferencesLoaded) {
      return;
    }

    if (allowedColumnKeysForCurrentView.length === 0) {
      return;
    }

    // Skip sync if this change came from external preferences (e.g., modal save)
    // to prevent render loops
    if (skipNextBoardPreferenceStateSyncRef.current) {
      skipNextBoardPreferenceStateSyncRef.current = false;
      return;
    }

    // Skip sync if the hidden column keys haven't actually changed
    const previousKeys = previousStoredHiddenColumnKeysRef.current;
    const keysChanged = !areStringArraysEqual(previousKeys, storedHiddenColumnKeysForCurrentView);
    previousStoredHiddenColumnKeysRef.current = storedHiddenColumnKeysForCurrentView;
    const previousAllowedKeys = previousAllowedColumnKeysRef.current;
    const allowedKeysChanged = !areStringArraysEqual(previousAllowedKeys, allowedColumnKeysForCurrentView);
    previousAllowedColumnKeysRef.current = allowedColumnKeysForCurrentView;

    if (!keysChanged && !allowedKeysChanged) {
      return;
    }

    const nextVisibleColumns = getVisibleColumnSetForKanbanView(
      storedHiddenColumnKeysForCurrentView,
      allowedColumnKeysForCurrentView,
    );

    if (areStringSetsEqual(visibleColumnsRef.current, nextVisibleColumns)) {
      return;
    }

    skipNextBoardPreferenceStateSyncRef.current = true;
    visibleColumnsChangedBySyncRef.current = true;
    setVisibleColumns(nextVisibleColumns);
  }, [
    allowedColumnKeysForCurrentView,
    boardPreferencesLoaded,
    storedHiddenColumnKeysForCurrentView,
  ]);

  useEffect(() => {
    if (!boardPreferencesLoaded || viewMode !== 'status') {
      return;
    }

    const allowedStatusKeys = columns.map((column) => column.key);
    const storedOrder = boardPreferenceState.statusColumnOrder;
    if (!Array.isArray(storedOrder) || storedOrder.length === 0 || allowedStatusKeys.length === 0) {
      return;
    }

    const allowedKeySet = new Set(allowedStatusKeys);
    const sanitizedStoredOrder = [
      ...storedOrder.filter((key) => allowedKeySet.has(key)),
      ...allowedStatusKeys.filter((key) => !storedOrder.includes(key)),
    ];

    setColumnOrder((previous) => (
      previous.join('|') === sanitizedStoredOrder.join('|')
        ? previous
        : (skipNextBoardPreferenceStateSyncRef.current = true, sanitizedStoredOrder)
    ));
  }, [boardPreferenceState.statusColumnOrder, boardPreferencesLoaded, columns, viewMode]);

  useEffect(() => {
    // Skip persistence entirely for non-status modes
    // The sync effect handles updating visibleColumns from stored preferences
    // and the persistence effect would create a render loop by recalculating from visibleColumns
    if (viewMode !== 'status') {
      return;
    }

    if (!boardPreferencesLoaded) {
      return;
    }

    if (skipNextBoardPreferenceStateSyncRef.current) {
      skipNextBoardPreferenceStateSyncRef.current = false;
      return;
    }

    // Skip hidden column recalculation when syncing from external preferences (modal save)
    // to prevent overwriting the server-provided values
    if (isSyncingFromExternalPreferencesRef.current) {
      isSyncingFromExternalPreferencesRef.current = false;
      return;
    }

    // Skip persistence entirely after external preference load to prevent render loop
    if (skipPersistenceAfterExternalLoadRef.current) {
      skipPersistenceAfterExternalLoadRef.current = false;
      return;
    }

    // Skip hidden column recalculation when visibleColumns was changed by sync effect
    // This prevents the render loop: sync → visibleColumns → persistence → boardPreferenceState → sync
    if (visibleColumnsChangedBySyncRef.current) {
      visibleColumnsChangedBySyncRef.current = false;
      return;
    }

    const nextSelectedDateISO = format(selectedDate, 'yyyy-MM-dd');
    const nextSelectedViewMode = currentUserId ? 'status' : viewMode;
    const nextTimeAllocationVisibleStartHour = clampKanbanVisibleStartHour(timeAllocationVisibleStartHour);
    const nextResourceScheduleVisibleStartHour = clampKanbanVisibleStartHour(resourceScheduleVisibleStartHour);
    const nextTimeAllocationDefaultStartHour = getDefaultTimeAllocationStartHour();
    const nextTimeAllocationVisibleHoursCount = timeAllocationVisibleHoursCount;
    const shouldPreserveLoadedHiddenColumns = preserveLoadedHiddenColumnsRef.current;
    if (shouldPreserveLoadedHiddenColumns) {
      preserveLoadedHiddenColumnsRef.current = false;
    }

    setBoardPreferenceState((previous) => {
      const nextState: KanbanBoardPreferenceState = {
        ...previous,
        version: KANBAN_BOARD_PREFERENCE_VERSION,
        selectedDateISO: nextSelectedDateISO,
        timeAllocationVisibleStartHour: nextTimeAllocationVisibleStartHour,
        timeAllocationDefaultStartHour: nextTimeAllocationDefaultStartHour,
        timeAllocationVisibleHoursCount: nextTimeAllocationVisibleHoursCount,
        resourceScheduleSidebarVisible,
        zoomScale,
        resourceScheduleTimelineZoom,
        resourceScheduleVisibleStartHour: nextResourceScheduleVisibleStartHour,
        statusHiddenColumnKeys: previous.statusHiddenColumnKeys,
        assignmentHiddenColumnKeys: previous.assignmentHiddenColumnKeys,
        timeAllocationHiddenColumnKeys: previous.timeAllocationHiddenColumnKeys,
        resourceScheduleHiddenColumnKeys: previous.resourceScheduleHiddenColumnKeys,
        statusColumnOrder: previous.statusColumnOrder,
        resourceScheduleStartHour: clampScheduleStartHour(SERVICE_OPERATIONS_CONFIG.startHour ?? 7),
        resourceScheduleEndHour: clampScheduleEndHour(
          SERVICE_OPERATIONS_CONFIG.endHour ?? 18,
          clampScheduleStartHour(SERVICE_OPERATIONS_CONFIG.startHour ?? 7),
        ),
        resourceScheduleIntervalMinutes: normalizeScheduleIntervalMinutes(SERVICE_OPERATIONS_CONFIG.intervalMinutes) ?? 30,
      };

      if (previous.resourceScheduleStartHour !== undefined) {
        nextState.resourceScheduleStartHour = previous.resourceScheduleStartHour;
      }

      if (previous.resourceScheduleEndHour !== undefined) {
        nextState.resourceScheduleEndHour = previous.resourceScheduleEndHour;
      }

      if (previous.resourceScheduleIntervalMinutes !== undefined) {
        nextState.resourceScheduleIntervalMinutes = previous.resourceScheduleIntervalMinutes;
      }

      if (previous.timeAllocationDefaultStartHour !== undefined) {
        nextState.timeAllocationDefaultStartHour = previous.timeAllocationDefaultStartHour;
      }

      if (previous.timeAllocationVisibleHoursCount !== undefined) {
        nextState.timeAllocationVisibleHoursCount = previous.timeAllocationVisibleHoursCount;
      }

      if (previous.resourceScheduleVisibleStartHour !== undefined) {
        nextState.resourceScheduleVisibleStartHour = previous.resourceScheduleVisibleStartHour;
      }

      if (!defaultBoardViewMode) {
        nextState.selectedViewMode = nextSelectedViewMode;
      }

      if (viewMode === 'status') {
        if (!shouldPreserveLoadedHiddenColumns) {
          nextState.statusHiddenColumnKeys = getHiddenColumnKeysFromVisibleColumns(
            Array.from(visibleColumns),
            columns.map((column) => column.key),
            columns.map((column) => column.key),
          );
        }
        nextState.statusColumnOrder = columnOrder.length > 0
          ? columnOrder.filter((key, index, values) => values.indexOf(key) === index && columns.some((column) => column.key === key))
          : columns.map((column) => column.key);
      } else {
        // For assignment/time_allocation/resource_schedule modes, never recalculate hidden column keys
        // from visibleColumns in the persistence effect. The sync effect handles updating visibleColumns
        // from stored preferences, and recalculating here creates a render loop.
        // Hidden column keys for these modes are only updated by the modal or when explicitly toggled in UI.
        nextState.assignmentHiddenColumnKeys = previous.assignmentHiddenColumnKeys;
        nextState.timeAllocationHiddenColumnKeys = previous.timeAllocationHiddenColumnKeys;
        nextState.resourceScheduleHiddenColumnKeys = previous.resourceScheduleHiddenColumnKeys;
      }

      const selectedViewModeUnchanged = defaultBoardViewMode
        ? true
        : previous.selectedViewMode === nextState.selectedViewMode;

      if (
        selectedViewModeUnchanged
        && previous.selectedDateISO === nextState.selectedDateISO
        && previous.timeAllocationVisibleStartHour === nextState.timeAllocationVisibleStartHour
        && previous.timeAllocationDefaultStartHour === nextState.timeAllocationDefaultStartHour
        && previous.timeAllocationVisibleHoursCount === nextState.timeAllocationVisibleHoursCount
        && previous.resourceScheduleSidebarVisible === nextState.resourceScheduleSidebarVisible
        && previous.zoomScale === nextState.zoomScale
        && previous.resourceScheduleTimelineZoom === nextState.resourceScheduleTimelineZoom
        && previous.resourceScheduleVisibleStartHour === nextState.resourceScheduleVisibleStartHour
        && JSON.stringify(previous.statusHiddenColumnKeys || []) === JSON.stringify(nextState.statusHiddenColumnKeys || [])
        && JSON.stringify(previous.assignmentHiddenColumnKeys || []) === JSON.stringify(nextState.assignmentHiddenColumnKeys || [])
        && JSON.stringify(previous.timeAllocationHiddenColumnKeys || []) === JSON.stringify(nextState.timeAllocationHiddenColumnKeys || [])
        && JSON.stringify(previous.resourceScheduleHiddenColumnKeys || []) === JSON.stringify(nextState.resourceScheduleHiddenColumnKeys || [])
        && JSON.stringify(previous.statusColumnOrder || []) === JSON.stringify(nextState.statusColumnOrder || [])
        && previous.resourceScheduleStartHour === nextState.resourceScheduleStartHour
        && previous.resourceScheduleEndHour === nextState.resourceScheduleEndHour
        && previous.resourceScheduleIntervalMinutes === nextState.resourceScheduleIntervalMinutes
      ) {
        return previous;
      }

      return nextState;
    });
  }, [
    boardPreferencesLoaded,
    columnOrder,
    columnSettingsColumns,
    columns,
    currentUserId,
    defaultBoardViewMode,
    getDefaultTimeAllocationStartHour,
    resourceScheduleSidebarVisible,
    resourceScheduleTimelineZoom,
    resourceScheduleVisibleStartHour,
    selectedDate,
    timeAllocationVisibleHoursCount,
    timeAllocationVisibleStartHour,
    viewMode,
    visibleColumns,
    zoomScale,
  ]);

  const resourceScheduleConfig = useMemo(() => {
    const defaultStartHour = clampScheduleStartHour(SERVICE_OPERATIONS_CONFIG.startHour ?? 7);
    const resolvedStartHour = typeof boardPreferenceState.resourceScheduleStartHour === 'number'
      ? clampScheduleStartHour(boardPreferenceState.resourceScheduleStartHour)
      : defaultStartHour;
    const resolvedEndHour = typeof boardPreferenceState.resourceScheduleEndHour === 'number'
      ? clampScheduleEndHour(boardPreferenceState.resourceScheduleEndHour, resolvedStartHour)
      : clampScheduleEndHour(SERVICE_OPERATIONS_CONFIG.endHour ?? 18, resolvedStartHour);
    const resolvedIntervalMinutes = normalizeScheduleIntervalMinutes(boardPreferenceState.resourceScheduleIntervalMinutes)
      ?? normalizeScheduleIntervalMinutes(SERVICE_OPERATIONS_CONFIG.intervalMinutes)
      ?? 30;

    return {
      ...SERVICE_OPERATIONS_CONFIG,
      startHour: resolvedStartHour,
      endHour: resolvedEndHour,
      intervalMinutes: resolvedIntervalMinutes,
    };
  }, [boardPreferenceState.resourceScheduleEndHour, boardPreferenceState.resourceScheduleIntervalMinutes, boardPreferenceState.resourceScheduleStartHour]);

  useEffect(() => {
    if (skipNextResourceScheduleAutoAdjustRef.current) {
      skipNextResourceScheduleAutoAdjustRef.current = false;
      return;
    }

    const span = Math.max(1, resourceScheduleConfig.endHour - resourceScheduleConfig.startHour + 1);
    const isDynamicMode = boardPreferenceState.resourceScheduleTimelineMode !== 'static';
    if (getDateStrSAST(selectedDate) === getTodayStrSAST() && isDynamicMode) {
      // Dynamic mode: align visible window to current time on today's date
      setResourceScheduleVisibleStartHour(
        alignRollingScheduleVisibleStartToNow(getCurrentHourSAST(), span),
      );
    } else {
      // Static mode (or non-today in dynamic mode): always use configured start hour
      setResourceScheduleVisibleStartHour(resourceScheduleConfig.startHour);
    }
  }, [
    boardPreferenceState.resourceScheduleTimelineMode,
    resourceScheduleConfig.endHour,
    resourceScheduleConfig.startHour,
    selectedDate,
  ]);

  // Track pending status update as STATE (not ref) so it triggers re-renders
  // Extended to also track time allocation updates (scheduled times)
  const [pendingUpdate, setPendingUpdate] = useState<{
    id: string;
    newStatus: string;
    newAssignedTo?: number | null;
    newAssignedTeam?: string | null;
    newAssignedUserIds?: number[];
    scheduledStart?: string;
    scheduledEnd?: string;
    isNewAppointment?: boolean;
    parentJobId?: string;
  } | null>(null);

  // When an attendee is removed via a sidebar, clear any optimistic drag-drop state
  // for the affected item so the board falls back to refreshed props instead of
  // showing stale assignments locked in optimisticItems.
  const optimisticItemsRef = useRef(optimisticItems);
  optimisticItemsRef.current = optimisticItems;
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const pendingUpdateRef = useRef(pendingUpdate);
  pendingUpdateRef.current = pendingUpdate;
  const activeIdRef = useRef(activeId);
  activeIdRef.current = activeId;
  useEffect(() => {
    const handleAssigneesChanged = (e: CustomEvent) => {
      const detail = e.detail as {
        workItemId?: string;
        action?: string;
        removedUserId?: number;
        removedUserIds?: number[];
        addedUserIds?: number[];
        nextAssigneeUserIds?: number[];
        primaryUserId?: number | null;
      } | undefined;
      if (!detail?.workItemId || activeIdRef.current) return;

      const source = optimisticItemsRef.current.length > 0 ? optimisticItemsRef.current : itemsRef.current;
      const workItemId = String(detail.workItemId);
      const affectedItem = source.find((item) => String(item.id) === workItemId);
      if (!affectedItem) {
        return;
      }

      const normalizeUserIds = (userIds: Array<number | string | null | undefined>) => Array.from(new Set(
        userIds
          .map((userId) => Number(userId))
          .filter((userId) => Number.isFinite(userId) && userId > 0),
      ));

      const removedUserIds = normalizeUserIds([
        detail.removedUserId,
        ...(Array.isArray(detail.removedUserIds) ? detail.removedUserIds : []),
      ]);
      const addedUserIds = normalizeUserIds(Array.isArray(detail.addedUserIds) ? detail.addedUserIds : []);
      const explicitNextUserIds = Array.isArray(detail.nextAssigneeUserIds)
        ? normalizeUserIds(detail.nextAssigneeUserIds)
        : null;

      const nextItems = source.map((item) => {
        if (String(item.id) !== workItemId) {
          return item;
        }

        const currentUserIds = getItemAssignedUserIds(item);
        const nextUserIds = explicitNextUserIds ?? (() => {
          const nextIds = currentUserIds.filter((userId) => !removedUserIds.includes(userId));
          addedUserIds.forEach((userId) => {
            if (!nextIds.includes(userId)) {
              nextIds.push(userId);
            }
          });
          return nextIds;
        })();

        const existingAssigneesById = new Map(
          (Array.isArray(item.assignees) ? item.assignees : [])
            .map((assignee) => [Number(assignee.user_id), assignee] as const)
            .filter(([userId]) => Number.isFinite(userId) && userId > 0),
        );

        const nextAssignees = nextUserIds.map((userId) => {
          const existingAssignee = existingAssigneesById.get(userId);
          if (existingAssignee) {
            return existingAssignee;
          }

          const matchedUser = assignedUsers.find((candidate) => candidate.id === userId);
          return {
            user_id: userId,
            first_name: matchedUser?.first_name || '',
            last_name: matchedUser?.last_name || '',
            email: matchedUser?.email || '',
          };
        });

        const nextAssigneeDetails = nextAssignees.map((assignee) => ({
          user_id: assignee.user_id,
          first_name: assignee.first_name || '',
          last_name: assignee.last_name || '',
          email: assignee.email || '',
        }));
        const nextCustomFields = item.custom_fields && typeof item.custom_fields === 'object'
          ? { ...item.custom_fields }
          : {};

        nextCustomFields.assigned_user_ids = nextUserIds;
        nextCustomFields.participant_user_ids = nextUserIds;
        nextCustomFields.assigned_users = nextUserIds;
        nextCustomFields.assigned_users_details = nextAssigneeDetails;

        const primaryAssignedUserId = typeof detail.primaryUserId === 'number' && nextUserIds.includes(detail.primaryUserId)
          ? detail.primaryUserId
          : nextUserIds[0] ?? null;

        return {
          ...item,
          assigned_to: primaryAssignedUserId,
          assignees: nextAssignees,
          assigned_users: nextUserIds,
          assigned_users_details: nextAssigneeDetails,
          custom_fields: nextCustomFields,
        };
      });

      setOptimisticItems(nextItems);

      if (pendingUpdateRef.current) {
        setPendingUpdate(null);
      }
    };
    window.addEventListener('workItemAssigneesChanged', handleAssigneesChanged as EventListener);
    return () => {
      window.removeEventListener('workItemAssigneesChanged', handleAssigneesChanged as EventListener);
    };
  }, [assignedUsers, getItemAssignedUserIds]);

  useEffect(() => {
    const handleStatusChanged = (e: CustomEvent) => {
      const detail = e.detail as {
        workItemId?: string;
        status?: string | null;
      } | undefined;

      if (!detail?.workItemId || typeof detail.status !== 'string' || detail.status.trim().length === 0 || activeIdRef.current) {
        return;
      }

      const workItemId = String(detail.workItemId);
      const source = optimisticItemsRef.current.length > 0 ? optimisticItemsRef.current : itemsRef.current;
      if (!source.some((item) => String(item.id) === workItemId)) {
        return;
      }

      setOptimisticItems(source.map((item) => (
        String(item.id) === workItemId
          ? { ...item, status: detail.status!.trim() }
          : item
      )));

      if (pendingUpdateRef.current) {
        setPendingUpdate(null);
      }
    };

    window.addEventListener('workItemStatusChange', handleStatusChanged as EventListener);
    window.addEventListener(WORK_ITEM_LIST_CHANGED_EVENT, handleStatusChanged as EventListener);
    return () => {
      window.removeEventListener('workItemStatusChange', handleStatusChanged as EventListener);
      window.removeEventListener(WORK_ITEM_LIST_CHANGED_EVENT, handleStatusChanged as EventListener);
    };
  }, []);

  // Use optimistic items if available, otherwise use props items
  // Apply any pending updates to ensure the UI stays consistent
  const displayItems = useMemo(() => {
    const filterForStatusOrAssignmentView = (itemsToFilter: WorkItem[]) => {
      if (focusedItemType) {
        return itemsToFilter.filter(item => item.item_type === focusedItemType);
      }
      return itemsToFilter.filter(item => item.item_type !== 'appointment');
    };

    // Helper: Filter out completed/cancelled items for time_allocation (schedule) view
    // Completed jobs should not appear in the schedule
    const filterForScheduleView = (itemsToFilter: WorkItem[]) => {
      return filterScheduleVisibleItems(itemsToFilter, { preserveScheduledAppointments: true });
    };

    // If we have optimistic items, use them (during drag and immediately after)
    if (optimisticItems.length > 0) {
      // For status and assignment views, filter out appointments
      if (viewMode === 'status' || viewMode === 'assignment') {
        return filterForStatusOrAssignmentView(optimisticItems);
      }
      // For schedule views, filter out completed/cancelled items
      if (viewMode === 'time_allocation' || viewMode === 'resource_schedule') {
        return filterForScheduleView(optimisticItems);
      }
      return optimisticItems;
    }

    // If we have a pending update, apply it to items
    if (pendingUpdate) {
      const updatedItems = items.map(item => {
        if (item.id !== pendingUpdate.id) return item;

        const updatedItem = { ...item };
        updatedItem.status = pendingUpdate.newStatus;
        if (pendingUpdate.newAssignedTo !== undefined) {
          updatedItem.assigned_to = pendingUpdate.newAssignedTo ?? null;
          updatedItem.assigned_team = pendingUpdate.newAssignedTeam ?? null;
          updatedItem.assignees = buildPlaceholderAssignees(pendingUpdate.newAssignedUserIds ?? (pendingUpdate.newAssignedTo ? [pendingUpdate.newAssignedTo] : []));
        }
        // Apply scheduled times if present (for time allocation)
        if (pendingUpdate.scheduledStart) {
          updatedItem.scheduled_start = pendingUpdate.scheduledStart;
        }
        if (pendingUpdate.scheduledEnd) {
          updatedItem.scheduled_end = pendingUpdate.scheduledEnd;
        }
        return updatedItem;
      });

      // For status and assignment views, filter out appointments
      if (viewMode === 'status' || viewMode === 'assignment') {
        return filterForStatusOrAssignmentView(updatedItems);
      }
      // For schedule views, filter out completed/cancelled items
      if (viewMode === 'time_allocation' || viewMode === 'resource_schedule') {
        return filterForScheduleView(updatedItems);
      }
      return updatedItems;
    }

    // Default: use items from props
    // For status and assignment views, filter out appointments
    if (viewMode === 'status' || viewMode === 'assignment') {
      return filterForStatusOrAssignmentView(items);
    }
    // For schedule views, filter out completed/cancelled items
    if (viewMode === 'time_allocation' || viewMode === 'resource_schedule') {
      return filterForScheduleView(items);
    }

    return items;
  }, [buildPlaceholderAssignees, focusedItemType, items, optimisticItems, pendingUpdate, viewMode]);

  // Sync optimistic items with props items when not dragging
  // Clear pending update when the server has caught up
  const prevItemsRef = useRef<WorkItem[]>(items);

  useEffect(() => {
    // Skip if items array is empty (might be during refresh)
    if (items.length === 0 && prevItemsRef.current.length > 0) {
      // Items temporarily empty during refresh - keep optimistic state
      return;
    }

    // If we have a pending update and items changed, check if it's been applied
    if (pendingUpdate && items !== prevItemsRef.current) {
      console.log('[Kanban] Checking pending update:', {
        isNewAppointment: pendingUpdate.isNewAppointment,
        parentJobId: pendingUpdate.parentJobId,
        pendingId: pendingUpdate.id,
        itemsCount: items.length,
        appointmentsInItems: items.filter(i => i.item_type === 'appointment').length
      });

      // For new appointments, check if an appointment exists for the parent job
      if (pendingUpdate.isNewAppointment && pendingUpdate.parentJobId) {
        const matchingAppointments = items.filter(
          item => item.item_type === 'appointment' &&
            item.parent_id === pendingUpdate.parentJobId &&
            item.scheduled_start // Has scheduled time
        );
        console.log('[Kanban] Checking for new appointment:', {
          parentJobId: pendingUpdate.parentJobId,
          matchingAppointments: matchingAppointments.map(a => ({ id: a.id, parent_id: a.parent_id }))
        });

        if (matchingAppointments.length > 0) {
          console.log('[Kanban] New appointment confirmed in items, clearing optimistic state');
          setOptimisticItems([]);
          setPendingUpdate(null);
          prevItemsRef.current = items;
          return;
        }
      }

      const updatedItem = items.find(i => i.id === pendingUpdate.id);

      // Check if status matches
      const statusMatches = updatedItem && updatedItem.status === pendingUpdate.newStatus;

      // Check if assignment matches (if this was an assignment update)
      let assignmentMatches = true;
      if (pendingUpdate.newAssignedTo !== undefined) {
        const currentAssignedTeam = updatedItem?.assigned_team ?? null;
        const currentAssigned = updatedItem?.assigned_to ||
          (updatedItem?.assignees && updatedItem.assignees.length > 0 ? updatedItem.assignees[0].user_id : null);
        const teamMatches = (pendingUpdate.newAssignedTeam ?? null) === currentAssignedTeam;
        assignmentMatches = currentAssigned === pendingUpdate.newAssignedTo && teamMatches;
      }

      // For time allocation, also check scheduled times
      let scheduleMatches = true;
      if (pendingUpdate.scheduledStart && updatedItem) {
        // Just check that the item has scheduled times (exact match not required due to timezone differences)
        scheduleMatches = !!updatedItem.scheduled_start;
      }

      console.log('[Kanban] Checking server update:', {
        statusMatches,
        assignmentMatches,
        scheduleMatches,
        pending: pendingUpdate,
        serverItem: updatedItem ? { status: updatedItem.status, assigned_to: updatedItem.assigned_to, scheduled_start: updatedItem.scheduled_start } : null
      });

      if (statusMatches && assignmentMatches && scheduleMatches) {
        // Server has the update - clear optimistic items
        console.log('[Kanban] Server confirmed update, clearing optimistic items');
        setOptimisticItems([]);
        // Clear pendingUpdate since server has caught up
        setPendingUpdate(null);
      }
    }

    // Clear optimistic items when not dragging and no pending update
    // BUT only if items has caught up with our expectations
    if (!activeId && optimisticItems.length > 0 && !pendingUpdate) {
      // No pending update - safe to clear
      setOptimisticItems([]);
    }

    prevItemsRef.current = items;
  }, [items, activeId, optimisticItems.length, pendingUpdate]);

  // Helper function to get template for an item
  const getTemplate = (item: WorkItem): WorkItemTemplate | null => {
    if (!item.item_subtype || templates.length === 0) return null;
    return templates.find(t => t.item_subtype === item.item_subtype) || null;
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 12, // Give scroll/pan gestures more breathing room before drag activates
      },
    })
  );

  // Group items by status or assignment - use column keys
  const itemsByColumn = useMemo(() => {
    const grouped: Record<string, WorkItem[]> = {};

    // Helper to get the normalized workflow value for an item
    // This handles status aliases (e.g., 'done' -> 'completed', 'pending_closure' -> 'pending_approval')
    // NOTE: When useStrictWorkflowStates is true, we skip aliases - the workflow defines its own column keys
    const getNormalizedStatus = (item: WorkItem): string => {
      const rawStatus = getWorkflowValue(item, workflowField);
      // Skip aliases when using strict workflow states (e.g., email tasks, workspace workflows)
      // The workflow explicitly defines all column keys
      if (useStrictWorkflowStates || workflowField !== 'status') {
        return rawStatus;
      }
      // Apply status aliases to normalize legacy/alternate statuses
      return STATUS_ALIASES[rawStatus] || rawStatus;
    };

    // Check if an item is in a "pending approval" state (any approval-related status)
    const isPendingApproval = (item: WorkItem): boolean => {
      const rawStatus = item.status;
      return rawStatus === 'pending_approval' ||
        rawStatus === 'pending_closure' ||
        rawStatus === 'review' ||
        rawStatus === 'admin_approval' ||
        rawStatus === 'awaiting_approval';
    };

    // Check if item is completed (any completion-related status)
    const isCompleted = (item: WorkItem): boolean => {
      const rawStatus = item.status;
      return rawStatus === 'completed' ||
        rawStatus === 'done' ||
        rawStatus === 'closed' ||
        rawStatus === 'finished';
    };

    if (viewMode === 'status') {
      // Group by workflow field (status or current_workflow_stage)
      // Use normalized status to ensure items with aliased statuses appear in correct column
      activeColumns.forEach(col => {
        grouped[col.key] = displayItems.filter(item => getNormalizedStatus(item) === col.key);
      });
    } else {
      // Group by assignment - used for both 'assignment' and 'time_allocation' views
      // Separate items by status category first
      const completedItems = displayItems.filter(item => isCompleted(item));
      const cancelledItems = displayItems.filter(item => item.status === 'cancelled');
      const pendingApprovalItems = displayItems.filter(item => isPendingApproval(item));
      const keepScheduledAppointmentInActiveLane = (item: WorkItem): boolean => {
        return viewMode === 'time_allocation'
          && item.item_type === 'appointment'
          && Boolean(item.scheduled_start)
          && Boolean(item.scheduled_end)
          && item.status !== 'cancelled';
      };
      // Active items exclude completed, cancelled, AND pending approval
      // This ensures pending approval items don't show in user columns
      const activeItems = displayItems.filter(item =>
        !isPendingApproval(item)
        && (
          keepScheduledAppointmentInActiveLane(item)
          || (!isCompleted(item) && item.status !== 'cancelled')
        )
      );

      activeColumns.forEach(col => {
        if (col.key === 'status-completed') {
          // Show all completed items regardless of assignment
          grouped[col.key] = completedItems;
        } else if (col.key === 'status-cancelled') {
          // Show all cancelled items regardless of assignment
          grouped[col.key] = cancelledItems;
        } else if (col.key === 'status-pending_approval') {
          // Show all pending approval items regardless of assignment
          grouped[col.key] = pendingApprovalItems;
        } else if (col.key === 'unassigned') {
          // Only show active (non-completed/cancelled/pending) unassigned items
          // Check both assignees array and legacy assigned_to
          grouped[col.key] = activeItems.filter(item => {
            const assignedTeamId = getNormalizedAssignedTeamId(item);
            return !assignedTeamId && getItemAssignedUserIds(item).length === 0;
          });
        } else if (col.key.startsWith('team-')) {
          const teamId = col.key.replace('team-', '');
          grouped[col.key] = activeItems.filter(item => getNormalizedAssignedTeamId(item) === teamId);
        } else if (col.key.startsWith('user-')) {
          const userId = parseInt(col.key.replace('user-', ''));
          grouped[col.key] = activeItems.filter(item => isItemAssignedToUserLane(item, userId));
        } else {
          // Unknown column type - initialize empty
          grouped[col.key] = [];
        }
      });
    }

    return grouped;
  }, [activeColumns, displayItems, getItemAssignedUserIds, useStrictWorkflowStates, viewMode, workflowField]);

  // When the filter bar has active filters, hide resource columns (user-/team-) that have no
  // matching items — mirrors visibleTimelineResources used by the horizontal schedule.
  // The unassigned column and status columns (completed/cancelled/pending_approval) are
  // always kept so unscheduled items and terminal-status items remain reachable.
  // When the assignee filter is active, show exactly the selected users' columns
  // (even if they have no matching items), matching the horizontal schedule behaviour.
  const visibleTimeAllocationColumns = useMemo<WorkflowState[]>(() => {
    if (!filtersActive) {
      return visibleScheduleColumns;
    }

    const selectedAssigneeUserIds = activeAssigneeUserIds && activeAssigneeUserIds.length > 0
      ? new Set(activeAssigneeUserIds)
      : null;

    return visibleScheduleColumns.filter((column) => {
      // Always keep the unassigned column and status columns
      if (column.key === 'unassigned' || column.key.startsWith('status-')) {
        return true;
      }

      // Assignee filter: show exactly the selected users (even if they have no matching items)
      if (selectedAssigneeUserIds && column.key.startsWith('user-')) {
        const userId = Number(column.key.replace('user-', ''));
        return selectedAssigneeUserIds.has(userId);
      }

      // Other filters: show only columns that have at least one matching item
      const columnItems = itemsByColumn[column.key];
      return Boolean(columnItems && columnItems.length > 0);
    });
  }, [visibleScheduleColumns, filtersActive, activeAssigneeUserIds, itemsByColumn]);

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    const id = event.active.id as string;
    isDraggingRef.current = true;
    lastDispatchedHourRef.current = null; // Reset on drag start
    // Snapshot DOM elements once at drag-start to avoid querySelectorAll on every mousemove
    dragDomCacheRef.current = {
      unscheduledAreas: Array.from(document.querySelectorAll('[data-unscheduled-area]')),
      timelineAreas: Array.from(document.querySelectorAll('[data-timeline-area]')),
    };

    // Check if dragging a column
    if (id.startsWith('column-')) {
      setDraggingColumnId(id);
      setActiveId(null);
    } else {
      setActiveId(getWorkItemIdFromDragId(id));
      setDraggingColumnId(null);
    }
    setOverColumnId(null);
    setDropHour(null);
    setHoveredHour(null);
  };

  // Handle drag over - track which column is being hovered
  // Hour calculation is done by the mousemove handler for smooth updates
  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;

    // If dragging a column, don't track column hover
    if (draggingColumnId) return;

    // For time allocation view, column detection is done by mousemove handler
    // This just handles fallback column detection when over items
    if (over) {
      const rawOverId = String(over.id);
      const overColumnKey = rawOverId.replace('-timeline', '').replace('-unscheduled', '');
      const overColumn = activeColumns.find(col => col.key === overColumnKey);
      if (overColumn) {
        setOverColumnId(overColumn.key);
      } else {
        const overItem = items.find(i => i.id === getWorkItemIdFromDragId(rawOverId));
        if (overItem) {
          if (viewMode === 'status') {
            setOverColumnId(overItem.status ?? null);
          } else {
            let columnKey = 'unassigned';
            if (overItem.assigned_team) {
              columnKey = `team-${overItem.assigned_team}`;
            } else if (overItem.assignees && overItem.assignees.length > 0) {
              columnKey = `user-${overItem.assignees[0].user_id}`;
            } else if (overItem.assigned_to) {
              columnKey = `user-${overItem.assigned_to}`;
            }
            setOverColumnId(columnKey);
          }
        }
      }
    } else {
      setOverColumnId(null);
    }
  };

  // Complete appointment creation after location is confirmed (or was already set)
  // This is extracted so both the normal flow and the location picker callback can use it
  const completeAppointmentCreation = async (
    item: WorkItem,
    scheduledStartISO: string,
    scheduledEndISO: string,
    assignedTo: number | string | null,
    assignedTeamId: string | null,
    assignedUserIds: Array<number | string | null | undefined>,
    duration: number,
    setupSelection?: AppointmentDropSetupSelection
  ) => {
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) throw new Error('Not authenticated');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const normalizedAssignedTo = normalizeAssignedUserId(assignedTo);
      const allAssignedIds = Array.from(new Set(
        assignedUserIds
          .map((value) => normalizeAssignedUserId(value))
          .filter((value): value is number => value !== null)
      ));
      // Split into user IDs (positive) and contractor IDs (negative → abs)
      const effectiveAssignedUserIds = allAssignedIds.filter((id) => id > 0);
      const effectiveAssignedContractorIds = allAssignedIds.filter((id) => id < 0).map((id) => Math.abs(id));
      const primaryAssignedTo = normalizedAssignedTo ?? effectiveAssignedUserIds[0] ?? null;

      if (primaryAssignedTo === null) {
        throw new Error('Cannot create appointment without a valid assignee.');
      }

      if (assignedTo !== primaryAssignedTo) {
        console.warn('[Kanban] Normalized appointment assignee before create:', {
          rawAssignedTo: assignedTo,
          primaryAssignedTo,
          assignedUserIds,
          effectiveAssignedUserIds,
        });
      }

      const fallbackAppointmentType =
        item.item_subtype ||
        appointmentTypeOptions[0]?.key ||
        DEFAULT_APPOINTMENT_TYPES[0]?.key ||
        'site_visit';
      const resolvedAppointmentType = setupSelection?.appointmentType || fallbackAppointmentType;
      const isPurchaseAppointmentType = normalizeAppointmentTypeKey(resolvedAppointmentType) === PURCHASE_APPOINTMENT_TYPE_KEY;

      // Resolve contact/location values from the setup modal when available,
      // otherwise fall back to the job's existing details.
      const contactId = setupSelection?.resolvedContactId ?? item.contact_id ?? null;
      const contactName = setupSelection?.resolvedContactName ?? item.contact_name ?? null;
      const contactPhone = setupSelection?.resolvedContactPhone ?? item.contact_phone ?? null;
      const contactEmail = setupSelection?.resolvedContactEmail ?? item.contact_email ?? null;
      const contactPersonId = setupSelection?.resolvedContactPersonId ?? item.contact_person_id ?? null;
      const contactPerson = setupSelection?.resolvedContactPerson ?? item.contact_person ?? null;

      let locationId: number | null = null;
      let locationName: string | null = null;
      let locationAddress: string | null = null;
      let locationCoordinates: { lat: number; lng: number } | null = null;

      if (setupSelection) {
        locationId = setupSelection.resolvedLocationId ?? null;
        locationName = setupSelection.resolvedLocationName ?? null;
        locationAddress = setupSelection.resolvedLocationAddress ?? null;
        locationCoordinates = setupSelection.resolvedCoordinates ?? null;
      } else if (item.location_name && !item.location_id) {
        // Job inherited a custom location (no contact_location FK)
        locationName = item.location_name;
        locationAddress = item.location_address ?? null;
      } else {
        // Inherit contact location from job
        locationId = item.location_id ?? null;
        locationAddress = item.location_address ?? null;
      }

      // Fetch fresh parent job details to guarantee custom_fields and description are available
      let parentCustomFields = getItemCustomFields(item);
      let parentDescription: string | null = item.description || null;
      try {
        const parentRes = await fetch(`/api/v1/work-items?ids=${item.id}`, { headers });
        if (parentRes.ok) {
          const parentData = await parentRes.json();
          const parentItem = parentData.data?.[0];
          if (parentItem?.custom_fields) {
            parentCustomFields = parentItem.custom_fields;
          }
          if (parentItem?.description) {
            parentDescription = parentItem.description;
          }
          console.log('[Kanban] Fetched parent job details for inheritance:', {
            parentId: item.id,
            parentNumber: item.item_number,
            hasSafety: !!parentCustomFields.safety_requirements,
            hasEquipment: !!parentCustomFields.equipment_required,
            hasDescription: !!parentDescription,
            customFields: parentCustomFields,
          });
        } else {
          console.warn('[Kanban] Failed to fetch parent job, status:', parentRes.status);
        }
      } catch (e) {
        console.warn('[Kanban] Failed to fetch parent job details, using item data:', e);
      }

      const appointmentPayload: Record<string, unknown> = {
        item_type: 'appointment',
        item_subtype: resolvedAppointmentType,
        parent_id: item.id,
        title: setupSelection?.appointmentTitle?.trim() || item.title,
        description: parentDescription,
        notes: setupSelection?.additionalNotes?.trim() || parentCustomFields.additional_notes || undefined,
        module_context: item.module_context,
        assigned_to: primaryAssignedTo,
        assigned_team: assignedTeamId,
        assigned_users: effectiveAssignedUserIds,
        assigned_contractors: effectiveAssignedContractorIds.length > 0 ? effectiveAssignedContractorIds : undefined,
        scheduled_start: scheduledStartISO,
        scheduled_end: scheduledEndISO,
        status: 'scheduled',
        priority: setupSelection?.appointmentPriority || item.priority || 'medium',
        contact_id: contactId,
        contact_name: contactName,
        contact_phone: contactPhone,
        contact_email: contactEmail,
        contact_person: contactPerson,
        contact_person_id: contactPersonId,
        location_id: locationId,
        location_name: locationName,
        location_address: locationAddress,
        ...(locationCoordinates ? { location_coordinates: locationCoordinates } : {}),
        custom_fields: {
          require_location_verification: parentCustomFields.require_exact_location || parentCustomFields.require_location_verification || false,
          geofence_radius_meters: parentCustomFields.geofence_radius_meters || 100,
          parent_job_id: item.id,
          parent_job_number: item.item_number,
          // Inherit safety & equipment from parent job
          ...(!isPurchaseAppointmentType && (setupSelection?.safetyRequirements || parentCustomFields.safety_requirements)
            ? { safety_requirements: setupSelection?.safetyRequirements || parentCustomFields.safety_requirements }
            : {}),
          ...(!isPurchaseAppointmentType && (setupSelection?.equipmentRequired || parentCustomFields.equipment_required)
            ? { equipment_required: setupSelection?.equipmentRequired || parentCustomFields.equipment_required }
            : {}),
          ...(isPurchaseAppointmentType && setupSelection?.purchaseItems?.length
            ? { purchase_items: setupSelection.purchaseItems }
            : {}),
        },
      };

      if (assignedTeamId) {
        appointmentPayload.assigned_team = assignedTeamId;
      }
      appointmentPayload.estimated_hours = item.estimated_hours
        ? parseFloat(String(item.estimated_hours))
        : duration;

      const createResponse = await fetch('/api/v1/work-items', {
        method: 'POST',
        headers,
        body: JSON.stringify(appointmentPayload)
      });

      if (!createResponse.ok) {
        const errorText = await createResponse.text();
        console.error('[Kanban] Appointment creation failed:', errorText);
        throw new Error(`Failed to create appointment: ${errorText}`);
      }

      const appointmentData = await createResponse.json();
      if (appointmentData.data) {
        appointmentData.data.parent_job_number = item.item_number;
        appointmentData.data.parent_job_type = item.item_type;
      }

      // Run independent post-creation tasks in parallel for speed
      const postCreationTasks: Promise<unknown>[] = [];

      // 1. Update job status to scheduled
      let jobStatusUpdateError: string | null = null;
      postCreationTasks.push(
        (async () => {
          try {
            const statusResponse = await fetch(`/api/v1/work-items/${item.id}`, {
              method: 'PUT',
              headers,
              body: JSON.stringify({ status: 'scheduled' })
            });
            if (!statusResponse.ok) {
              let errMsg = `HTTP ${statusResponse.status}`;
              try {
                const errData = await statusResponse.json();
                errMsg = errData.error || errData.message || errMsg;
              } catch { /* ignore parse error */ }
              jobStatusUpdateError = errMsg;
            }
          } catch (err) {
            console.warn('[Kanban] Failed to update job status:', err);
          }
        })()
      );

      // 2. Handle assignees (get existing + add if needed)
      postCreationTasks.push(
        (async () => {
          try {
            const getResponse = await fetch(`/api/v1/work-items/${item.id}/assignees`, {
              method: 'GET',
              headers,
            });
            let existingAssignees: { user_id: number }[] = [];
            if (getResponse.ok) {
              const getData = await getResponse.json();
              existingAssignees = getData.data || [];
            }

            for (const userId of effectiveAssignedUserIds) {
              const isAlreadyAssigned = existingAssignees.some(a => a.user_id === userId);
              if (!isAlreadyAssigned) {
                await fetch(`/api/v1/work-items/${item.id}/assignees`, {
                  method: 'POST',
                  headers,
                  body: JSON.stringify({ user_id: userId })
                });
              }
            }

            return existingAssignees;
          } catch (err) {
            console.warn('[Kanban] Failed to handle assignees:', err);
            return [];
          }
        })()
      );

      // 3. Notify job thread (fire-and-forget, don't block on it)
      if (appointmentData.data?.id) {
        postCreationTasks.push(
          fetch('/api/v1/messaging/job-threads', {
            method: 'POST',
            headers,
            body: JSON.stringify({
              action: 'notifyAppointmentScheduled',
              job_id: item.id,
              appointment_id: appointmentData.data.id,
              assignee_user_ids: effectiveAssignedUserIds,
              scheduled_start: scheduledStartISO,
              scheduled_end: scheduledEndISO,
              appointment_title: setupSelection?.appointmentTitle?.trim() || `${item.title} - Appointment`
            })
          }).catch(notifyErr => console.warn('[Kanban] Failed to notify job thread:', notifyErr))
        );
      }

      // 4. Clone subtasks from parent job to appointment (inherit checklist items)
      if (appointmentData.data?.id) {
        postCreationTasks.push(
          (async () => {
            try {
              const subtasksRes = await fetch(`/api/v1/work-items/${item.id}/subtasks`, { headers });
              if (!subtasksRes.ok) return;
              const subtasksData = await subtasksRes.json();
              const subtasks = subtasksData.data || [];
              if (subtasks.length === 0) return;
              for (const st of subtasks) {
                await fetch(`/api/v1/work-items/${appointmentData.data.id}/subtasks`, {
                  method: 'POST',
                  headers,
                  body: JSON.stringify({
                    title: st.title,
                    item_type: st.item_type || 'subtask',
                    status: 'open',
                    priority: st.priority || 'medium',
                    description: st.description || null,
                  })
                }).catch(() => { });
              }
            } catch (err) {
              console.warn('[Kanban] Failed to clone subtasks to appointment:', err);
            }
          })()
        );
      }

      // Wait for all parallel tasks to finish
      await Promise.allSettled(postCreationTasks);

      // Refresh items (appointment was created — do this before surfacing errors)
      if (onRefresh) await onRefresh();
      setOptimisticItems([]);

      // Surface job status update failure after the board has refreshed
      if (jobStatusUpdateError) {
        throw new Error(`Appointment was created, but job status could not be updated: ${jobStatusUpdateError}`);
      }

      // Notify parent of appointment creation (for schedule↔route sync)
      if (onAppointmentChange) {
        onAppointmentChange({
          action: 'created',
          appointmentId: appointmentData.data?.id ? String(appointmentData.data.id) : undefined,
          date: scheduledStartISO.split('T')[0],
          assignedTo: primaryAssignedTo,
        });
      }
    } catch (err) {
      console.error('[Kanban] completeAppointmentCreation error:', err);
      setOptimisticItems([]);
      if (onRefresh) await onRefresh();
      throw err;
    }
  };

  // Appointment setup callbacks
  const handleAppointmentSetupConfirmed = async (selection: AppointmentDropSetupSelection) => {
    if (!pendingAppointmentSetup) return;

    const currentSetup = pendingAppointmentSetup;
    const resolvedScheduledStartISO = selection.scheduledStartISO || currentSetup.scheduledStartISO;
    const resolvedScheduledEndISO = selection.scheduledEndISO || currentSetup.scheduledEndISO;
    await completeAppointmentCreation(
      currentSetup.activeItem,
      resolvedScheduledStartISO,
      resolvedScheduledEndISO,
      currentSetup.newAssignedTo,
      currentSetup.assignedTeamId ?? null,
      currentSetup.assignedUserIds ?? [currentSetup.newAssignedTo],
      currentSetup.duration,
      selection
    );

    setPendingAppointmentSetup(null);
  };

  const handleAppointmentSetupCancelled = () => {
    const sourceView = pendingAppointmentSetup?.sourceView;
    setPendingAppointmentSetup(null);
    setOptimisticItems([]);

    if (sourceView === 'resource_schedule' && onRefresh) {
      void onRefresh();
    }
  };

  // Handle drag end
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    // Handle column reordering
    if (draggingColumnId) {
      setDraggingColumnId(null);

      if (over && active.id !== over.id) {
        const activeColumnKey = (active.id as string).replace('column-', '');
        const overColumnKey = (over.id as string).replace('column-', '');

        // Get current order (or use visible columns order)
        const currentOrder = columnOrder.length > 0
          ? columnOrder
          : activeColumns.filter((column) => visibleColumns.has(column.key)).map((column) => column.key);

        const oldIndex = currentOrder.indexOf(activeColumnKey);
        const newIndex = currentOrder.indexOf(overColumnKey);

        if (oldIndex !== -1 && newIndex !== -1) {
          const newOrder = arrayMove(currentOrder, oldIndex, newIndex);
          if (newOrder.join('|') !== currentOrder.join('|')) {
            void persistColumnOrder(newOrder);
          }
        }
      }
      return;
    }

    // Save overColumnId before clearing (fallback for drop detection)
    const savedOverColumnId = overColumnId;
    // Save the hovered hour ref value (most accurate for time allocation)
    const savedHoveredHour = hoveredHourRef.current;

    // Reset drag state
    setOverColumnId(null);

    if (!over) {
      setActiveId(null);
      setOptimisticItems([]);
      setDropHour(null);
      setHoveredHour(null);
      hoveredHourRef.current = null;
      isDraggingRef.current = false;
      return;
    }

    const activeItem = items.find(i => i.id === getWorkItemIdFromDragId(String(active.id)));
    const overId = over.id as string;

    if (!activeItem) {
      setActiveId(null);
      setOptimisticItems([]);
      setDropHour(null);
      setHoveredHour(null);
      hoveredHourRef.current = null;
      isDraggingRef.current = false;
      return;
    }

    // For time allocation view, PRIORITIZE the hoveredHourRef column (most accurate)
    // This uses the actual mouse position over the timeline area
    let targetColumn: WorkflowState | undefined;

    if (viewMode === 'time_allocation' && savedHoveredHour?.columnKey) {
      targetColumn = activeColumns.find(col => col.key === savedHoveredHour.columnKey);
    }

    // Fallback: Check if dropped on a column directly
    if (!targetColumn) {
      // Handle new droppable IDs with -unscheduled or -timeline suffixes
      targetColumn = activeColumns.find(col => col.key === overId);

      // If not a direct match, check if it's a timeline or unscheduled area
      if (!targetColumn) {
        // Extract base column key (remove -timeline or -unscheduled suffix)
        const baseKey = overId.replace(/-timeline$/, '').replace(/-unscheduled$/, '');
        targetColumn = activeColumns.find(col => col.key === baseKey);
      }
    }

    // If not dropped on column, check if dropped on a card - find that card's column
    if (!targetColumn) {
      const overItem = items.find(i => i.id === getWorkItemIdFromDragId(overId));
      if (overItem) {
        if (viewMode === 'status') {
          // Find column by the item's status
          targetColumn = activeColumns.find(col => col.key === overItem.status);
        } else {
          // Find column by the item's assignee
          if (overItem.assigned_team) {
            targetColumn = activeColumns.find(col => col.key === `team-${overItem.assigned_team}`);
          } else if (overItem.assignees && overItem.assignees.length > 0) {
            targetColumn = activeColumns.find(col => col.key === `user-${overItem.assignees![0].user_id}`);
          } else if (overItem.assigned_to) {
            targetColumn = activeColumns.find(col => col.key === `user-${overItem.assigned_to}`);
          } else {
            targetColumn = activeColumns.find(col => col.key === 'unassigned');
          }
        }
      }
    }

    // Fallback: use saved overColumnId if we tracked it during drag
    if (!targetColumn && savedOverColumnId) {
      targetColumn = activeColumns.find(col => col.key === savedOverColumnId);
    }

    if (targetColumn) {
      if (viewMode === 'status' && workflowField === 'status' && !canManuallyTargetWorkflowStatusKey(targetColumn.key)) {
        setActiveId(null);
        setOptimisticItems([]);
        setDropHour(null);
        setHoveredHour(null);
        hoveredHourRef.current = null;
        isDraggingRef.current = false;
        return;
      }

      const activeWorkflowValue = getWorkflowValue(activeItem, workflowField);

      if (viewMode === 'status' && activeWorkflowValue !== targetColumn.key) {
        // Validate against configured transition rules (allowed_from)
        // Per-status: statuses with explicit allowed_from respect it;
        // statuses without allowed_from (undefined or empty) remain permissive.
        if (boardWorkflowStatuses) {
          const normalizedCurrent = normalizeWorkflowStatusKey(activeWorkflowValue);
          const normalizedTarget = normalizeWorkflowStatusKey(targetColumn.key);
          const targetStatusConfig = boardWorkflowStatuses.find(
            (s) => normalizeWorkflowStatusKey(s.key) === normalizedTarget
          );
          const hasExplicitRules = targetStatusConfig?.allowed_from && targetStatusConfig.allowed_from.length > 0;
          const isAllowed = hasExplicitRules
            ? targetStatusConfig!.allowed_from!.includes(normalizedCurrent)
            : true;
          if (!isAllowed) {
            setActiveId(null);
            setOptimisticItems([]);
            setDropHour(null);
            setHoveredHour(null);
            hoveredHourRef.current = null;
            isDraggingRef.current = false;
            void notify.error(
              `Cannot move from "${activeWorkflowValue}" to "${targetColumn.key}" — transition not configured.`,
              { duration: 4000 }
            );
            return;
          }
        }

        // Status-based update
        console.log('[Kanban] Status update starting:', {
          itemId: activeItem.id,
          itemNumber: activeItem.item_number,
          fromStatus: activeWorkflowValue,
          toStatus: targetColumn.key
        });

        // CRITICAL: Clear drag state FIRST so dnd-kit releases the item
        // This allows the new card instance in the target column to render properly
        setActiveId(null);
        setDropHour(null);
        setHoveredHour(null);
        hoveredHourRef.current = null;
        isDraggingRef.current = false;

        // Optimistic update
        const updatedItems = items.map(item =>
          item.id === activeItem.id
            ? withWorkflowValue(item, workflowField, targetColumn.key)
            : item
        );
        console.log('[Kanban] Setting optimistic items, count:', updatedItems.length);
        setOptimisticItems(updatedItems);

        // Track the pending update for verification (using STATE to trigger re-render)
        setPendingUpdate({ id: activeItem.id, newStatus: targetColumn.key });

        // Fire-and-forget API call - don't await to let dnd-kit clear its internal state
        // The useEffect will handle clearing optimistic items when the items prop updates
        onUpdateStatus(activeItem.id, targetColumn.key)
          .then(() => {
            console.log('[Kanban] onUpdateStatus completed successfully');
          })
          .catch((error) => {
            console.error('[Kanban] Failed to update status:', error);
            setOptimisticItems([]);
            setPendingUpdate(null);
            void notify.error(`Failed to move item: ${error instanceof Error ? error.message : 'Unknown error'}`, { duration: 6000 });
          });

        // Return early since we've already cleared drag state
        return;
      } else if (viewMode === 'assignment') {
        // Check if dropping to status-based columns (completed/cancelled)
        if (targetColumn.key === 'status-completed' || targetColumn.key === 'status-cancelled') {
          // Update status instead of assignment
          const newStatus = targetColumn.key === 'status-completed' ? 'completed' : 'cancelled';

          if (activeItem.status !== newStatus) {
            // CRITICAL: Clear drag state FIRST
            setActiveId(null);
            setDropHour(null);
            setHoveredHour(null);
            hoveredHourRef.current = null;
            isDraggingRef.current = false;

            // Optimistic update
            const updatedItems = items.map(item =>
              item.id === activeItem.id
                ? { ...item, status: newStatus }
                : item
            );
            setOptimisticItems(updatedItems);

            // Fire-and-forget API call
            onUpdateStatus(activeItem.id, newStatus)
              .then(() => {
                console.log('[Kanban] Status update (assignment view) completed successfully');
              })
              .catch((error) => {
                console.error('[Kanban] Failed to update status:', error);
                setOptimisticItems([]);
                void notify.error(`Failed to move item: ${error instanceof Error ? error.message : 'Unknown error'}`, { duration: 6000 });
              });

            return;
          }
        } else {
          // Assignment-based update
          const assignmentTarget = resolveAssignmentTarget(targetColumn.key);
          if (!assignmentTarget) {
            setActiveId(null);
            setDropHour(null);
            setHoveredHour(null);
            hoveredHourRef.current = null;
            isDraggingRef.current = false;
            return;
          }

          const newAssignedTo = assignmentTarget.assignedTo;
          const newAssignedTeam = assignmentTarget.assignedTeam;
          const newAssignedUserIds = assignmentTarget.assignedUserIds;

          // Check current assignment - consider both assignees array and legacy assigned_to
          let currentAssignedTo: number | null = null;
          const currentAssignedTeam = activeItem.assigned_team ?? null;
          if (activeItem.assignees && activeItem.assignees.length > 0) {
            currentAssignedTo = activeItem.assignees[0].user_id;
          } else if (activeItem.assigned_to) {
            currentAssignedTo = typeof activeItem.assigned_to === 'string'
              ? parseInt(activeItem.assigned_to)
              : activeItem.assigned_to;
          }

          // Skip if dropping on same column (no change needed)
          if (currentAssignedTo === newAssignedTo && currentAssignedTeam === newAssignedTeam) {
            setActiveId(null);
            setDropHour(null);
            setHoveredHour(null);
            hoveredHourRef.current = null;
            isDraggingRef.current = false;
            return;
          }

          // CRITICAL: Clear drag state FIRST so dnd-kit releases the item
          setActiveId(null);
          setDropHour(null);
          setHoveredHour(null);
          hoveredHourRef.current = null;
          isDraggingRef.current = false;

          // Optimistic update - update local state immediately
          const updatedItems = items.map(item =>
            item.id === activeItem.id
              ? {
                ...item,
                assigned_to: newAssignedTo,
                assigned_team: newAssignedTeam,
                assignees: buildPlaceholderAssignees(newAssignedUserIds)
              }
              : item
          );
          setOptimisticItems(updatedItems);

          // Track the pending update for verification (using STATE to trigger re-render)
          setPendingUpdate({
            id: activeItem.id,
            newStatus: activeItem.status || 'open',
            newAssignedTo,
            newAssignedTeam,
            newAssignedUserIds,
          });

          // Fire-and-forget the API call - don't await to let dnd-kit clear its internal state
          const performAssignmentUpdate = async () => {
            try {
              if (onUpdateAssignment && assignmentTarget.kind !== 'team') {
                await onUpdateAssignment(activeItem.id, newAssignedTo);
              } else {
                // Use the assignees API for proper multi-assignee support
                const getAuthToken = () => {
                  try {
                    const storedTokens = localStorage.getItem('auth_tokens');
                    if (storedTokens) {
                      const tokens = JSON.parse(storedTokens);
                      return tokens.access_token;
                    }
                  } catch (error) {
                    console.warn('[KanbanBoard] Failed to load auth token:', error);
                  }
                  return null;
                };

                const token = getAuthToken();
                const headers = {
                  'Content-Type': 'application/json',
                  ...(token && { 'Authorization': `Bearer ${token}` }),
                };

                // Step 1: Get current assignees from API (source of truth)
                const getResponse = await fetch(`/api/v1/work-items/${activeItem.id}/assignees`, {
                  method: 'GET',
                  headers,
                });

                let existingAssignees: { user_id: number }[] = [];
                if (getResponse.ok) {
                  const getData = await getResponse.json();
                  existingAssignees = getData.data || [];
                }

                // Step 2: Remove all current assignees from work_item_assignees table
                for (const assignee of existingAssignees) {
                  await fetch(`/api/v1/work-items/${activeItem.id}/assignees/${assignee.user_id}`, {
                    method: 'DELETE',
                    headers,
                  });
                }

                // Step 3: Also clear the legacy assigned_to field on the work item
                // This is needed because some items have assigned_to but no entries in work_item_assignees
                if (assignmentTarget.kind === 'team' || !newAssignedTo || currentAssignedTo) {
                  await fetch(`/api/v1/work-items/${activeItem.id}`, {
                    method: 'PUT',
                    headers,
                    body: JSON.stringify({ assigned_to: newAssignedTo, assigned_team: newAssignedTeam })
                  });
                }

                // Step 4: Add new assignee (if not unassigning)
                for (const userId of newAssignedUserIds) {
                  const addResponse = await fetch(`/api/v1/work-items/${activeItem.id}/assignees`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({ user_id: userId })
                  });

                  if (!addResponse.ok) {
                    const errorData = await addResponse.json().catch(() => ({}));
                    throw new Error(errorData.error || 'Failed to add assignee');
                  }
                }
              }

              // Trigger a refresh of the items to get updated data
              if (onRefresh) {
                await onRefresh();
              }
              console.log('[Kanban] Assignment update completed successfully');
            } catch (error) {
              console.error('[Kanban] Failed to update assignment:', error);
              setOptimisticItems([]);
              setPendingUpdate(null);
              notify.error(`Failed to reassign item: ${error instanceof Error ? error.message : 'Unknown error'}`);
            }
          };

          // Fire and forget - return immediately so dnd-kit can clear its state
          performAssignmentUpdate();
          return;
        }
      } else if (viewMode === 'time_allocation') {
        // Time allocation update - assign to user and set scheduled time
        const isStatusColumn = targetColumn.key === 'status-completed' || targetColumn.key === 'status-cancelled';

        if (isStatusColumn) {
          // Update status
          const newStatus = targetColumn.key === 'status-completed' ? 'completed' : 'cancelled';

          if (activeItem.status !== newStatus) {
            // CRITICAL: Clear drag state FIRST so dnd-kit releases the item
            setActiveId(null);
            setDropHour(null);
            setHoveredHour(null);
            hoveredHourRef.current = null;
            isDraggingRef.current = false;

            const updatedItems = items.map(item =>
              item.id === activeItem.id
                ? { ...item, status: newStatus }
                : item
            );
            setOptimisticItems(updatedItems);

            // Fire-and-forget status update
            onUpdateStatus(activeItem.id, newStatus)
              .then(() => {
                console.log('[Kanban] Time allocation status update completed');
              })
              .catch((error) => {
                console.error('[Kanban] Failed to update status:', error);
                setOptimisticItems([]);
                void notify.error(`Failed to move item: ${error instanceof Error ? error.message : 'Unknown error'}`, { duration: 6000 });
              });

            return; // Return immediately to let dnd-kit clear its state
          }
        } else {
          // Assignment update for time allocation
          // CRITICAL: Clear drag state FIRST so dnd-kit releases the item
          setActiveId(null);
          setDropHour(null);
          setHoveredHour(null);
          hoveredHourRef.current = null;
          isDraggingRef.current = false;

          // ============================================================
          // SYNCHRONOUS OPTIMISTIC UPDATE - Calculate and set BEFORE async
          // ============================================================

          // Determine if dropped on unscheduled area (synchronously)
          let droppedOnUnscheduledSync = targetColumn.key === 'unassigned' || String(overId).endsWith('-unscheduled');
          const assignmentTargetSync = droppedOnUnscheduledSync ? null : resolveAssignmentTarget(targetColumn.key);
          const hasValidHoverHourSync = savedHoveredHour !== null &&
            savedHoveredHour.hour !== undefined &&
            (targetColumn.key.startsWith('user-') || targetColumn.key.startsWith('team-'));

          if (hasValidHoverHourSync) {
            droppedOnUnscheduledSync = false;
          } else if (!droppedOnUnscheduledSync && savedHoveredHour === null && targetColumn.key !== 'unassigned') {
            droppedOnUnscheduledSync = true;
          } else if (!droppedOnUnscheduledSync && overId) {
            const overIdStr = String(overId);
            const isColumnId = overIdStr.startsWith('user-') || overIdStr.startsWith('team-') || overIdStr.includes('-timeline') || overIdStr.includes('-unscheduled') || overIdStr === 'unassigned';
            if (!isColumnId) {
              const targetItem = items.find(item => item.id === overId);
              if (targetItem) {
                const hasScheduledTime = targetItem.scheduled_start && isSameDay(new Date(targetItem.scheduled_start), selectedDate);
                if (!hasScheduledTime) {
                  droppedOnUnscheduledSync = true;
                }
              }
            }
          }

          const mergedAssignmentSync = droppedOnUnscheduledSync
            ? { assignedTo: null, assignedTeam: null, assignedUserIds: [] as number[] }
            : buildAppointmentAssignmentForTarget(activeItem, assignmentTargetSync);
          const newAssignedToSync = mergedAssignmentSync.assignedTo;
          const newAssignedTeamSync = mergedAssignmentSync.assignedTeam;
          const newAssignedUserIdsSync = mergedAssignmentSync.assignedUserIds;
          const startHourSync = savedHoveredHour?.hour ?? dropHour ?? 9;

          // Calculate scheduled times synchronously
          let durationSync = 1;
          if (activeItem.scheduled_start && activeItem.scheduled_end) {
            const existingStart = new Date(activeItem.scheduled_start);
            const existingEnd = new Date(activeItem.scheduled_end);
            const durationMs = existingEnd.getTime() - existingStart.getTime();
            durationSync = durationMs / (1000 * 60 * 60);
            if (durationSync <= 0) durationSync = 1;
          } else if (activeItem.estimated_hours) {
            durationSync = parseFloat(String(activeItem.estimated_hours)) || 1;
          }

          const scheduledStartSync = new Date(selectedDate);
          scheduledStartSync.setHours(startHourSync, 0, 0, 0);
          const scheduledEndSync = new Date(scheduledStartSync);
          scheduledEndSync.setTime(scheduledStartSync.getTime() + durationSync * 60 * 60 * 1000);

          // Format times synchronously using SAST utility
          const scheduledStartISOSync = formatLocalISOSAST(scheduledStartSync);
          const scheduledEndISOSync = formatLocalISOSAST(scheduledEndSync);

          // SET OPTIMISTIC STATE IMMEDIATELY (before any async operations)
          if (activeItem.item_type === 'appointment') {
            if (droppedOnUnscheduledSync) {
              // Appointment dropped to unscheduled - optimistically remove it
              setOptimisticItems(items.filter(item => item.id !== activeItem.id));
              // Track that we're deleting this appointment
              setPendingUpdate({
                id: activeItem.id,
                newStatus: 'deleted',
                parentJobId: activeItem.parent_id || undefined
              });
            } else {
              // Appointment moved to new time/user - optimistically update it
              const optimisticUpdatedItems = items.map(item => {
                if (item.id === activeItem.id) {
                  return {
                    ...item,
                    assigned_to: newAssignedToSync,
                    assigned_team: newAssignedTeamSync,
                    assignees: buildPlaceholderAssignees(newAssignedUserIdsSync),
                    scheduled_start: scheduledStartISOSync,
                    scheduled_end: scheduledEndISOSync,
                    status: newAssignedToSync ? 'scheduled' : 'pending',
                  };
                }
                return item;
              });
              setOptimisticItems(optimisticUpdatedItems);
              // Track the pending update for verification
              setPendingUpdate({
                id: activeItem.id,
                newStatus: newAssignedToSync ? 'scheduled' : 'pending',
                newAssignedTo: newAssignedToSync,
                newAssignedTeam: newAssignedTeamSync,
                newAssignedUserIds: newAssignedUserIdsSync,
                scheduledStart: scheduledStartISOSync,
                scheduledEnd: scheduledEndISOSync
              });
            }
          } else if (activeItem.item_type === 'job') {
            if (droppedOnUnscheduledSync) {
              // Job dropped to unscheduled - optimistically update job status
              const optimisticUpdatedItems = items.map(item => {
                if (item.id === activeItem.id) {
                  return { ...item, assigned_to: null, assigned_team: null, status: 'open', assignees: [] };
                }
                // Also remove any appointment child of this job
                if (item.item_type === 'appointment' && item.parent_id === activeItem.id) {
                  return null; // Mark for removal
                }
                return item;
              }).filter(Boolean) as typeof items;
              setOptimisticItems(optimisticUpdatedItems);
              // Track the pending update
              setPendingUpdate({
                id: activeItem.id,
                newStatus: 'open',
                newAssignedTo: null
              });
            } else if (newAssignedToSync) {
              // Job dropped to schedule - optimistically add temporary appointment
              const tempAppointmentId = `temp-${Date.now()}`;
              const optimisticAppointment = {
                id: tempAppointmentId,
                item_number: 'APT-...',
                item_type: 'appointment',
                parent_id: activeItem.id,
                parent_job_number: activeItem.item_number,
                title: activeItem.title,
                module_context: activeItem.module_context,
                assigned_to: newAssignedToSync,
                assigned_team: newAssignedTeamSync,
                assignees: buildPlaceholderAssignees(newAssignedUserIdsSync),
                scheduled_start: scheduledStartISOSync,
                scheduled_end: scheduledEndISOSync,
                status: 'scheduled',
                priority: activeItem.priority,
                estimated_hours: activeItem.estimated_hours || String(durationSync),
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              };
              setOptimisticItems([...items, optimisticAppointment]);
              // Track that we're creating a new appointment for this job
              setPendingUpdate({
                id: tempAppointmentId,
                newStatus: 'scheduled',
                newAssignedTo: newAssignedToSync,
                newAssignedTeam: newAssignedTeamSync,
                newAssignedUserIds: newAssignedUserIdsSync,
                scheduledStart: scheduledStartISOSync,
                scheduledEnd: scheduledEndISOSync,
                isNewAppointment: true,
                parentJobId: activeItem.id
              });
            }
          }

          // ============================================================
          // ASYNC API OPERATIONS - Fire and forget after optimistic update
          // ============================================================

          // Wrap the entire async operation in a fire-and-forget function
          const performTimeAllocationUpdate = async () => {

            // Check if dropped on unscheduled area (should delete appointment/unschedule job)
            // Detection methods:
            // 1. overId ends with '-unscheduled' (dropped on empty unscheduled container)
            // 2. targetColumn is 'unassigned'
            // 3. savedHoveredHour is null (mouse was over unscheduled area during drag)
            // 4. overId is an item that's in the unscheduled items (no scheduled time for today)
            let droppedOnUnscheduled = targetColumn.key === 'unassigned' || String(overId).endsWith('-unscheduled');
            const assignmentTarget = droppedOnUnscheduled ? null : resolveAssignmentTarget(targetColumn.key);

            // IMPORTANT: If savedHoveredHour has a valid hour value and we're on a user column, 
            // this is definitely a scheduled drop - don't override it
            const hasValidHoverHour = savedHoveredHour !== null &&
              savedHoveredHour.hour !== undefined &&
              (targetColumn.key.startsWith('user-') || targetColumn.key.startsWith('team-'));

            if (hasValidHoverHour) {
              // User was hovering over a time slot - this is a scheduled drop
              droppedOnUnscheduled = false;
            } else {
              // Also check if hoveredHourRef was null (meaning mouse was over unscheduled area)
              if (!droppedOnUnscheduled && savedHoveredHour === null && targetColumn.key !== 'unassigned') {
                // Check if we were over an unscheduled area - hoveredHourRef would be null
                droppedOnUnscheduled = true;
              }

              // Also check if overId is an item that's in the unscheduled section
              // Skip this check if overId looks like a column/droppable ID (user-, -timeline, -unscheduled)
              if (!droppedOnUnscheduled && overId) {
                const overIdStr = String(overId);
                const isColumnId = overIdStr.startsWith('user-') || overIdStr.startsWith('team-') || overIdStr.includes('-timeline') || overIdStr.includes('-unscheduled') || overIdStr === 'unassigned';
                if (!isColumnId) {
                  // overId might be an item ID - check if that item is unscheduled
                  const targetItem = items.find(item => item.id === overId);
                  if (targetItem) {
                    // If the target item has no scheduled_start or it's not on the selected date, it's unscheduled
                    const hasScheduledTime = targetItem.scheduled_start && isSameDay(new Date(targetItem.scheduled_start), selectedDate);
                    if (!hasScheduledTime) {
                      droppedOnUnscheduled = true;
                    }
                  }
                }
              }
            }


            const mergedAssignment = droppedOnUnscheduled
              ? { assignedTo: null, assignedTeam: null, assignedUserIds: [] as number[] }
              : buildAppointmentAssignmentForTarget(activeItem, assignmentTarget);
            const newAssignedTo = mergedAssignment.assignedTo;
            const newAssignedTeam = mergedAssignment.assignedTeam;
            const newAssignedUserIds = mergedAssignment.assignedUserIds;

            try {
              const getAuthToken = () => {
                try {
                  const storedTokens = localStorage.getItem('auth_tokens');
                  if (storedTokens) {
                    const tokens = JSON.parse(storedTokens);
                    return tokens.access_token;
                  }
                } catch (error) {
                  console.warn('[KanbanBoard] Failed to load auth token:', error);
                }
                return null;
              };

              const token = getAuthToken();
              const headers = {
                'Content-Type': 'application/json',
                ...(token && { 'Authorization': `Bearer ${token}` }),
              };

              // Helper function to check for appointment clashes
              const checkForClashes = (
                userId: number,
                newStart: Date,
                newEnd: Date,
                excludeItemId?: string,
              ): WorkItem[] => {
                // Find all appointments for this user on this day
                const userAppointments = items.filter(item => {
                  if (item.item_type !== 'appointment') return false;
                  if (excludeItemId && item.id === excludeItemId) return false;
                  if (!item.scheduled_start || !item.scheduled_end) return false;

                  // Check if assigned to this user
                  const itemUserIds = getItemAssignedUserIds(item);
                  if (!itemUserIds.includes(userId)) return false;

                  // Check if on the same day
                  const itemStart = new Date(item.scheduled_start);
                  if (!isSameDay(itemStart, selectedDate)) return false;

                  return true;
                });

                // Check for overlaps
                const clashingAppointments: WorkItem[] = [];
                for (const apt of userAppointments) {
                  const aptStart = new Date(apt.scheduled_start!);
                  const aptEnd = new Date(apt.scheduled_end!);

                  // Check if times overlap (not just touch)
                  // Overlap exists if: newStart < aptEnd AND newEnd > aptStart
                  if (newStart < aptEnd && newEnd > aptStart) {
                    clashingAppointments.push(apt);
                  }
                }

                return clashingAppointments;
              };

              // Helper to format time for display (uses SAST)
              // Note: formatTimeSAST is imported at module level

              if (activeItem.item_type === 'job' && droppedOnUnscheduled) {
                // Job dropped to unscheduled - delete its appointment if it has one

                // Find the appointment child for this job
                const appointmentToDelete = items.find(item =>
                  item.item_type === 'appointment' &&
                  item.parent_id === activeItem.id
                );

                if (appointmentToDelete) {

                  const deleteResponse = await fetch(`/api/v1/work-items/${appointmentToDelete.id}`, {
                    method: 'DELETE',
                    headers,
                  });

                  if (!deleteResponse.ok) {
                    const errorText = await deleteResponse.text();
                    console.error('[Kanban] Failed to delete appointment:', errorText);
                    throw new Error(`Failed to delete appointment: ${errorText}`);
                  }


                  // Update the job to clear assignment and reset status
                  await fetch(`/api/v1/work-items/${activeItem.id}`, {
                    method: 'PUT',
                    headers,
                    body: JSON.stringify({ assigned_to: null, assigned_team: null, status: 'open' })
                  });

                  // Clear assignees
                  const getResponse = await fetch(`/api/v1/work-items/${activeItem.id}/assignees`, {
                    method: 'GET',
                    headers,
                  });

                  if (getResponse.ok) {
                    const getData = await getResponse.json();
                    const existingAssignees = getData.data || [];
                    for (const assignee of existingAssignees) {
                      await fetch(`/api/v1/work-items/${activeItem.id}/assignees/${assignee.user_id}`, {
                        method: 'DELETE',
                        headers,
                      });
                    }
                  }

                  // Refresh to show updated state
                  if (onRefresh) {
                    await onRefresh();
                  }

                  // Don't clear optimistic items here - let the useEffect clear when items prop updates
                  return;
                } else {

                  // Just clear assignment
                  await fetch(`/api/v1/work-items/${activeItem.id}`, {
                    method: 'PUT',
                    headers,
                    body: JSON.stringify({ assigned_to: null, assigned_team: null, status: 'open' })
                  });

                  if (onRefresh) {
                    await onRefresh();
                  }

                  // Don't clear optimistic items here - let the useEffect clear when items prop updates
                  return;
                }
              }

              if (activeItem.item_type === 'job' && !droppedOnUnscheduled && newAssignedTo) {
                // For jobs: Create an appointment as a child
                // Use savedHoveredHour (from ref - most accurate) or fallback to dropHour state
                const startHour = savedHoveredHour?.hour ?? dropHour ?? 9;

                // Check if job is in a completed/closed status - cannot schedule completed jobs
                if (jobStatusBlocksAppointmentCreation(activeItem.status)) {
                  notify.error('Cannot create appointments for completed or closed jobs.');
                  setActiveId(null);
                  setOptimisticItems([]);
                  setDropHour(null);
                  setHoveredHour(null);
                  hoveredHourRef.current = null;
                  isDraggingRef.current = false;
                  return;
                }

                // Check if dropping on a past time slot (SAST timezone)
                // Use the pre-computed values from useMemo for better performance
                const isPastTimeSlot = isTimeSlotInPast(startHour);

                // Silently reject drops on past time slots (no error message)
                if (isPastTimeSlot) {
                  setActiveId(null);
                  setOptimisticItems([]);
                  setDropHour(null);
                  setHoveredHour(null);
                  hoveredHourRef.current = null;
                  isDraggingRef.current = false;
                  return;
                }

                const duration = parseFloat(String(activeItem.estimated_hours || '1')) || 1;
                const scheduledStart = new Date(selectedDate);
                scheduledStart.setHours(startHour, 0, 0, 0); // Use drop position hour in LOCAL time
                const scheduledEnd = new Date(scheduledStart);
                scheduledEnd.setHours(scheduledEnd.getHours() + Math.ceil(duration));

                // CHECK FOR CLASHES before creating the appointment
                const clashResults = newAssignedUserIds.map((userId) => {
                  const conflicts = checkForClashes(userId, scheduledStart, scheduledEnd);
                  if (conflicts.length === 0) return null;
                  const user = assignedUsers.find((candidate) => candidate.id === userId);
                  const userName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email : `User ${userId}`;
                  return { userName, conflicts };
                }).filter((result): result is { userName: string; conflicts: WorkItem[] } => Boolean(result));
                if (clashResults.length > 0) {
                  const clashList = clashResults.map(({ userName, conflicts }) => `${userName}:\n${conflicts.map((apt) =>
                    `• ${apt.item_number || apt.title} (${formatTimeSAST(apt.scheduled_start!)} - ${formatTimeSAST(apt.scheduled_end!)})`
                  ).join('\n')}`).join('\n\n');
                  const targetLabel = assignmentTarget?.label || 'this resource';

                  const confirmed = await confirm({
                    title: 'Appointment Clash Detected',
                    message: `Creating this appointment for ${targetLabel} (${formatTimeSAST(scheduledStart)} - ${formatTimeSAST(scheduledEnd)}) will overlap with existing appointments:\n\n${clashList}\n\nDo you want to create it anyway?`,
                    confirmText: 'Create Anyway',
                    cancelText: 'Cancel',
                    variant: 'warning',
                    onConfirm: () => { },
                  });

                  if (!confirmed) {
                    setOptimisticItems([]);
                    return;
                  }
                }

                // Format as ISO string in SAST to prevent timezone drift
                const scheduledStartISO = formatLocalISOSAST(scheduledStart);
                const scheduledEndISO = formatLocalISOSAST(scheduledEnd);

                openAppointmentSetupModal({
                  activeItem,
                  scheduledStartISO,
                  scheduledEndISO,
                  newAssignedTo,
                  assignedTeamId: newAssignedTeam,
                  assignedUserIds: newAssignedUserIds,
                  duration,
                  sourceView: 'time_allocation',
                });
                return;

              } else if (activeItem.item_type === 'appointment') {
                // Check if dropping on unscheduled section (to delete appointment and unlink from job)
                if (droppedOnUnscheduled) {

                  // Get the parent job ID to update job status after deleting appointment
                  const parentJobId = activeItem.parent_id;

                  // NOTE: Optimistic update already done synchronously before this async function

                  // Delete the appointment (soft delete - sets deleted_at)
                  const deleteResponse = await fetch(`/api/v1/work-items/${activeItem.id}`, {
                    method: 'DELETE',
                    headers,
                  });

                  if (!deleteResponse.ok) {
                    const errorText = await deleteResponse.text();
                    console.error('[Kanban] Failed to delete appointment:', errorText);
                    // Revert optimistic update on failure
                    setOptimisticItems([]);
                    throw new Error(`Failed to delete appointment: ${errorText}`);
                  }


                  // Update the parent job's status back to 'open' but KEEP the assignees
                  if (parentJobId) {

                    await fetch(`/api/v1/work-items/${parentJobId}`, {
                      method: 'PUT',
                      headers,
                      body: JSON.stringify({
                        status: 'open'
                        // Don't clear assigned_to - keep the job assigned to the user
                      })
                    });

                  }

                  // Refresh to sync with server (appointment already hidden via optimistic update)
                  if (onRefresh) {
                    await onRefresh();
                  }

                  // Don't clear optimistic items - let useEffect clear when items prop updates
                  return; // Exit early
                }

                // For appointments: Update assignment and scheduled times
                // Use savedHoveredHour (from ref - most accurate) or fallback to dropHour state
                const startHour = savedHoveredHour?.hour ?? dropHour ?? 9;

                // Check if dropping on a past time slot (SAST timezone)
                // Use the pre-computed values from useMemo for better performance
                const isPastTimeSlot = isTimeSlotInPast(startHour);

                // Silently reject drops on past time slots (no error message)
                // NOTE: We only block drops where the DESTINATION is in the past.
                // Moving appointments FROM the past TO the future is allowed.
                if (isPastTimeSlot) {
                  setActiveId(null);
                  setOptimisticItems([]);
                  setDropHour(null);
                  setHoveredHour(null);
                  hoveredHourRef.current = null;
                  isDraggingRef.current = false;
                  return;
                }

                // Calculate actual duration from existing scheduled times (preserve resized duration)
                let duration = 1; // Default 1 hour
                if (activeItem.scheduled_start && activeItem.scheduled_end) {
                  const existingStart = new Date(activeItem.scheduled_start);
                  const existingEnd = new Date(activeItem.scheduled_end);
                  const durationMs = existingEnd.getTime() - existingStart.getTime();
                  duration = durationMs / (1000 * 60 * 60); // Convert to hours
                  if (duration <= 0) duration = 1; // Fallback to 1 hour if invalid
                } else if (activeItem.estimated_hours) {
                  duration = parseFloat(String(activeItem.estimated_hours)) || 1;
                }

                const scheduledStart = new Date(selectedDate);
                scheduledStart.setHours(startHour, 0, 0, 0);
                const scheduledEnd = new Date(scheduledStart);
                scheduledEnd.setTime(scheduledStart.getTime() + duration * 60 * 60 * 1000); // Preserve exact duration

                // Format as ISO string in SAST to prevent timezone drift
                const scheduledStartISO = formatLocalISOSAST(scheduledStart);
                const scheduledEndISO = formatLocalISOSAST(scheduledEnd);


                // Check for clashes with other appointments (exclude current appointment)
                if (newAssignedTo) {
                  const clashResults = newAssignedUserIds.map((userId) => {
                    const conflicts = checkForClashes(userId, scheduledStart, scheduledEnd, activeItem.id);
                    if (conflicts.length === 0) return null;
                    const user = assignedUsers.find((candidate) => candidate.id === userId);
                    const userName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email : `User ${userId}`;
                    return { userName, conflicts };
                  }).filter((result): result is { userName: string; conflicts: WorkItem[] } => Boolean(result));
                  if (clashResults.length > 0) {
                    const clashList = clashResults.map(({ userName, conflicts }) => `${userName}:\n${conflicts.map((clash) =>
                      `• ${clash.item_number || clash.title}: ${formatTimeSAST(clash.scheduled_start!)} - ${formatTimeSAST(clash.scheduled_end!)}`
                    ).join('\n')}`).join('\n\n');

                    const confirmed = await confirm({
                      title: 'Appointment Clash Detected',
                      message: `Moving this appointment (${formatTimeSAST(scheduledStart)} - ${formatTimeSAST(scheduledEnd)}) will overlap with existing appointments:\n\n${clashList}\n\nDo you want to move it anyway?`,
                      variant: 'warning',
                      confirmText: 'Move Anyway',
                      cancelText: 'Cancel',
                      onConfirm: () => { },
                    });

                    if (!confirmed) {
                      setOptimisticItems([]);
                      return;
                    }
                  }
                }

                // NOTE: Optimistic update already done synchronously before this async function

                const updateResponse = await fetch(`/api/v1/work-items/${activeItem.id}`, {
                  method: 'PUT',
                  headers,
                  body: JSON.stringify({
                    assigned_to: newAssignedTo,
                    assigned_team: newAssignedTeam,
                    assigned_users: newAssignedUserIds,
                    scheduled_start: scheduledStartISO,
                    scheduled_end: scheduledEndISO,
                    status: newAssignedTo ? 'scheduled' : 'pending',
                  })
                });

                if (!updateResponse.ok) {
                  const errorText = await updateResponse.text();
                  console.error('[Kanban] Failed to update appointment:', errorText);
                  throw new Error(`Failed to update appointment: ${errorText}`);
                }

                // IMPORTANT: Also add the new assignee to the parent job if this appointment has a parent
                if (activeItem.parent_id && newAssignedTo) {
                  const previousAssignedUserIds = getItemAssignedUserIds(activeItem);
                  const addedParentUserIds = newAssignedUserIds.filter((userId) => !previousAssignedUserIds.includes(userId));
                  if (addedParentUserIds.length > 0) {
                    // Get parent job's current assignees
                    const parentAssigneesResponse = await fetch(`/api/v1/work-items/${activeItem.parent_id}/assignees`, {
                      method: 'GET',
                      headers,
                    });

                    let parentAssignees: { user_id: number }[] = [];
                    if (parentAssigneesResponse.ok) {
                      const parentData = await parentAssigneesResponse.json();
                      parentAssignees = parentData.data || [];
                    }

                    // Only add if not already assigned to parent job
                    for (const userId of addedParentUserIds) {
                      const isAlreadyOnParent = parentAssignees.some(a => a.user_id === userId);
                      if (!isAlreadyOnParent) {
                        await fetch(`/api/v1/work-items/${activeItem.parent_id}/assignees`, {
                          method: 'POST',
                          headers,
                          body: JSON.stringify({ user_id: userId })
                        });
                      }
                    }
                  }
                }
              }


              if (onRefresh) {
                await onRefresh();
              }

              // Don't clear optimistic items here - let useEffect clear when items prop updates
            } catch (error) {
              console.error('[Kanban] Failed to update time allocation:', error);
              setOptimisticItems([]);
              notify.error(`Failed to schedule item: ${error instanceof Error ? error.message : 'Unknown error'}`);
            }
          }; // End of performTimeAllocationUpdate async function

          // Fire and forget - return immediately so dnd-kit can clear its state
          performTimeAllocationUpdate();
          return;
        }
      }
    }

    // CRITICAL: Clear all drag state at the end (fallback for cases that don't return early)
    setActiveId(null);
    setDropHour(null);
    setHoveredHour(null);
    hoveredHourRef.current = null;
    isDraggingRef.current = false;
    dragDomCacheRef.current = null;
  };

  // Handle item selection
  const handleItemSelect = (itemId: string) => {
    setSelectedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleResetVisibleHours = useCallback(() => {
    if (viewMode === 'resource_schedule') {
      const span = Math.max(1, resourceScheduleConfig.endHour - resourceScheduleConfig.startHour + 1);
      const isDynamicMode = boardPreferenceState.resourceScheduleTimelineMode !== 'static';
      if (getDateStrSAST(selectedDate) === getTodayStrSAST() && isDynamicMode) {
        // Dynamic mode: align visible window to current time on today's date
        setResourceScheduleVisibleStartHour(alignRollingScheduleVisibleStartToNow(getCurrentHourSAST(), span));
      } else {
        // Static mode (or non-today in dynamic mode): always use configured start hour
        setResourceScheduleVisibleStartHour(resourceScheduleConfig.startHour);
      }
      return;
    }

    if (getDateStrSAST(selectedDate) === getTodayStrSAST()) {
      setTimeAllocationVisibleStartHour(
        alignRollingScheduleVisibleStartToNow(getCurrentHourSAST(), timeAllocationVisibleHoursCount),
      );
    } else {
      setTimeAllocationVisibleStartHour(getDefaultTimeAllocationStartHour());
    }
  }, [
    boardPreferenceState.resourceScheduleTimelineMode,
    getDefaultTimeAllocationStartHour,
    resourceScheduleConfig.endHour,
    resourceScheduleConfig.startHour,
    selectedDate,
    timeAllocationVisibleHoursCount,
    viewMode,
  ]);

  // Filtered columns based on visibility, then sorted by custom order
  const visibleColumnsList = useMemo(() => {
    const visible = activeColumns.filter(col => visibleColumns.has(col.key));

    // If we have a custom column order, sort by that order
    if (columnOrder.length > 0) {
      return [...visible].sort((a, b) => {
        const indexA = columnOrder.indexOf(a.key);
        const indexB = columnOrder.indexOf(b.key);
        // Columns not in the order go to the end
        const orderA = indexA === -1 ? 999 : indexA;
        const orderB = indexB === -1 ? 999 : indexB;
        return orderA - orderB;
      });
    }

    return visible;
  }, [activeColumns, visibleColumns, columnOrder]);

  // When the filter bar has active filters, hide columns that have no matching items —
  // mirrors visibleTimelineResources / visibleTimeAllocationColumns used by the schedule views.
  // The unassigned column is always kept so unscheduled items remain reachable.
  // When the assignee filter is active, show exactly the selected users' columns
  // (even if they have no matching items), matching the schedule behaviour.
  const filteredColumnsList = useMemo<WorkflowState[]>(() => {
    if (!filtersActive) {
      return visibleColumnsList;
    }

    const selectedAssigneeUserIds = activeAssigneeUserIds && activeAssigneeUserIds.length > 0
      ? new Set(activeAssigneeUserIds)
      : null;

    return visibleColumnsList.filter((column) => {
      // Always keep the unassigned column
      if (column.key === 'unassigned' || column.key === 'open') {
        return true;
      }

      // Assignee filter: show exactly the selected users (even if they have no matching items)
      if (selectedAssigneeUserIds && column.key.startsWith('user-')) {
        const userId = Number(column.key.replace('user-', ''));
        return selectedAssigneeUserIds.has(userId);
      }

      // Other filters: show only columns that have at least one matching item
      const columnItems = itemsByColumn[column.key];
      return Boolean(columnItems && columnItems.length > 0);
    });
  }, [visibleColumnsList, filtersActive, activeAssigneeUserIds, itemsByColumn]);

  // Split columns: unassigned always first and sticky, others can be reordered
  const unassignedColumn = useMemo(() => {
    return filteredColumnsList.find(col => col.key === 'unassigned' || col.key === 'open');
  }, [filteredColumnsList]);

  const otherColumns = useMemo(() => {
    return filteredColumnsList.filter(col => col.key !== 'unassigned' && col.key !== 'open');
  }, [filteredColumnsList]);

  const otherColumnIds = useMemo(() => {
    return otherColumns.map((column) => `column-${column.key}`);
  }, [otherColumns]);

  const pendingAppointmentAssignedUserIds = useMemo(() => {
    if (pendingAppointmentSetup?.assignedUserIds) {
      return pendingAppointmentSetup.assignedUserIds;
    }

    if (pendingAppointmentSetup?.newAssignedTo != null) {
      return [pendingAppointmentSetup.newAssignedTo];
    }

    return [] as Array<number | string | null | undefined>;
  }, [pendingAppointmentSetup]);

  // Active item for drag overlay
  const activeItem = activeId ? displayItems.find(i => i.id === activeId) : null;

  // Get the dragging column for overlay
  const draggingColumn = draggingColumnId
    ? activeColumns.find(col => col.key === draggingColumnId.replace('column-', ''))
    : null;

  // ============================================================================
  // RESOURCE SCHEDULE TIMELINE (HORIZONTAL) - Helpers
  // ============================================================================

  // Map assignedUsers → TimelineResource[] for ResourceScheduleTimeline
  const timelineResources: TimelineResource[] = useMemo(() => {
    const resources: TimelineResource[] = [];

    operationsTeams
      .filter((team) => visibleResourceScheduleColumns.some((column) => column.key === `team-${team.id}`))
      .forEach((team) => {
        resources.push({
          id: team.id,
          name: team.team_name,
          role: 'Operations Team',
          department: team.description,
          resourceType: 'team',
          teamId: team.id,
          memberUserIds: team.memberUserIds,
          leaderUserId: team.leader?.user_id || null,
          profileUserId: null,
        });
      });

    assignedUsers
      .filter(u => visibleResourceScheduleColumns.some((column) => column.key === `user-${u.id}`))
      .filter(u => u.id != null)
      .forEach((u) => {
        resources.push({
          id: u.id,
          name: `${[u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || `User #${u.id}`}${u.isInactive === true || u.role === 'inactive' ? ' (Inactive)' : ''}`,
          email: u.email,
          role: u.role,
          resourceType: 'user',
          userId: u.id,
          profileUserId: u.id,
        });
      });

    return resources;
  }, [assignedUsers, operationsTeams, visibleResourceScheduleColumns]);

  // When the filter bar has active filters, hide resource rows that have no matching items.
  // Mirrors doesItemMatchResource() in ResourceScheduleTimeline (includeMemberAssignmentsForTeam: false):
  //   - team rows only count items assigned to that team (assigned_team === teamId)
  //   - user rows only count items without a team whose assigned user ids include the resource user id
  // When the assignee filter is active, user rows are limited to the selected assignees
  // (not co-assignees of their items) so filtering by "Michelle" shows only Michelle's row.
  // With no active filters, all board-preference-visible resources remain (preserves default behaviour).
  const visibleTimelineResources = useMemo(() => {
    if (!filtersActive) {
      return timelineResources;
    }

    const matchedTeamIds = new Set<string>();
    const matchedUserIds = new Set<number>();

    for (const item of displayItems) {
      const teamId = getNormalizedAssignedTeamId(item);
      if (teamId) {
        matchedTeamIds.add(teamId);
      } else {
        for (const userId of getParsedAssignedUserIds(item)) {
          matchedUserIds.add(userId);
        }
      }
    }

    const selectedAssigneeUserIds = activeAssigneeUserIds && activeAssigneeUserIds.length > 0
      ? new Set(activeAssigneeUserIds)
      : null;

    return timelineResources.filter((resource) => {
      if (resource.resourceType === 'team') {
        const teamId = resource.teamId || (typeof resource.id === 'string' ? resource.id : null);
        return Boolean(teamId && matchedTeamIds.has(teamId));
      }

      const userId = typeof resource.userId === 'number'
        ? resource.userId
        : (typeof resource.id === 'number' ? resource.id : null);
      if (!userId) {
        return false;
      }

      // Assignee filter: show exactly the selected users (even if they have no matching items).
      if (selectedAssigneeUserIds) {
        return selectedAssigneeUserIds.has(userId);
      }

      // Other filters: show only users that have at least one matching item.
      return matchedUserIds.has(userId);
    });
  }, [timelineResources, filtersActive, activeAssigneeUserIds, displayItems]);

  const resourceScheduleUnscheduledCount = useMemo(() => {
    if (SERVICE_OPERATIONS_CONFIG.unscheduledSidebarMode === 'jobs_for_selected_date') {
      return getUnscheduledJobsForSelectedDate({
        items: displayItems,
        selectedDate,
      }).length;
    }

    return getGenericUnscheduledScheduleItems({
      items: displayItems,
    }).length;
  }, [displayItems, selectedDate]);

  const boardToolbarModel = useMemo<BoardToolbarModel | null>(() => {
    if (!hideToolbar) {
      return null;
    }

    const isScheduleView = viewMode === 'time_allocation' || viewMode === 'resource_schedule';
    const resolvedZoomLabel = viewMode === 'resource_schedule' ? timelineZoomLabel : zoomLabel;
    const resolvedCanZoomIn = viewMode === 'resource_schedule' ? timelineCanZoomIn : canZoomIn;
    const resolvedCanZoomOut = viewMode === 'resource_schedule' ? timelineCanZoomOut : canZoomOut;
    const visibleColumnCount = columnSettingsColumns.filter((column) => visibleColumns.has(column.key)).length;
    const scheduleWindowStartHour = viewMode === 'resource_schedule'
      ? resourceScheduleVisibleStartHour
      : timeAllocationVisibleStartHour;
    const scheduleWindowHourCount = viewMode === 'resource_schedule'
      ? Math.max(1, resourceScheduleConfig.endHour - resourceScheduleConfig.startHour + 1)
      : timeAllocationVisibleHoursCount;
    const scheduleResetStartHour = viewMode === 'resource_schedule'
      ? resourceScheduleConfig.startHour
      : getDefaultTimeAllocationStartHour();

    return {
      showZoomControls: true,
      zoomLabel: resolvedZoomLabel,
      canZoomIn: resolvedCanZoomIn,
      canZoomOut: resolvedCanZoomOut,
      onZoomIn: () => {
        if (viewMode === 'resource_schedule') {
          timelineRef.current?.zoomIn();
          return;
        }
        zoomIn();
      },
      onZoomOut: () => {
        if (viewMode === 'resource_schedule') {
          timelineRef.current?.zoomOut();
          return;
        }
        zoomOut();
      },
      onZoomReset: () => {
        if (viewMode === 'resource_schedule') {
          timelineRef.current?.resetZoom();
          return;
        }
        resetZoom();
      },
      showDateControls: isScheduleView,
      selectedDateLabel: format(selectedDate, 'EEEE, MMM d, yyyy'),
      selectedDateCompactLabel: format(selectedDate, 'MMM d'),
      isToday: isSameDay(selectedDate, new Date()),
      onPreviousDate: isScheduleView ? () => setSelectedDate(subDays(selectedDate, 1)) : undefined,
      onToday: isScheduleView ? () => setSelectedDate(new Date()) : undefined,
      onNextDate: isScheduleView ? () => setSelectedDate(addDays(selectedDate, 1)) : undefined,
      showTimeWindowControls: isScheduleView,
      canRollEarlier: isScheduleView ? scheduleWindowStartHour > -12 : undefined,
      canRollLater: isScheduleView ? scheduleWindowStartHour + scheduleWindowHourCount < 36 : undefined,
      canResetTimeWindow: isScheduleView ? scheduleWindowStartHour !== scheduleResetStartHour : undefined,
      onRollEarlier: isScheduleView
        ? () => (viewMode === 'resource_schedule' ? handleResourceScheduleRollHours('up') : handleTimeAllocationRollHours('up'))
        : undefined,
      onRollLater: isScheduleView
        ? () => (viewMode === 'resource_schedule' ? handleResourceScheduleRollHours('down') : handleTimeAllocationRollHours('down'))
        : undefined,
      onResetTimeWindow: isScheduleView ? handleResetVisibleHours : undefined,
      showSidebarToggle: viewMode === 'resource_schedule',
      sidebarVisible: viewMode === 'resource_schedule' ? resourceScheduleSidebarVisible : undefined,
      sidebarCount: viewMode === 'resource_schedule' ? resourceScheduleUnscheduledCount : undefined,
      sidebarLabel: viewMode === 'resource_schedule' ? 'Unscheduled' : undefined,
      onToggleSidebar: viewMode === 'resource_schedule'
        ? () => setResourceScheduleSidebarVisible((prev) => !prev)
        : undefined,
      showColumnSettings: false,
      columnSettingsOpen: false,
      visibleColumnCount,
      totalColumnCount: columnSettingsColumns.length,
      columnOptions: [],
    };
  }, [
    hideToolbar,
    viewMode,
    timelineZoomLabel,
    zoomLabel,
    timelineCanZoomIn,
    canZoomIn,
    timelineCanZoomOut,
    canZoomOut,
    selectedDate,
    timeAllocationVisibleStartHour,
    timeAllocationVisibleHoursCount,
    resourceScheduleConfig.endHour,
    resourceScheduleConfig.startHour,
    resourceScheduleVisibleStartHour,
    getDefaultTimeAllocationStartHour,
    handleResetVisibleHours,
    handleResourceScheduleRollHours,
    handleTimeAllocationRollHours,
    resourceScheduleSidebarVisible,
    resourceScheduleUnscheduledCount,
    columnSettingsColumns,
    visibleColumns,
    zoomIn,
    zoomOut,
    resetZoom,
  ]);

  const boardToolbarSignature = useMemo(() => {
    if (!boardToolbarModel) {
      return null;
    }

    return JSON.stringify({
      showZoomControls: boardToolbarModel.showZoomControls,
      zoomLabel: boardToolbarModel.zoomLabel,
      canZoomIn: boardToolbarModel.canZoomIn,
      canZoomOut: boardToolbarModel.canZoomOut,
      showDateControls: boardToolbarModel.showDateControls,
      selectedDateLabel: boardToolbarModel.selectedDateLabel,
      selectedDateCompactLabel: boardToolbarModel.selectedDateCompactLabel,
      isToday: boardToolbarModel.isToday,
      showTimeWindowControls: boardToolbarModel.showTimeWindowControls,
      canRollEarlier: boardToolbarModel.canRollEarlier,
      canRollLater: boardToolbarModel.canRollLater,
      canResetTimeWindow: boardToolbarModel.canResetTimeWindow,
      showSidebarToggle: boardToolbarModel.showSidebarToggle,
      sidebarVisible: boardToolbarModel.sidebarVisible,
      sidebarCount: boardToolbarModel.sidebarCount,
      sidebarLabel: boardToolbarModel.sidebarLabel,
      showColumnSettings: boardToolbarModel.showColumnSettings,
      columnSettingsOpen: boardToolbarModel.columnSettingsOpen,
      visibleColumnCount: boardToolbarModel.visibleColumnCount,
      totalColumnCount: boardToolbarModel.totalColumnCount,
      columnOptions: boardToolbarModel.columnOptions.map((column) => ({
        key: column.key,
        label: column.label,
        count: column.count,
        visible: column.visible,
        disabled: column.disabled,
        colorClassName: column.colorClassName,
      })),
    });
  }, [boardToolbarModel]);

  const lastPublishedToolbarSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    if (!onToolbarModelChange) {
      lastPublishedToolbarSignatureRef.current = null;
      return;
    }

    if (!boardToolbarModel) {
      if (lastPublishedToolbarSignatureRef.current !== null) {
        lastPublishedToolbarSignatureRef.current = null;
        onToolbarModelChange(null);
      }
      return;
    }

    if (boardToolbarSignature === lastPublishedToolbarSignatureRef.current) {
      return;
    }

    lastPublishedToolbarSignatureRef.current = boardToolbarSignature;
    onToolbarModelChange(boardToolbarModel);
  }, [boardToolbarModel, boardToolbarSignature, onToolbarModelChange]);

  // Handler for scheduling items from the Resource Schedule Timeline
  const handleTimelineScheduleItem = useCallback(async (
    itemId: string,
    resourceId: number | string,
    scheduledStart: string,
    scheduledEnd: string
  ): Promise<boolean> => {
    const timelineItem = items.find(item => item.id === itemId) || optimisticItems.find(item => item.id === itemId);
    const timelineResource = timelineResources.find((resource) => resource.id === resourceId);
    const resourceAssignedUserIds = typeof resourceId === 'number'
      ? [resourceId]
      : timelineResource?.memberUserIds || [];
    const isTeamResource = typeof resourceId === 'string';
    const assignedTeamId = isTeamResource ? resourceId : null;
    const baseAssignedUserIds = timelineItem && !getNormalizedAssignedTeamId(timelineItem)
      ? getItemAssignedUserIds(timelineItem)
      : [];
    const assignedUserIds = timelineItem?.item_type === 'appointment' && !isTeamResource
      ? Array.from(new Set([
        ...baseAssignedUserIds,
        ...resourceAssignedUserIds,
      ]))
      : resourceAssignedUserIds;
    const assignedTo = isTeamResource
      ? timelineResource?.leaderUserId || resourceAssignedUserIds[0] || null
      : timelineItem?.item_type === 'appointment'
        ? getPrimaryAssignedUserId(timelineItem, assignedUserIds) ?? resourceAssignedUserIds[0] ?? null
        : resourceAssignedUserIds[0] || null;

    if (!assignedTo) {
      notify.error(`No team leader or team members are configured for ${timelineResource?.name || 'this team'}.`);
      if (onRefresh) await onRefresh();
      return false;
    }

    if (timelineItem?.item_type === 'job') {
      if (jobStatusBlocksAppointmentCreation(timelineItem.status)) {
        notify.error('Cannot create appointments for completed or closed jobs.');
        if (onRefresh) await onRefresh();
        return false;
      }

      const scheduledStartDate = new Date(scheduledStart);
      const scheduledEndDate = new Date(scheduledEnd);
      const clashResults = assignedUserIds
        .map((userId) => {
          const conflicts = checkForClashes(userId, scheduledStartDate, scheduledEndDate);
          if (conflicts.length === 0) return null;
          const user = assignedUsers.find((candidate) => candidate.id === userId);
          const userName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email : `User ${userId}`;
          return { userName, conflicts };
        })
        .filter((result): result is { userName: string; conflicts: WorkItem[] } => Boolean(result));

      if (clashResults.length > 0) {
        const clashList = clashResults.map(({ userName, conflicts }) => {
          const details = conflicts.map((apt) =>
            `• ${apt.item_number || apt.title} (${formatTimeSAST(apt.scheduled_start!)} - ${formatTimeSAST(apt.scheduled_end!)})`
          ).join('\n');
          return `${userName}:\n${details}`;
        }).join('\n\n');

        const resourceName = timelineResource?.name || 'this resource';

        const confirmed = await confirm({
          title: 'Appointment Clash Detected',
          message: `Creating this appointment for ${resourceName} (${formatTimeSAST(scheduledStartDate)} - ${formatTimeSAST(scheduledEndDate)}) will overlap with existing appointments:\n\n${clashList}\n\nDo you want to create it anyway?`,
          confirmText: 'Create Anyway',
          cancelText: 'Cancel',
          variant: 'warning',
          onConfirm: () => { },
        });

        if (!confirmed) {
          if (onRefresh) await onRefresh();
          return false;
        }
      }

      openAppointmentSetupModal({
        activeItem: timelineItem,
        scheduledStartISO: scheduledStart,
        scheduledEndISO: scheduledEnd,
        newAssignedTo: assignedTo,
        assignedTeamId,
        assignedUserIds,
        duration: parseFloat(String(timelineItem.estimated_hours || '1')) || 1,
        sourceView: 'resource_schedule',
      });

      if (onRefresh) {
        await onRefresh();
      }

      return true;
    }

    // Delegate to the parent's onUpdateSchedule if available
    if (onUpdateSchedule && !assignedTeamId && timelineItem?.item_type !== 'appointment') {
      try {
        await onUpdateSchedule(itemId, scheduledStart, scheduledEnd, assignedTo);
        if (onRefresh) await onRefresh();
        return true;
      } catch (err) {
        console.error('[KanbanBoard] Timeline schedule update failed:', err);
        return false;
      }
    }
    // Fallback: direct API call (same pattern as time_allocation resize)
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) return false;

      const response = await fetch(`/api/v1/work-items/${itemId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          scheduled_start: scheduledStart,
          scheduled_end: scheduledEnd,
          assigned_to: assignedTo,
          assigned_team: assignedTeamId,
          assigned_users: assignedUserIds
        })
      });

      if (!response.ok) return false;
      if (onRefresh) await onRefresh();

      // Notify parent of schedule update (for schedule↔route sync)
      if (onAppointmentChange) {
        onAppointmentChange({
          action: 'moved',
          appointmentId: itemId,
          date: scheduledStart.split('T')[0],
          assignedTo,
        });
      }

      return true;
    } catch (err) {
      console.error('[KanbanBoard] Timeline schedule save failed:', err);
      return false;
    }
  }, [assignedUsers, checkForClashes, getItemAssignedUserIds, items, onAppointmentChange, onRefresh, onUpdateSchedule, openAppointmentSetupModal, optimisticItems, timelineResources]);

  const toolbarTitle = useMemo(() => {
    if (activeViewLabel && activeViewLabel.trim().length > 0) {
      return activeViewLabel.trim();
    }

    switch (viewMode) {
      case 'assignment':
        return 'Assign Board';
      case 'time_allocation':
        return 'Schedule Vertical';
      case 'resource_schedule':
        return 'Schedule Horizontal';
      default:
        return 'Status Board';
    }
  }, [activeViewLabel, viewMode]);

  const toolbarItemType = useMemo(() => {
    if (focusedItemType && focusedItemType.trim().length > 0) {
      return focusedItemType.trim();
    }

    if (typeof itemType !== 'string') {
      return null;
    }

    return itemType
      .split(',')
      .map((value) => value.trim())
      .find(Boolean) || null;
  }, [focusedItemType, itemType]);

  const toolbarTypeAccent = useMemo(
    () => getWorkItemTypeAccent(toolbarItemType, clientBranding?.primary_color || '#3b82f6'),
    [clientBranding?.primary_color, toolbarItemType],
  );

  const toolbarGroupClassName = WORK_ITEMS_VIEW_TOOLBAR_GROUP_CLASS;
  const toolbarIconButtonClassName = WORK_ITEMS_VIEW_TOOLBAR_ICON_BUTTON_CLASS;
  const boardPreferencesButton = onOpenBoardPreferences ? (
    <button
      type="button"
      onClick={onOpenBoardPreferences}
      className={WORK_ITEMS_VIEW_BOARD_PREFS_BUTTON_CLASS}
      title="Preferences"
    >
      <Settings2 className="h-3.5 w-3.5" />
      <span className="hidden sm:inline">Preferences</span>
      <span className="sm:hidden">Preferences</span>
    </button>
  ) : null;

  const timeAllocationExportButton = viewMode === 'time_allocation' ? (
    <ScheduleExportButton
      items={displayItems}
      resources={timeAllocationExportResources}
      selectedDate={selectedDate}
      includeEmptyResources
      className={WORK_ITEMS_VIEW_BOARD_PREFS_BUTTON_CLASS}
      title="Export schedule"
      dialogDescription="Choose whether to copy the vertical schedule as readable text or structured JSON."
    />
  ) : null;

  if (!areViewPreferencesHydrated) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex flex-1 items-center justify-center overflow-hidden rounded-2xl border border-gray-200/70 bg-white/90 px-6 py-10 shadow-sm dark:border-gray-700/70 dark:bg-gray-900/90">
          <BrandedLoadingSpinner
            size="lg"
            message="Applying board preferences..."
            subMessage="Loading hidden columns and team members before the board appears."
            showSurface
            showProgressBar
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar - Mobile Optimized */}
      {!hideToolbar && viewMode !== 'resource_schedule' && (
        <div className={WORK_ITEMS_VIEW_TOOLBAR_CARD_CLASS}>
          <div className={WORK_ITEMS_VIEW_TOOLBAR_INNER_ROW_CLASS}>
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <WorkItemsTypeAccentBadge
                label={toolbarTitle}
                accent={toolbarTypeAccent}
                className={`${WORK_ITEMS_VIEW_TITLE_PILL_CLASS} max-w-[min(100%,28rem)]`}
                labelClassName="truncate"
              />
              <UserDocsLink docsPath="/total-operations/workflows/service-operations/lifecycle#board-columns-vs-statuses" label="User guide: Status lifecycle" />
              {hiddenResultsNotice ? (
                <div
                  className="inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"
                  title="Some columns are hidden by saved board preferences. Open Preferences to show them again."
                >
                  <EyeOff className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="truncate">{hiddenResultsNotice}</span>
                </div>
              ) : null}
            </div>

            {/* Zoom Controls - Condensed on mobile. Delegates to timeline zoom when in resource_schedule mode */}
            <div className={toolbarGroupClassName}>
              <button
                onClick={zoomOut}
                disabled={!canZoomOut}
                className={`${toolbarIconButtonClassName} ${(!canZoomOut)
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 active:bg-blue-200 dark:active:bg-blue-800'
                  }`}
                title="Zoom Out"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <button
                onClick={resetZoom}
                className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 active:bg-gray-200 dark:active:bg-gray-500 rounded-lg transition-colors touch-manipulation min-w-[44px] sm:min-w-0"
                title="Reset Zoom"
              >
                {isMobile ? zoomLabel.replace('%', '') : zoomLabel}
              </button>
              <button
                onClick={zoomIn}
                disabled={!canZoomIn}
                className={`${toolbarIconButtonClassName} ${(!canZoomIn)
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 active:bg-blue-200 dark:active:bg-blue-800'
                  }`}
                title="Zoom In"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>

            {/* Date — immediately after zoom (matches ResourceScheduleTimeline order) */}
            {viewMode === 'time_allocation' && (
              <div className={toolbarGroupClassName}>
                <button
                  onClick={() => setSelectedDate(subDays(selectedDate, 1))}
                  className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600 transition-colors touch-manipulation"
                  title="Previous Day"
                >
                  <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                </button>
                <button
                  onClick={() => setSelectedDate(new Date())}
                  className="px-2 py-1 text-xs font-medium bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 rounded hover:bg-orange-200 dark:hover:bg-orange-900 transition-colors touch-manipulation"
                >
                  Today
                </button>
                <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 text-center whitespace-nowrap">
                  {isMobile ? format(selectedDate, 'MMM d') : format(selectedDate, 'EEEE, MMM d, yyyy')}
                </span>
                <button
                  onClick={() => setSelectedDate(addDays(selectedDate, 1))}
                  className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600 transition-colors touch-manipulation"
                  title="Next Day"
                >
                  <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                </button>
              </div>
            )}

            {/* View Mode Toggle - Pill style on mobile for better UX */}
            {!currentUserId && !hideBoardViewToggle && (
              <div className="flex flex-wrap items-center gap-0.5 rounded-full border border-gray-200 bg-gray-100 p-0.5 dark:border-gray-600 dark:bg-gray-700 flex-shrink-0">
                <button
                  onClick={() => setViewMode('status')}
                  className={`px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-md transition-all flex items-center gap-1 sm:gap-2 touch-manipulation ${viewMode === 'status'
                    ? 'bg-white dark:bg-gray-600 text-blue-700 dark:text-blue-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                  title="Group by Status"
                >
                  <ListTodo className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="hidden xs:inline sm:inline">Status</span>
                </button>
                <button
                  onClick={() => setViewMode('assignment')}
                  disabled={loadingUsers}
                  className={`px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-md transition-all flex items-center gap-1 sm:gap-2 touch-manipulation ${viewMode === 'assignment'
                    ? 'bg-white dark:bg-gray-600 text-purple-700 dark:text-purple-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-200'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  title="Group by Assignment"
                >
                  <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="hidden xs:inline sm:inline">{loadingUsers ? '...' : 'Assign'}</span>
                </button>
                {/* Schedule (Vertical) - Only show if enabled */}
                {enableTimeAllocation && (
                  <button
                    onClick={() => setViewMode('time_allocation')}
                    disabled={loadingUsers}
                    className={`px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-md transition-all flex items-center gap-1 sm:gap-2 touch-manipulation ${viewMode === 'time_allocation'
                      ? 'bg-white dark:bg-gray-600 text-orange-700 dark:text-orange-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-200'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    title="Vertical Schedule — columns per person, time flows top-to-bottom"
                  >
                    <CalendarClock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="hidden xs:inline sm:inline">{loadingUsers ? '...' : 'Schedule ↕'}</span>
                  </button>
                )}
                {/* Schedule (Horizontal / Resource Timeline) - Only show if enabled */}
                {enableTimeAllocation && (
                  <button
                    onClick={() => setViewMode('resource_schedule')}
                    disabled={loadingUsers}
                    className="px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-md transition-all flex items-center gap-1 sm:gap-2 touch-manipulation text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Horizontal Schedule — rows per person, time flows left-to-right"
                  >
                    <Route className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="hidden xs:inline sm:inline">{loadingUsers ? '...' : 'Timeline ↔'}</span>
                  </button>
                )}
              </div>
            )}

            {/* Selected Items - Compact on mobile */}
            {selectedItems.length > 0 && (
              <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg text-xs sm:text-sm font-medium flex-shrink-0">
                <CheckSquare className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>{selectedItems.length}</span>
                <span className="hidden sm:inline">selected</span>
              </div>
            )}

            {(timeAllocationExportButton || boardPreferencesButton) && (
              <div className="ml-auto flex flex-shrink-0 items-center gap-2">
                {timeAllocationExportButton}
                {boardPreferencesButton}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Kanban Board with Zoom Container - Status and Assignment views */}
      {(viewMode === 'status' || viewMode === 'assignment') && (
        <div
          ref={containerRef}
          className={`flex-1 min-h-0 flex flex-col overflow-hidden ${presetConfig?.board?.background || ''} ${presetConfig?.board?.borderRadius || ''}`}
        >
          {/* DndContext must be outside of any zoom/transform to work correctly */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            {/* Zoom wrapper using CSS zoom property */}
            <div
              style={{
                zoom: zoomScale,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minWidth: '100%',
                minHeight: 0,
                overflow: 'hidden',
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
            >
              {/* Top Scrollbar - Now inside zoom container */}
              <div
                ref={topScrollRef}
                onScroll={handleTopScroll}
                className={`overflow-x-scroll overflow-y-hidden mb-2 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 ${isMobile ? 'scrollbar-visible' : 'kanban-scrollbar'}`}
                style={{
                  height: isMobile ? '24px' : '20px',
                  width: '100%',
                  // Force scrollbar visibility on mobile
                  ...(isMobile ? {
                    WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#9CA3AF #E5E7EB'
                  } : {})
                }}
              >
                <div
                  ref={scrollContentRef}
                  style={{
                    height: '1px',
                    // Match the exact width of the scrollable columns container
                    // Calculate total width: unassigned column (if exists) + all other columns
                    width: `${(unassignedColumn ? (
                      zoomLevel === 'compact' ? 240 :
                        zoomLevel === 'expanded' ? 336 :
                          304
                    ) : 0) + otherColumns.length * (
                      zoomLevel === 'compact' ? 240 :
                        zoomLevel === 'expanded' ? 336 :
                          304
                    )}px`,
                    minWidth: '100%' // Ensure minimum width to show scrollbar
                  }}
                />
              </div>

              <div className="flex-1 flex min-h-0 overflow-hidden">
                {/* Show loading state when switching to assignment view and users are loading */}
                {viewMode === 'assignment' && loadingUsers ? (
                  <CardLoadingRow
                    label="Loading team members..."
                    primaryColor="#7c3aed"
                    className="w-full py-12"
                  />
                ) : (
                  <>
                    {/* Sticky Unassigned Column - Only on desktop, not mobile */}
                    {!isMobile && unassignedColumn && (
                      <div className="h-full flex-shrink-0 min-h-0 pr-4">
                        <KanbanColumn
                          column={unassignedColumn}
                          items={itemsByColumn[unassignedColumn.key] || []}
                          selectedItems={selectedItems}
                          onItemSelect={handleItemSelect}
                          onViewItem={onViewItem}
                          onUpdateStatus={onUpdateStatus}
                          getTemplate={getTemplate}
                          zoomLevel={zoomLevel}
                          viewMode={viewMode}
                          assignedUsers={assignedUsers}
                          operationsTeamsById={operationsTeamsById}
                          activeId={activeId}
                          isOver={overColumnId === unassignedColumn.key}
                          dragHandleProps={null}
                          isDraggingColumn={false}
                          canAcceptDrops={viewMode !== 'status' || canManuallyTargetWorkflowStatusKey(unassignedColumn.key)}
                          presetConfig={presetConfig}
                          onOpenTeamMember={onOpenTeamMember}
                        />
                      </div>
                    )}

                    {/* Scrollable Columns Container */}
                    <div
                      ref={bottomScrollRef}
                      onScroll={handleBottomScroll}
                      className={`flex-1 min-h-0 overflow-x-auto overflow-y-hidden ${isMobile ? 'scrollbar-visible' : 'kanban-scrollbar'
                        }`}
                      style={{
                        cursor: isCtrlDragging ? 'grabbing' : 'default',
                        WebkitOverflowScrolling: 'touch', // Momentum scrolling on iOS
                        // Force scrollbar visibility on mobile
                        ...(isMobile ? {
                          scrollbarWidth: 'thin',
                          scrollbarColor: '#9CA3AF #E5E7EB',
                          paddingBottom: '12px' // Extra padding for scrollbar
                        } : {})
                      }}
                      title={isMobile ? "Use arrows or swipe to view columns" : "Ctrl+drag to scroll horizontally"}
                    >
                      <div className="flex h-full min-h-0 items-stretch gap-4" style={{ minWidth: 'max-content' }}>
                        {/* On mobile, render unassigned column inside scrollable area */}
                        {isMobile && unassignedColumn && (
                          <KanbanColumn
                            column={unassignedColumn}
                            items={itemsByColumn[unassignedColumn.key] || []}
                            selectedItems={selectedItems}
                            onItemSelect={handleItemSelect}
                            onViewItem={onViewItem}
                            onUpdateStatus={onUpdateStatus}
                            getTemplate={getTemplate}
                            zoomLevel={zoomLevel}
                            viewMode={viewMode}
                            assignedUsers={assignedUsers}
                            operationsTeamsById={operationsTeamsById}
                            activeId={activeId}
                            isOver={overColumnId === unassignedColumn.key}
                            dragHandleProps={null}
                            isDraggingColumn={false}
                            canAcceptDrops={viewMode !== 'status' || canManuallyTargetWorkflowStatusKey(unassignedColumn.key)}
                            presetConfig={presetConfig}
                            onOpenTeamMember={onOpenTeamMember}
                          />
                        )}
                        {/* Render other columns - wrapped in SortableContext for column reordering */}
                        <SortableContext
                          items={otherColumnIds}
                          strategy={horizontalListSortingStrategy}
                        >
                          {otherColumns.map((column) => (
                            <SortableKanbanColumn
                              key={column.key}
                              column={column}
                              items={itemsByColumn[column.key] || []}
                              selectedItems={selectedItems}
                              onItemSelect={handleItemSelect}
                              onViewItem={onViewItem}
                              onUpdateStatus={onUpdateStatus}
                              getTemplate={getTemplate}
                              zoomLevel={zoomLevel}
                              viewMode={viewMode}
                              assignedUsers={assignedUsers}
                              operationsTeamsById={operationsTeamsById}
                              activeId={activeId}
                              isOver={overColumnId === column.key}
                              isDraggingColumn={draggingColumnId === `column-${column.key}`}
                              canAcceptDrops={viewMode !== 'status' || canManuallyTargetWorkflowStatusKey(column.key)}
                              presetConfig={presetConfig}
                              onOpenTeamMember={onOpenTeamMember}
                            />
                          ))}
                        </SortableContext>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Drag Overlay - OUTSIDE the scaled container for proper positioning */}
            <EnhancedDragGhost
              activeItem={activeItem || null}
              isDragging={!!activeId}
            />

            {/* Column Drag Overlay */}
            <DragOverlay>
              {draggingColumn && (
                <div className="opacity-80 rotate-3">
                  <KanbanColumn
                    column={draggingColumn}
                    items={itemsByColumn[draggingColumn.key] || []}
                    selectedItems={[]}
                    onItemSelect={() => { }}
                    onViewItem={() => { }}
                    onUpdateStatus={async () => { }}
                    getTemplate={getTemplate}
                    zoomLevel={zoomLevel}
                    viewMode={viewMode}
                    assignedUsers={assignedUsers}
                    operationsTeamsById={operationsTeamsById}
                    activeId={null}
                    isOver={false}
                    canAcceptDrops={viewMode !== 'status' || canManuallyTargetWorkflowStatusKey(draggingColumn.key)}
                    presetConfig={presetConfig}
                  />
                </div>
              )}
            </DragOverlay>
          </DndContext>
        </div>
      )}

      {/* Time Allocation View - Same columns as Assignment but with hourly time slots */}
      {viewMode === 'time_allocation' && (
        <div ref={containerRef} className={`flex-1 flex flex-col overflow-hidden ${presetConfig?.board?.background || ''} ${presetConfig?.board?.borderRadius || ''}`}>
          {/* DndContext for drag and drop - SAME as Status/Assignment */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            {/* Zoom wrapper using CSS zoom property */}
            <div
              style={{
                zoom: zoomScale,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                minWidth: '100%',
                minHeight: 0,
                overflow: 'hidden',
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
            >
              {/* Top Scrollbar - Same as Status/Assignment */}
              <div
                ref={topScrollRef}
                onScroll={handleTopScroll}
                className={`overflow-x-scroll overflow-y-hidden mb-2 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 ${isMobile ? 'scrollbar-visible' : ''}`}
                style={{
                  height: isMobile ? '24px' : '20px',
                  width: '100%',
                  ...(isMobile ? {
                    WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#9CA3AF #E5E7EB'
                  } : {})
                }}
              >
                <div
                  style={{
                    height: '1px',
                    width: visibleTimeAllocationColumns.filter(col => col.key !== 'unassigned').length > 0
                      ? `${(visibleTimeAllocationColumns.find(col => col.key === 'unassigned') ? 304 : 0) + visibleTimeAllocationColumns.filter(col => col.key !== 'unassigned').length * (
                        zoomLevel === 'compact' ? 240 :
                          zoomLevel === 'expanded' ? 336 :
                            304
                      )}px`
                      : '100%'
                  }}
                />
              </div>

              <div
                className="flex-1 overflow-hidden min-h-0"
              >
                <TimeAllocationView
                  columns={visibleTimeAllocationColumns}
                  itemsByColumn={itemsByColumn}
                  selectedDate={selectedDate}
                  onViewItem={onViewItem}
                  onRefresh={onRefresh}
                  getTemplate={getTemplate}
                  loadingUsers={loadingUsers}
                  zoomLevel={zoomLevel}
                  scrollRef={bottomScrollRef}
                  onScroll={handleBottomScroll}
                  columnOrder={columnOrder}
                  hoveredHour={hoveredHour}
                  checkForClashes={checkForClashes}
                  onOpenAppointmentAssignees={(item) => setAppointmentAssigneesItemId(item.id)}
                  visibleStartHour={timeAllocationVisibleStartHour}
                  visibleHoursCount={timeAllocationVisibleHoursCount}
                  defaultVisibleStartHour={getDefaultTimeAllocationStartHour()}
                  onRollHours={handleTimeAllocationRollHours}
                  onSetVisibleStartHour={setTimeAllocationVisibleStartHour}
                  isTimeSlotInPast={isTimeSlotInPast}
                  userLeaveData={userLeaveData}
                  isMobile={isMobile}
                  presetConfig={presetConfig}
                  assignedUsers={assignedUsers}
                  onOpenTeamMember={onOpenTeamMember}
                  allItems={displayItems}
                  hideTimeWindowControls={hideToolbar}
                />
              </div>
            </div>

            {/* Drag Overlay - SAME as Status/Assignment */}
            <EnhancedDragGhost
              activeItem={activeItem || null}
              isDragging={!!activeId}
            />
          </DndContext>
        </div>
      )}

      {/* Resource Schedule Timeline - Horizontal schedule with resource rows and time columns */}
      {viewMode === 'resource_schedule' && (
        <div className={`flex-1 min-h-0 flex flex-col overflow-hidden ${presetConfig?.board?.borderRadius || ''}`}>
          <div className="flex-1 min-h-0 overflow-hidden">
            <ResourceScheduleTimeline
              ref={timelineRef}
              items={displayItems}
              resources={visibleTimelineResources}
              templates={templates}
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
              onViewItem={(item) => onViewItem(item.id)}
              onScheduleItem={handleTimelineScheduleItem}
              onRefresh={onRefresh}
              onOpenAppointmentAssignees={(item) => setAppointmentAssigneesItemId(item.id)}
              config={resourceScheduleConfig}
              stylePreset={stylePreset}
              hideBackButton
              hideHeader={hideToolbar}
              headerLabel={toolbarTitle}
              activeItemType={toolbarItemType}
              hideTimelineControls={hideToolbar}
              primaryColor={clientBranding?.primary_color || '#3b82f6'}
              timelineZoom={resourceScheduleTimelineZoom}
              onTimelineZoomChange={setResourceScheduleTimelineZoom}
              visibleStartHour={resourceScheduleVisibleStartHour}
              onVisibleStartHourChange={setResourceScheduleVisibleStartHour}
              timelineMode={boardPreferenceState.resourceScheduleTimelineMode}
              showSidebar={resourceScheduleSidebarVisible}
              onShowSidebarChange={setResourceScheduleSidebarVisible}
              onOpenBoardPreferences={onOpenBoardPreferences}
              hiddenResultsNotice={hiddenResultsNotice}
              onOpenTeamMember={onOpenTeamMember}
            />
          </div>
        </div>
      )}

      {/* NEW: Add Column Modal */}
      <AddColumnModal
        isOpen={showAddColumn}
        onClose={() => setShowAddColumn(false)}
        onAdd={async (columnData) => {
          // Generate unique key from label
          const key = columnData.label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

          try {
            const token = getAuthToken();
            if (!token) {
              throw new Error('Not authenticated');
            }

            // When using strict workflow states (from dynamic config),
            // save directly to work_item_number_sequences.config.custom_statuses
            // via /api/v1/job-config — this is the same store IndustrySolutionSettings uses.
            if (useStrictWorkflowStates && workflowStates && workflowStates.length > 0) {
              const updatedStatuses = [
                ...workflowStates.map((ws) => {
                  const extendedState = ws as ExtendedWorkflowState;

                  return ({
                    key: ws.key,
                    label: ws.label,
                    color: ws.color,
                    description: ws.description,
                    ...(extendedState.isStart ? { isStart: true } : {}),
                    ...(extendedState.isEnd ? { isEnd: true } : {}),
                  });
                }),
                {
                  key,
                  label: columnData.label,
                  color: columnData.color,
                  description: columnData.description || '',
                }
              ];

              // Determine the item type to save for
              const saveItemType = focusedItemType || (typeof itemType === 'string'
                ? itemType.split(',')[0].trim()
                : 'job');

              const response = await fetch('/api/v1/job-config', {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  item_type: saveItemType,
                  workflow_context: workflowContext || undefined,
                  workflow_statuses: updatedStatuses,
                })
              });

              if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to save workflow status');
              }

              // Also save to work_item_number_sequences for consistency
              await fetch('/api/v1/admin/work-item-sequences', {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  item_type: saveItemType,
                  workflow_context: workflowContext || undefined,
                  config: { custom_statuses: updatedStatuses }
                })
              });

              // Notify parent to re-fetch workflow states
              const newColumn: WorkflowState = {
                key,
                label: columnData.label,
                color: columnData.color,
                description: columnData.description
              };
              onColumnsChange?.([...workflowStates, newColumn]);

              // Add to visible
              setVisibleColumns(prev => new Set([...prev, key]));

              setShowAddColumn(false);
              return;
            }

            // Legacy path: save to board_columns table (when not using strict workflow states)
            // Initialize board columns if this is the first custom column
            if (!persistedColumns || persistedColumns.length === 0) {
              // First, initialize with current columns + new one
              const existingColumnsData = columns.map((col, idx) => ({
                key: col.key,
                label: col.label,
                color: col.color,
                position: idx,
                is_hidden: !visibleColumns.has(col.key)
              }));

              // Add new column at the end
              existingColumnsData.push({
                key,
                label: columnData.label,
                color: columnData.color,
                position: columns.length,
                is_hidden: false
              });

              const response = await fetch('/api/v1/work-items/boards/columns', {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  action: 'initialize',
                  board_key: boardKey,
                  item_type: itemType,
                  view_mode: viewMode,
                  columns: existingColumnsData
                })
              });

              if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to initialize columns');
              }

              const data = await response.json() as BoardColumnsResponse;
              if (data.success && data.data) {
                // Update persisted columns
                const newColumns: WorkflowState[] = data.data.map((col) => ({
                  key: col.column_key,
                  label: col.column_label,
                  color: col.color || 'gray',
                  description: col.description ?? undefined
                }));
                setPersistedColumns(newColumns);

                // Add new column to visible
                setVisibleColumns(prev => new Set([...prev, key]));

                // Notify parent if callback provided
                onColumnsChange?.(newColumns);
              }
            } else {
              // Add single column to existing board
              const response = await fetch('/api/v1/work-items/boards/columns', {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  action: 'add',
                  board_key: boardKey,
                  item_type: itemType,
                  view_mode: viewMode,
                  column_key: key,
                  column_label: columnData.label,
                  color: columnData.color,
                  position: persistedColumns.length
                })
              });

              if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to add column');
              }

              const data = await response.json();
              if (data.success && data.data) {
                // Add new column to state
                const newColumn: WorkflowState = {
                  key: data.data.column_key,
                  label: data.data.column_label,
                  color: data.data.color || 'gray'
                };
                const updatedColumns = [...persistedColumns, newColumn];
                setPersistedColumns(updatedColumns);

                // Add to visible columns
                setVisibleColumns(prev => new Set([...prev, key]));

                // Notify parent if callback provided
                onColumnsChange?.(updatedColumns);
              }
            }

          } catch (error) {
            console.error('[KanbanBoard] Failed to add column:', error);
            notify.error(`Failed to add column: ${error instanceof Error ? error.message : 'Unknown error'}`);
          }

          setShowAddColumn(false);
        }}
        existingColumns={columns}
      />

      <AppointmentAssigneesModal
        isOpen={!!appointmentAssigneesItem}
        onClose={() => setAppointmentAssigneesItemId(null)}
        appointment={appointmentAssigneesItem}
        availableUsers={appointmentAssigneesAvailableUsers}
        currentAssigneeIds={appointmentAssigneesCurrentUserIds}
        currentContractorIds={appointmentAssigneesCurrentContractorIds}
        isDark={isDark}
        onBeforeSave={handleAssigneesModalBeforeSave}
        onSaved={async () => {
          await onRefresh?.();
        }}
      />

      <AppointmentDropSetupModal
        isOpen={!!pendingAppointmentSetup}
        job={pendingAppointmentSetup?.activeItem || null}
        scheduledStartISO={pendingAppointmentSetup?.scheduledStartISO}
        scheduledEndISO={pendingAppointmentSetup?.scheduledEndISO}
        assignedUserIds={pendingAppointmentAssignedUserIds}
        availableAssignees={[
          ...assignedUsers.map((u) => ({ id: u.id, first_name: u.first_name, last_name: u.last_name, email: u.email })),
          ...availableContractors.map((c) => ({ id: -c.id, name: c.company_name || c.contact_person || `Contractor #${c.id}`, email: c.email, assignee_type: 'contractor' as const })),
        ]}
        appointmentTypeOptions={appointmentTypeOptions}
        canCreate={canCreateAppointmentForItem(pendingAppointmentSetup?.activeItem)}
        onConfirm={handleAppointmentSetupConfirmed}
        onClose={handleAppointmentSetupCancelled}
      />
    </div>
  );
}
